<template>
  <div
    @click.stop="closeGridView"
    class="grid-view-underlay"
    v-if="isGridView && windowWidth >= 2100"
  ></div>
  <div
    v-if="windowWidth >= 2100"
    :style="{ maxHeight: notesContainerHeight }"
    :class="{
      'notes-container-grid-view': isGridView,
      'notes-container': !isGridView,
    }"
  >
    <div
      :class="{
        'notes-container-grid-view-inner': isGridView,
        'notes-container-inner': !isGridView,
      }"
    >
      <div class="title-search-container">
        <div
          class="notes-title-bar"
          @click="cycleContainerHeight"
          @mouseenter="mouseOverNotesTitle(true)"
          @mouseleave="handleMouseLeave"
          @mousemove="updateMousePosition"
        >
          <i
            class="fas fa-th-large toggle-view-icon"
            @click.stop="toggleViewMode"
            :title="
              isGridView
                ? 'Switch to Single Column View'
                : 'Switch to Grid View'
            "
          ></i>
          <span @click="hideNotes = !hideNotes">Quick Notes</span>
          <span v-if="newNotesTodayCount > 0" class="quick-notes-badge">{{
            newNotesTodayCount
          }}</span>
          <i
            style="margin-left: 5px; color: #fff"
            class="fas fa-bell"
            :title="'add a new note'"
            @click.stop="showQuickRemindersList = !showQuickRemindersList"
          ></i>
          <div :title="'click to toggle\nsize of panel'" class="state-bars">
            <div
              v-for="n in 4"
              :key="n"
              :class="{ active: n <= currentHeightState }"
              class="state-bar"
            ></div>
          </div>
          <!-- DEBUG BUTTON - TEMPORARY -->
          <button
            @click="debugSocketConnection"
            style="
              background: red;
              color: white;
              padding: 2px 5px;
              margin-left: 10px;
              font-size: 10px;
              display: none;
            "
          >
            DEBUG SOCKET
          </button>
        </div>
        <input
          @click="
            searchQuery = '';
            isGridView = true;
            hideNotes = false;
          "
          class="search-input"
          @input="containerHeight = '100%'"
          type="text"
          v-model="searchQuery"
          :title="'Search notes,\n:y for yesterday\n:t for today\n:o older than yest'"
          placeholder="Search or :y, :t, :o"
        />
      </div>

      <!-- Render a placeholder if there are no notes -->
      <div
        class="notes-container-wrapper"
        :class="{ 'grid-view': isGridView, 'single-column-view': !isGridView }"
      ></div>
      <div
        v-if="notes.length === 0"
        class="note-tile placeholder"
        @mouseenter="showPlaceholderIcons = true"
        @mouseleave="showPlaceholderIcons = false"
      >
        <div>Add your first note...</div>
        <div v-if="showPlaceholderIcons" class="note-icons">
          <i
            title="add new note"
            class="fas fa-plus"
            @click.stop="addNewNoteAbove()"
          ></i>
        </div>
      </div>

      <!-- Render notes if available -->
      <div
        v-if="!hideNotes"
        :class="{ 'grid-view': isGridView, 'single-column-view': !isGridView }"
      >
        <div
          v-for="note in filteredNotes"
          :key="note.id"
          class="note-tile"
          :title="note.id"
          @mouseenter="onMouseEnter(note)"
          @mouseleave="onMouseLeave(note)"
          @keydown="handleKeyDown($event, note)"
          @click.prevent="selectSingleNote(note)"
          @dblclick.prevent="editNote(note)"
        >
          <div class="edit-in-progress" v-if="note.isEditing">
            editing, <br />click outside the <br />
            popup to close
          </div>
          <!-- Display the converted markdown content when not editing -->
          <div class="outer-note" v-if="!note.isEditing">
            <div class="inner-note">
              <span v-html="note.highlightedNote || note.note"></span>
              <div class="note-date">
                <span>{{ formatDate(note.modifiedOn) }}</span
                >&nbsp;
                <span>{{ formatWho(note.displayName) }}</span>
              </div>
            </div>
            <div v-if="note.showIcons" class="note-icons">
              <i class="fas fa-arrow-up" @click="moveNote('up', note)"></i>
              <i class="fas fa-arrow-down" @click="moveNote('down', note)"></i>
              <i
                style="margin-left: 10px"
                class="fas fa-trash"
                @click.stop="deleteNote(note.id)"
              ></i>
            </div>
          </div>

          <!-- Use v-show instead of v-if to keep textarea in the DOM for better performance when toggling -->
          <div
            v-show="note.isEditing"
            class="textarea-container draggable-popup bring-to-frontable"
          >
            <div class="quicknote-edit-title">
              <span>Quick Notes</span>
              <i
                style="position: absolute; right: 56px"
                class="fas fa-trash"
                title="Delete note"
                @click.prevent="deleteNote(note.id)"
              ></i>
              <i
                style="position: absolute; right: 40px"
                class="fas fa-save"
                title="Save note (keep open)"
                @mousedown.prevent
                @click.stop="saveNoteKeepOpen(note)"
              ></i>
              <!-- Close Icon -->
              <i
                style="position: absolute; right: 24px"
                class="fas fa-close"
                title="Save and close"
                @click.stop="closeSimpleNote(note)"
              ></i>

              <span>
                <i
                  style="margin-left: 10px; color: rgb(110 171 207)"
                  class="fas fa-circle-question"
                  @mousedown.prevent
                  @click="showQuickNotesHelp = !showQuickNotesHelp"
                >
                </i>
                <!-- Pin Icon -->
                <i
                  class="fas fa-thumbtack pin-icon"
                  :class="{ pinned: note.isPinned }"
                  @mousedown.prevent
                  @click="togglePin(note)"
                >
                </i>
              </span>
            </div>

            <!-- Textarea -->
            <textarea
              class="editing-textarea"
              :id="createRef(note.id)"
              v-model="note.note"
              spellcheck="false"
              autocorrect="off"
              autocomplete="off"
              autocapitalize="off"
              @dblclick="note.note = processCalculateFeature(note.note)"
            ></textarea>
            <div class="quicknotes-help" v-if="showQuickNotesHelp">
              <div style="margin-bottom: 10px; font-weight: bold">
                SmartNote Tips
              </div>
              <table class="quick-help-table">
                <thead></thead>
                <tbody>
                  <tr>
                    <td><span class="emphasis">calc</span> 1+2*3</td>
                    <td>on double click, does calculation</td>
                  </tr>
                  <tr>
                    <td>
                      <span class="emphasis">calculate:</span><br />item 1:
                      100K<br />item 2: -950
                    </td>
                    <td>
                      on double-click, converts items to a mini-table and auto
                      calculates. Converts K to thousands
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span class="emphasis">Remind me to</span><br />...
                      <span class="emphasis">Remind [user] to</span><br />...
                      <span class="emphasis">on</span>...
                      <span class="emphasis">at</span>...
                    </td>
                    <td>
                      Set a reminder for (whatever) on date at time. If no date
                      is entered, assumes today; if no time entered, assumes
                      noon. You can also use "tomorrow" or "next week" too.<br />
                      <strong
                        >Use "Remind [username] to" to send a reminder to
                        another user.</strong
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div
        class="more-notes"
        v-if="!hideNotes && currentHeightState !== 1"
        @click="cycleContainerHeight(1)"
      >
        more
        <i
          style="color: #666; margin-left: 5px"
          class="fas fa-circle-chevron-down"
        ></i>
      </div>
    </div>
  </div>
  <div class="nav-bar">
    <nav :class="['menu-bar', { 'menu-bar-compact': windowWidth < 1375 }]">
      <ul class="navbar-nav">
        <button v-if="isDev" class="dev-mode">Dev</button>
        <li @mouseenter="showMenu('home')" @mouseleave="hideMenu('home')">
          <a href="#"
            ><span
              class="health-status-circle"
              :class="{ online: serviceOnline, offline: !serviceOnline }"
            ></span
            >Home</a
          >
          <ul v-if="menus.home" class="dropdown">
            <li><a href="/home">Home</a></li>
            <li v-if="isMainPage"></li>
            <!--
            <li><a href="#">-</a></li>
          --></ul>
        </li>
        <li @mouseenter="showMenu('view')" @mouseleave="hideMenu('view')">
          <a href="#">View</a>
          <ul v-if="menus.view" class="dropdown">
            <li><a href="/lease-search">Lease Search</a></li>
            <li><a href="/lease-ledger-edit">Ledger Entry Edit</a></li>
            <li><a href="/properties?tab=leasesByMonth">Lease Overview</a></li>
            <li><a href="/properties?tab=overview">Properties</a></li>
            <li><a href="/properties?tab=payments">Payments</a></li>
            <li><a href="/documents">Documents</a></li>
            <li><a href="/people">People</a></li>
            <!--
            <li><a href="#">-</a></li>
            <li><a href="#">-</a></li>
            <li><a href="#">-</a></li>
          --></ul>
        </li>
        <li @mouseenter="showMenu('bank')" @mouseleave="hideMenu('bank')">
          <a href="#">Financial</a>
          <ul v-if="menus.bank" class="dropdown">
            <li><a href="#" @click="openPayrollPopup">Payroll</a></li>
            <li><a href="/main">Ledgers</a></li>
            <li class="has-submenu">
              <a href="#"
                ><span>Import</span><span class="submenu-arrow">▸</span></a
              >
              <ul class="submenu">
                <li>
                  <a href="#" @click="openAceStatementImport">Ace Statements</a>
                </li>
                <li>
                  <a href="#" @click="openBrysonLawncareImport"
                    >Bryson Lawncare</a
                  >
                </li>
              </ul>
            </li>
            <li v-if="$route.path === '/main'">
              <a href="#" @click="uploadBankFile">Upload Bank file</a>
            </li>
            <li>
              <a
                href="https://businessonline.huntington.com/BOLHome/BusinessOnlineLogin.aspx"
                >Login to Bank</a
              >
            </li>
            <li><a href="#" @click="openQuarters">Quarters</a></li>
            <li v-if="myUserRole < 2" class="has-submenu">
              <a href="#"
                ><span>Financials</span><span class="submenu-arrow">▸</span></a
              >
              <ul class="submenu">
                <li>
                  <a href="#" @click="openFinancialSummary"
                    >Financial Summary</a
                  >
                </li>
                <li>
                  <a href="#" @click="openPayeeSummary">Payee Summary</a>
                </li>
                <li>
                  <a href="#" @click="openCashPosition">Cash Position</a>
                </li>
                <li>
                  <a href="#" @click="openPropertyAnalysis"
                    >Property Analysis</a
                  >
                </li>
                <li>
                  <a href="#" @click="openUnclearedChecks">Uncleared Checks</a>
                </li>
                <li><a href="/reports">Reports</a></li>
                <li class="has-submenu">
                  <a href="#"
                    ><span>Trend Charts</span
                    ><span class="submenu-arrow">▸</span></a
                  >
                  <ul class="submenu">
                    <li>
                      <a href="#" @click="openMaintenanceTrends"
                        >Maintenance Trends</a
                      >
                    </li>
                    <li>
                      <a href="#" @click="openUtilitiesTrend"
                        >Utilities Trend</a
                      >
                    </li>
                    <li>
                      <a href="#" @click="openInsuranceTrend"
                        >Insurance Trend</a
                      >
                    </li>
                  </ul>
                </li>
                <li class="has-submenu">
                  <a href="#"
                    ><span>Utilities</span
                    ><span class="submenu-arrow">▸</span></a
                  >
                  <ul class="submenu">
                    <li>
                      <a href="#" @click="openUtilityReport('Natural Gas')"
                        >Natural Gas</a
                      >
                    </li>
                    <li>
                      <a href="#" @click="openUtilityReport('Electricity')"
                        >Electricity</a
                      >
                    </li>
                    <li>
                      <a href="#" @click="openUtilityReport('Water')">Water</a>
                    </li>
                    <li>
                      <a href="#" @click="openUtilityReport('Trash')">Trash</a>
                    </li>
                  </ul>
                </li>
                <li>
                  <a href="#" @click="openLeaseYearEnd">Lease Year End</a>
                </li>
                <li>
                  <a href="#" @click="openSecurityDepositsYearEnd"
                    >Security Deposits Year End</a
                  >
                </li>
                <li>
                  <a href="/reports?q=Ledger%20Adjustments"
                    >Ledger Adj Prior Year</a
                  >
                </li>
                <li>
                  <a href="#" @click="openTerminatedLeases"
                    >Terminated Leases</a
                  >
                </li>
                <li>
                  <a href="#" @click="openPayeeSummary('1099')">1099 List</a>
                </li>
                <li>
                  <a href="#" @click="runAutomatchOnly($event)">Automatch Only</a>
                </li>
              </ul>
            </li>
          </ul>
        </li>

        <li @mouseenter="showMenu('tools')" @mouseleave="hideMenu('tools')">
          <a href="#">Tools</a>
          <ul v-if="menus.tools" class="dropdown">
            <li class="has-submenu">
              <a href="#"
                ><span>Properties</span><span class="submenu-arrow">▸</span></a
              >
              <ul class="submenu">
                <li>
                  <a href="#" @click="openLockCodes">Lock Codes</a>
                </li>
                <li>
                  <a href="#" @click="openAppliances">Appliances</a>
                </li>
                <li>
                  <a href="#" @click="openPropertySummary">Property Summary</a>
                </li>
                <li>
                  <a href="#" @click="openServiceContacts">Service Contacts</a>
                </li>
                <li>
                  <a href="#" @click="openShopInventory">Shop Inventory</a>
                </li>
              </ul>
            </li>
            <li class="has-submenu">
              <a href="#"
                ><span>Email</span><span class="submenu-arrow">▸</span></a
              >
              <ul class="submenu">
                <li>
                  <a href="#" @click="handleShowQuickEmails">Quick Email</a>
                </li>
                <li>
                  <a href="#" @click="openBulkEmail">Bulk Email</a>
                </li>
              </ul>
            </li>
            <li class="has-submenu">
              <a href="#"
                ><span>Voicemail</span><span class="submenu-arrow">▸</span></a
              >
              <ul class="submenu">
                <li>
                  <a href="#" @click.prevent="openVoicemailCustomize"
                    >Customize</a
                  >
                </li>
              </ul>
            </li>
            <li
              v-if="myUserRole != null && myUserRole <= 2"
              class="has-submenu"
            >
              <a href="#"
                ><span>Staff</span><span class="submenu-arrow">▸</span></a
              >
              <ul class="submenu">
                <li>
                  <a href="#" @click.prevent="openStaffSms">SMS to Staff</a>
                </li>
                <li>
                  <a href="#" @click.prevent="openDndStatus">DND Status</a>
                </li>
                <li>
                  <a href="#" @click.prevent="openStaffTelemetry">Telemetry</a>
                </li>
              </ul>
            </li>
            <li class="has-submenu">
              <a href="#"
                ><span>Tenants</span><span class="submenu-arrow">▸</span></a
              >
              <ul class="submenu">
                <li>
                  <a href="#" @click.prevent="openChatBotMotd">Tenant App MOTD</a>
                </li>
                <li>
                  <a href="#" @click.prevent="openRentPaymentSignups"
                    >Tenant App Stats</a
                  >
                </li>
                <li>
                  <a href="#" @click.prevent="openChatbotTelemetry"
                    >Tenant App telemetry</a
                  >
                </li>
                <li>
                  <a href="#" @click.prevent="openPastDueTexts">Past due texts</a>
                </li>
                <li>
                  <a href="#" @click="openAutomate">Send all Ledgers</a>
                </li>
                <li>
                  <a href="#" @click="openCollectorSetup">Campaigns</a>
                </li>
                <li>
                  <a href="#" @click="openSurveySetup">Surveys</a>
                </li>
                <li>
                  <a href="#" @click="openRentMeSetup">Rent Me</a>
                </li>
                <li>
                  <a href="#" @click="openLateFees">Add Late Fees</a>
                </li>
              </ul>
            </li>
            <li class="has-submenu">
              <a href="#"
                ><span>Server</span><span class="submenu-arrow">▸</span></a
              >
              <ul class="submenu">
                <li v-if="myUserRole != null && myUserRole <= 1">
                  <a href="#" @click.prevent="openPollingLocksStatus"
                    >Polling Status</a
                  >
                </li>
                <li v-if="myUserRole === 1">
                  <a href="#" @click.prevent="openServerManagement"
                    >Server Management</a
                  >
                </li>
                <li>
                  <a href="#" @click.prevent="openRecentActivity"
                    >Recent Activity</a
                  >
                </li>
              </ul>
            </li>
            <li class="has-submenu">
              <a href="#"
                ><span>Sensors</span><span class="submenu-arrow">▸</span></a
              >
              <ul class="submenu">
                <li>
                  <a href="#" @click="openTempCharts">Temp Charts</a>
                </li>
              </ul>
            </li>
            <li><a href="#" @click="openMapIt">Map It</a></li>
          </ul>
        </li>
        <li @mouseenter="showMenu('help')" @mouseleave="hideMenu('help')">
          <a href="#">Help</a>
          <ul v-if="menus.help" class="dropdown">
            <li v-if="isMainPage">
              <a href="#" @click="showShortcuts">Show Shortcuts</a>
            </li>
            <li><a href="#" @click="openHelpVideos">Help Videos</a></li>
            <li><a href="#" @click="getServiceStatus">About</a></li>
            <!--
            <li><a href="#">-</a></li>
          --></ul>
        </li>
        <li class="divider">|</li>

        <li class="alert-item" style="cursor: pointer">
          <a ref="#" @click="navigateToPayments" style="position: relative">
            <i
              style="color: #42bf42; cursor: pointer; margin-right: 2px"
              class="fas fa-money-bill-wave"
            ></i>
            <span
              v-if="unreadReceipts > 0"
              class="calendar-badge"
              @click.stop="handlePaymentBadgeClick"
              >{{ unreadReceipts }}</span
            >
            Payments
          </a>
        </li>
        <li class="alert-item" style="cursor: pointer">
          <a ref="#" @click="navigateToDocuments" style="position: relative">
            <i
              style="color: #42bf42; cursor: pointer; margin-right: 2px"
              class="fas fa-file-alt"
            ></i>
            <span
              v-if="waitingDocumentsCount > 0"
              class="calendar-badge"
              @click.stop="openDocuments"
              >{{ waitingDocumentsCount }}</span
            >
            Documents
          </a>
        </li>
        <li class="alert-item" style="cursor: pointer">
          <a ref="#" @click="handleEmailNavClick" style="position: relative">
            <i
              style="color: #42bf42; cursor: pointer; margin-right: 2px"
              class="fas fa-envelope"
            ></i>
            <span v-if="unseenEmailsCount > 0" class="calendar-badge">{{
              unseenEmailsCount
            }}</span>
            Email
          </a>
        </li>
        <li class="alert-item" style="cursor: pointer">
          <a ref="#" @click="openWorkOrders" style="position: relative">
            <i
              style="color: #42bf42; cursor: pointer; margin-right: 2px"
              class="fas fa-wrench"
            ></i>
            <span v-if="newWorkOrdersCount > 0" class="calendar-badge">{{
              newWorkOrdersCount
            }}</span>
            Work Orders
          </a>
        </li>
        <li class="alert-item" style="cursor: pointer">
          <a ref="#" @click="handleNewVoiceClick" style="position: relative">
            <i
              style="color: #42bf42; cursor: pointer; margin-right: 2px"
              class="fas fa-microphone"
            ></i>
            <span v-if="unheardVoiceMemosCount > 0" class="calendar-badge">{{
              unheardVoiceMemosCount
            }}</span>
            Voice Messages
          </a>
        </li>
        <li class="alert-item" style="cursor: pointer">
          <a
            ref="#"
            v-if="newNoteAvailable"
            @click="handleNewNoteClick"
            style="position: relative"
          >
            <i
              style="color: rgb(216 229 42); cursor: pointer; margin-right: 2px"
              class="fas fa-book-open"
            ></i>
            <span v-if="newMobileNotesTodayCount > 0" class="calendar-badge">{{
              newMobileNotesTodayCount
            }}</span>
            Notes
          </a>
        </li>
        <li class="alert-item" style="cursor: pointer">
          <a ref="#" @click="openSmsInboxColumn" style="position: relative">
            <i
              style="color: #42bf42; cursor: pointer; margin-right: 2px"
              class="fas fa-sms"
            ></i>
            <span v-if="staffSmsCount > 0" class="calendar-badge">{{
              staffSmsCount
            }}</span>
            SMS
          </a>
        </li>
        <li class="alert-item" style="cursor: pointer">
          <a ref="#" @click="handleChatToolClick" style="position: relative">
            <i
              style="color: #42bf42; cursor: pointer; margin-right: 2px"
              class="fas fa-comment-dots"
            ></i>
            <span v-if="unreadTenantChatCount > 0" class="calendar-badge">{{
              unreadTenantChatCount
            }}</span>
            Chat Bot
          </a>
        </li>
        <li class="alert-item" style="cursor: pointer">
          <a ref="#" @click="openCalendar" style="position: relative">
            <i
              style="color: #5ba3d0; cursor: pointer; margin-right: 2px"
              class="fas fa-calendar-alt"
            ></i>
            <span v-if="todayEventsCount > 0" class="calendar-badge">{{
              todayEventsCount
            }}</span>
            Calendar
          </a>
        </li>
        <li
          v-if="myUserRole != null && myUserRole <= 2"
          class="alert-item"
          style="cursor: pointer"
        >
          <a ref="#" @click="openRentMeFromNavButton" style="position: relative">
            <i
              style="color: #a080a8; cursor: pointer; margin-right: 2px"
              class="fas fa-fan"
            ></i>
            <span v-if="newRentMeAgreementsCount > 0" class="calendar-badge">{{
              newRentMeAgreementsCount
            }}</span>
            Rent Me
          </a>
        </li>
        <li class="right-aligned user-menu-wrapper">
          <button
            class="user-menu-trigger"
            :title="`Window: ${windowWidth} x ${windowHeight}`"
            @click.stop="toggleUserMenu"
          >
            Welcome, {{ myUserName }}
          </button>
          <a ref="#">
            <i class="notification-bell fas fa-bell"></i>
          </a>
          <ul v-if="userMenuOpen" class="dropdown user-dropdown">
            <li>
              <a href="#" @click.prevent="togglePageScroll">{{
                pageScrollEnabled ? 'Disable page scroll' : 'Enable page scroll'
              }}</a>
            </li>
            <li>
              <a href="#" @click.prevent="logoutFromUserMenu">Logout</a>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
  </div>

  <!-- Screen Size Warning Popup -->
  <div v-if="showScreenSizeWarning" class="screen-size-warning-overlay">
    <div class="screen-size-warning-popup">
      <p>
        This app is optimized for wide screens, 2560 x 1080. Some elements will
        not display properly on smaller screens. You can use the 'zoom' feature
        of your browser to adjust fit in these cases.
      </p>
      <div class="screen-size-warning-buttons">
        <button @click="dismissScreenSizeWarning(true)">OK</button>
        <button @click="dismissScreenSizeWarning(false)">Cancel</button>
      </div>
    </div>
  </div>

  <div
    v-if="showReminders"
    ref="remindersPanel"
    class="reminders-panel draggable-popup bring-to-frontable"
    @mousedown="bringToFront($event)"
  >
    <div class="help-title reminders-panel-drag-handle">
      <i class="fas fa-grip-horizontal" aria-hidden="true"></i>
      <span>Reminders</span>
      <span style="margin-left: 8px; font-size: 0.7em"
        >(alt-r to show/hide)</span
      >
      <!-- close icon -->
      <i
        class="fas fa-close close-button"
        ref="closeButton"
        @mousedown.stop
        @click.stop="closeRemindersPanel"
      ></i>
    </div>

    <ReminderDialog
      :showReminderDialog="true"
      @update:showReminderDialog="closeRemindersPanel"
    />
  </div>

  <div class="hotkeys-panel" v-if="showHelp">
    <div class="help-title">Hotkeys to help navigate this app:</div>
    <table class="key-table">
      <!-- ... (table content) ... -->
      <thead>
        <tr class="headers">
          <td class="header-key">shortcut key</td>
          <td class="header-action">action</td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="hotkey">Alt-m</td>
          <td class="key-description">Open Quick E<u>m</u>ail</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-?</td>
          <td class="key-description">show (or hide) this help panel</td>
        </tr>
        <tr>
          <td class="hotkey">Esc</td>
          <td class="key-description">clear the search input</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-s</td>
          <td class="key-description">
            in Documents: find more like selected row
          </td>
        </tr>
        <tr>
          <td class="hotkey">Alt-b</td>
          <td class="key-description">jump to <u>B</u>ank and ledger</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-q</td>
          <td class="key-description">
            <u>Q</u>uick sticky note and reminders
          </td>
        </tr>
        <tr>
          <td class="hotkey">Alt-w</td>
          <td class="key-description"><u>W</u>ide view for QuickNotes</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-D (caps)</td>
          <td class="key-description">jump to <u>D</u>ocuments</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-t</td>
          <td class="key-description">jump to <u>T</u>enants view</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-p</td>
          <td class="key-description">jump to <u>P</u>roperties view</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-c</td>
          <td class="key-description">jump to <u>C</u>alendar view</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-r</td>
          <td class="key-description">Show (or hide) <u>R</u>eminders</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-k</td>
          <td class="key-description">Open the screen (K)lip tool</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-l</td>
          <td class="key-description">jump to <u>L</u>ease view</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-y</td>
          <td class="key-description">jump to Pa<u>Y</u>ments view</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-h</td>
          <td class="key-description">jump to <u>H</u>ome</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-o</td>
          <td class="key-description">jump to T<u>o</u>ols page</td>
        </tr>
        <tr>
          <td class="hotkey">Alt-g</td>
          <td class="key-description">
            jump to <u>G</u>raphs and Reports page
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Footer Message -->
  <div :class="{ 'footer-message': true, highlighted: footerMessage !== '' }">
    {{ footerMessage }}
  </div>

  <BulkEmail
    :showBulkEmailPanel="showBulkEmail"
    @close-panel="showBulkEmail = false"
  />

  <div
    v-if="showQuickEmail"
    class="quick-email-wrapper"
    :class="quickEmailWrapperClass"
  >
    <QuickEmail
      :show="showQuickEmail"
      :userId="myUserId"
      @showMessage="displayMessage"
      :emailTo="emailTo"
      :emailSubject="emailSubject"
      :emailBody="emailBody"
    />
  </div>

  <NotesPanel
    :showNotesPanel="this.showNotesPanel"
    :noteType="this.noteType"
    :noteId="this.noteId"
    @update-show-notes-panel="updateShowNotesPanel"
  />

  <!-- message of the day -->
  <MotdModal :route="$route.path" />

  <!-- Quick Reminder Entry Popup -->
  <div
    v-if="showQuickReminderPopup"
    class="reminder-popup draggable-popup bring-to-frontable"
  >
    <audio
      v-show="soundEnabled"
      ref="reminderSound"
      src="/ding2-89720.mp3"
    ></audio>
    <div class="reminder-title">
      <span>Reminder Alert</span>
      <i
        @click="reminderPopupCollapsed = !reminderPopupCollapsed"
        class="fas toggle-reminder-icon"
        :class="reminderPopupCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"
      ></i>
    </div>
    <div v-if="!reminderPopupCollapsed" class="reminder-content">
      <!-- Task List with Selection Capability -->
      <div
        v-for="task in alertingTasks"
        :key="task.noteId"
        class="reminder-task"
        :class="{
          selected: selectedReminder.noteId === task.noteId,
        }"
        @click="selectTask(task)"
      >
        {{ task.task }}
      </div>
    </div>
    <!-- Buttons for Current Task Actions -->
    <div
      v-if="selectedReminder && !reminderPopupCollapsed"
      class="reminder-buttons"
    >
      <button @click="dismissReminder(selectedReminder)">Dismiss</button>
      <button @click="snoozeReminder(selectedReminder, 60, 'minute')">
        Snooze 1 Hr
      </button>
      <button @click="snoozeReminder(selectedReminder, 24, 'hour')">
        Snooze 1 Day
      </button>
      <button @click="snoozeAllReminders">Snooze All</button>
    </div>
  </div>

  <!-- quick reminder list popup -->
  <div v-if="showQuickRemindersList" class="reminders-list">
    <div class="reminders-list-title">Upcoming Quick Reminders</div>
    <div class="reminders-list-outer">
      <div class="reminders-list-inner">
        <ul style="margin-left: -40px">
          <li
            v-for="reminder in sortedQuickReminders"
            :key="reminder.noteId"
            class="reminders-list-item"
          >
            <span
              >{{ formatDateTime(reminder.dateTime) }} -
              {{ reminder.task }}</span
            >
            <i
              class="fas fa-trash delete-button"
              @click="deleteReminderItem(reminder)"
            ></i>
          </li>
        </ul>
      </div>
    </div>
    <button style="margin-top: 10px" @click="closeRemindersList">Close</button>
  </div>
  <PayrollPopup
    :showPayrollPopup="showPayrollPopup"
    @update-show-payroll-popup="showPayrollPopup = $event"
  />

  <CalendarTool
    :showModal="showCalendarModal"
    :initialMonth="calendarInitialMonth"
    :initialYear="calendarInitialYear"
    :initialDay="calendarInitialDay"
    :personalCalendar="calendarPersonalMode"
    :initialGridTab="calendarInitialGridTab"
    @close="closeCalendar"
    @notification="handleCalendarNotification"
  />

  <!-- Availability Warning Popup -->
  <div v-if="showAvailabilityWarning" class="screen-size-warning-overlay">
    <div class="screen-size-warning-popup">
      <p style="font-size: 15px">
        <i
          class="fas fa-exclamation-triangle"
          style="color: #e67e22; margin-right: 6px"
        ></i>
        There are no availability slots set up for
        <strong>{{ availabilityWarningWeeks }}</strong
        >.
      </p>
      <div class="screen-size-warning-buttons">
        <button @click="openCalendarAvailability">Open Calendar</button>
        <button @click="showAvailabilityWarning = false">Dismiss</button>
      </div>
    </div>
  </div>

  <div v-if="showChatTool" :class="chatWrapperClass" :style="chatWrapperStyle">
    <InsideChatTool
      ref="insideChatTool"
      :chatMessage="chatMessage"
      :sharedSocket="socket"
    />
  </div>

  <div :class="notesWrapperClass" v-if="showMobileNotes">
    <MobileNotes />
  </div>

  <div :class="notesWrapperClass" v-if="showSmsInbox">
    <MSmsInbox />
  </div>

  <div :class="notesWrapperClass" v-if="showVoiceMessages">
    <MVoiceMessages />
  </div>

  <div
    class="cash-position-wrapper"
    :class="cashWrapperClass"
    v-if="showCashPosition"
  >
    <MCashPosition />
  </div>

  <div
    class="lock-codes-wrapper"
    v-if="showLockCodes"
    :class="lockCodesWrapperClass"
  >
    <MLockCodes />
  </div>

  <div
    class="work-orders-wrapper"
    :class="workOrderWrapperClass"
    v-if="showWorkOrders"
  >
    <MWorkOrders />
  </div>
  <div
    class="appliances-wrapper"
    :class="appliancesWrapperClass"
    v-if="showAppliances"
  >
    <MAppliances />
  </div>
  <div
    class="appliances-wrapper"
    :class="appliancesWrapperClass"
    v-if="showPropertySummary"
  >
    <MPropertySummary :start-view="propertySummaryStart" />
  </div>
  <div v-if="showLateFees" class="late-fees-wrapper">
    <LateFees @close="showLateFees = false" />
  </div>

  <AceStatementImport
    :show="showAceStatementImport"
    @close="showAceStatementImport = false"
  />

  <BrysonLawncareImport
    :show="showBrysonLawncareImport"
    @close="showBrysonLawncareImport = false"
  />

  <div
    class="quarters-wrapper"
    :class="quartersWrapperClass"
    v-if="showQuarters"
  >
    <QuarterTracker />
  </div>

  <div
    class="collector-setup-wrapper"
    :class="collectorSetupWrapperClass"
    v-if="showCollectorSetup"
  >
    <CollectorSetup @close="showCollectorSetup = false" />
  </div>

  <div
    class="survey-setup-wrapper"
    :class="surveySetupWrapperClass"
    v-if="showSurveySetup"
  >
    <SurveySetup @close="showSurveySetup = false" />
  </div>

  <div
    class="collector-setup-wrapper"
    :class="rentMeSetupWrapperClass"
    v-if="showRentMeSetup"
  >
    <RentMeSetup @close="showRentMeSetup = false" />
  </div>

  <ChatBotMotdDialog
    v-if="showChatBotMotd"
    @close="showChatBotMotd = false"
  />

  <VoicemailGreetingDialog
    v-if="showVoicemailCustomize"
    @close="showVoicemailCustomize = false"
  />

  <StaffSmsDialog
    v-if="showStaffSms"
    @close="showStaffSms = false"
  />

  <PastDueSmsDialog
    v-if="showPastDueTexts"
    @close="showPastDueTexts = false"
  />

  <HelpVideos :showModal="showHelpVideos" @close="closeHelpVideos" />

  <div class="financial-summary-wrapper" v-if="showFinancialSummary">
    <FinancialSummary
      :year="new Date().getFullYear()"
      :userRole="myUserRole"
      @close="closeFinancialSummary"
      @month-filter="handleMonthFilter"
      @show-uncategorized="handleShowUncategorized"
      @show-annual-detail="handleShowAnnualDetail"
    />
  </div>

  <div class="financial-summary-wrapper" v-if="showPayeeSummary">
    <PayeeSummary
      :userRole="myUserRole"
      :initialFilter="payeeSummaryFilter"
      @close="closePayeeSummary"
    />
  </div>

  <div class="financial-summary-wrapper" v-if="showPropertyAnalysis">
    <PropertyAnalysis :userRole="myUserRole" @close="closePropertyAnalysis" />
  </div>

  <div class="financial-summary-wrapper" v-if="showUnclearedChecks">
    <UnclearedChecks @close="closeUnclearedChecks" />
  </div>

  <div class="financial-summary-wrapper" v-if="showMaintenanceTrends">
    <MaintenanceTrends
      :userRole="myUserRole"
      @close="closeMaintenanceTrends"
      @navigate-prev="navigateTrend('maintenance', -1)"
      @navigate-next="navigateTrend('maintenance', 1)"
    />
  </div>

  <div class="financial-summary-wrapper" v-if="showUtilitiesTrend">
    <UtilitiesTrend
      :userRole="myUserRole"
      @close="closeUtilitiesTrend"
      @navigate-prev="navigateTrend('utilities', -1)"
      @navigate-next="navigateTrend('utilities', 1)"
    />
  </div>

  <div class="financial-summary-wrapper" v-if="showInsuranceTrend">
    <InsuranceTrend
      :userRole="myUserRole"
      @close="closeInsuranceTrend"
      @navigate-prev="navigateTrend('insurance', -1)"
      @navigate-next="navigateTrend('insurance', 1)"
    />
  </div>

  <div class="financial-summary-wrapper" v-if="showTempCharts">
    <TempCharts
      :userRole="myUserRole"
      @close="closeTempCharts"
      @navigate-prev="navigateTrend('tempcharts', -1)"
      @navigate-next="navigateTrend('tempcharts', 1)"
    />
  </div>

  <AnnualCategoryDetail
    v-if="showAnnualCategoryDetail"
    :show="showAnnualCategoryDetail"
    :category-name="annualDetailCategoryName"
    :transactions="annualDetailTransactions"
    :year="annualDetailYear"
    @close="closeAnnualCategoryDetail"
    @refresh="refreshAnnualCategoryDetail"
  />

  <TransactionsDrilldown
    v-if="showUncategorizedTransactions"
    :show="showUncategorizedTransactions"
    :category-name="drilldownCategoryName"
    :transactions="uncategorizedTransactions"
    :month="uncategorizedMonth"
    :year="uncategorizedYear"
    :refreshing="uncategorizedRefreshing"
    @close="closeUncategorizedTransactions"
    @refresh="refreshUncategorizedTransactions"
  />

  <!-- Service Offline Popup -->
  <div v-if="showOfflinePopup" class="offline-popup-overlay">
    <div class="offline-popup-dialog">
      <i class="fas fa-exclamation-triangle"></i>
      <p>
        The service is offline for maintenance.<br />Please try again later.
      </p>
      <button @click="showOfflinePopup = false">Close</button>
    </div>
  </div>

  <!-- Recent Activity Popup -->
  <div
    v-if="showRecentActivityPopup"
    class="offline-popup-overlay"
    @click.self="showRecentActivityPopup = false"
  >
    <div class="recent-activity-dialog">
      <div class="recent-activity-header">
        <span>Recent Activity</span>
        <button class="close-btn" @click="showRecentActivityPopup = false">
          &times;
        </button>
      </div>
      <div class="recent-activity-list">
        <div
          v-for="(item, index) in recentActivityData"
          :key="index"
          class="activity-item"
        >
          <!-- eslint-disable-next-line prettier/prettier -->
          <div class="activity-user" :title="item.request_url">
            {{ item.displayName }}
          </div>
          <div class="activity-time">
            {{ formatActivityDateTime(item.access_datetime) }}
          </div>
        </div>
        <div v-if="recentActivityData.length === 0" class="no-activity">
          No recent activity found
        </div>
      </div>
    </div>
  </div>

  <!-- Rent Payment Signups Popup -->
  <div
    v-if="showRentPaymentSignupsPopup"
    class="offline-popup-overlay tenant-app-stats-overlay"
    @click.self="closeRentPaymentSignups"
  >
    <div class="recent-activity-dialog rent-payment-signups-dialog">
      <div class="recent-activity-header">
        <span>Tenant App Stats</span>
        <div style="display: flex; align-items: center; gap: 8px">
          <button
            class="refresh-btn"
            :disabled="rentSignupsLoading"
            @click="openRentPaymentSignups"
            title="Refresh"
          >
            <i
              :class="['fas', 'fa-sync-alt', { 'fa-spin': rentSignupsLoading }]"
            ></i>
          </button>
          <button class="close-btn" @click="closeRentPaymentSignups">
            &times;
          </button>
        </div>
      </div>
      <div class="tenant-app-stats-body">
      <div class="recent-activity-list">
        <div class="chatbot-visits-title">Recent visitors</div>
        <div
          v-for="(item, index) in recentVisitorsData"
          :key="item.peopleId || index"
          class="activity-item"
        >
          <div class="activity-user" :title="item.address || ''">
            {{ item.fullName }}
            <span
              v-if="item.address"
              style="color: #888; font-weight: normal; margin-left: 6px"
              >— {{ item.address }}</span
            >
          </div>
          <div class="activity-time">
            {{ formatActivityDateTime(item.lastVisit) }}
          </div>
        </div>
        <div v-if="recentVisitorsData.length === 0" class="no-activity">
          No recent visitors
        </div>
      </div>
      <div class="chatbot-visits-section">
        <div class="chatbot-visits-title">
          Tenant App Page Visits (last 10 days)
        </div>
        <div class="chatbot-visits-hint">
          Double-click a day to see tenant names
        </div>
        <table class="chatbot-visits-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Visits</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in chatbotVisitsData"
              :key="i"
              class="chatbot-visit-row"
              :class="{
                'is-selected': visitDayKey(row.day) === selectedVisitDay,
              }"
              title="Double-click to see tenant names"
              @dblclick="openVisitorsForDay(row)"
            >
              <td>{{ formatVisitDay(row.day) }}</td>
              <td>{{ row.visits }}</td>
            </tr>
            <tr v-if="chatbotVisitsData.length === 0">
              <td colspan="2" style="text-align: center; color: #888">
                No data
              </td>
            </tr>
          </tbody>
        </table>
        <div class="chatbot-visits-title" style="margin-top: 16px">
          Tenant app versions
        </div>
        <div class="chatbot-visits-hint">Click a count to see tenants</div>
        <table class="chatbot-visits-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Tenants</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in chatbotVersionData"
              :key="row.version"
              class="chatbot-visit-row"
              :class="{ 'is-selected': row.version === selectedTenantVersion }"
            >
              <td>{{ row.version }}</td>
              <td>
                <button
                  type="button"
                  class="chatbot-version-count"
                  @click="openTenantsForVersion(row)"
                >
                  {{ row.tenantCount }}
                </button>
              </td>
            </tr>
            <tr v-if="chatbotVersionData.length === 0">
              <td colspan="2" style="text-align: center; color: #888">
                No data
              </td>
            </tr>
          </tbody>
        </table>
        <div class="chatbot-visits-title" style="margin-top: 16px">
          SMS opt-ins
        </div>
        <div class="chatbot-visits-hint">Click a count to see names</div>
        <table class="chatbot-visits-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Opted in</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in smsOptInByDayData"
              :key="row.day"
              class="chatbot-visit-row"
              :class="{ 'is-selected': visitDayKey(row.day) === selectedSmsOptInDay }"
            >
              <td>{{ formatVisitDay(row.day) }}</td>
              <td>
                <button
                  type="button"
                  class="chatbot-version-count"
                  @click="openSmsOptInsForDay(row)"
                >
                  {{ row.optInCount }}
                </button>
              </td>
            </tr>
            <tr v-if="smsOptInByDayData.length === 0">
              <td colspan="2" style="text-align: center; color: #888">
                No data
              </td>
            </tr>
          </tbody>
        </table>
        <div class="chatbot-visits-title" style="margin-top: 16px">
          Notifications
        </div>
        <div class="chatbot-visits-hint">Click a count to see names</div>
        <table class="chatbot-visits-table">
          <thead>
            <tr>
              <th>State</th>
              <th>Tenants</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in tenantNotificationData"
              :key="row.state"
              class="chatbot-visit-row"
              :class="{
                'is-selected': row.state === selectedNotificationState,
              }"
            >
              <td>{{ row.label }}</td>
              <td>
                <button
                  type="button"
                  class="chatbot-version-count"
                  @click="openTenantsForNotificationState(row)"
                >
                  {{ row.tenantCount }}
                </button>
              </td>
            </tr>
            <tr v-if="tenantNotificationData.length === 0">
              <td colspan="2" style="text-align: center; color: #888">
                No data
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </div>
    </div>
  </div>

  <div
    v-if="showVersionTenantsPopup"
    class="offline-popup-overlay visit-day-names-overlay"
    @click.self="closeTenantsForVersion"
  >
    <div class="recent-activity-dialog visit-day-names-dialog">
      <div class="recent-activity-header">
        <span>Tenants on {{ selectedTenantVersion }}</span>
        <button class="close-btn" @click="closeTenantsForVersion">
          &times;
        </button>
      </div>
      <div class="recent-activity-list">
        <div
          v-for="(item, index) in versionTenantsData"
          :key="item.peopleId || index"
          class="activity-item"
        >
          <div class="activity-user" :title="item.address || ''">
            {{ item.fullName }}
            <span
              v-if="item.address"
              style="color: #888; font-weight: normal; margin-left: 6px"
              >— {{ item.address }}</span
            >
          </div>
          <div class="activity-time">
            {{ formatActivityDateTime(item.lastVisit) }}
          </div>
        </div>
        <div
          v-if="!versionTenantsLoading && versionTenantsData.length === 0"
          class="no-activity"
        >
          No tenants on this version
        </div>
        <div v-if="versionTenantsLoading" class="no-activity">Loading...</div>
      </div>
    </div>
  </div>

  <div
    v-if="showNotificationTenantsPopup"
    class="offline-popup-overlay visit-day-names-overlay"
    @click.self="closeTenantsForNotificationState"
  >
    <div class="recent-activity-dialog visit-day-names-dialog">
      <div class="recent-activity-header">
        <span>Notifications {{ selectedNotificationLabel }}</span>
        <button class="close-btn" @click="closeTenantsForNotificationState">
          &times;
        </button>
      </div>
      <div class="recent-activity-list">
        <div
          v-for="(item, index) in notificationTenantsData"
          :key="item.peopleId || index"
          class="activity-item"
        >
          <div class="activity-user" :title="item.address || ''">
            {{ item.fullName }}
            <span
              v-if="item.address"
              style="color: #888; font-weight: normal; margin-left: 6px"
              >— {{ item.address }}</span
            >
          </div>
          <div class="activity-time">
            {{ formatActivityDateTime(item.lastVisit) }}
          </div>
        </div>
        <div
          v-if="
            !notificationTenantsLoading && notificationTenantsData.length === 0
          "
          class="no-activity"
        >
          No tenants in this group
        </div>
        <div v-if="notificationTenantsLoading" class="no-activity">
          Loading...
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showSmsOptInNamesPopup"
    class="offline-popup-overlay visit-day-names-overlay"
    @click.self="closeSmsOptInsForDay"
  >
    <div class="recent-activity-dialog visit-day-names-dialog">
      <div class="recent-activity-header">
        <span>SMS opt-ins on {{ formatVisitDay(selectedSmsOptInDay) }}</span>
        <button class="close-btn" @click="closeSmsOptInsForDay">&times;</button>
      </div>
      <div class="recent-activity-list">
        <div
          v-for="(item, index) in smsOptInNamesData"
          :key="item.peopleId || index"
          class="activity-item"
        >
          <div class="activity-user" :title="item.address || ''">
            {{ item.fullName }}
            <span
              v-if="item.address"
              style="color: #888; font-weight: normal; margin-left: 6px"
              >— {{ item.address }}</span
            >
          </div>
          <div class="activity-time">
            {{ formatActivityDateTime(item.smsOptInAt) }}
          </div>
        </div>
        <div
          v-if="!smsOptInNamesLoading && smsOptInNamesData.length === 0"
          class="no-activity"
        >
          No opt-ins for this date
        </div>
        <div v-if="smsOptInNamesLoading" class="no-activity">Loading...</div>
      </div>
    </div>
  </div>

  <!-- Tenant names for a visit day -->
  <div
    v-if="showVisitDayNamesPopup"
    class="offline-popup-overlay visit-day-names-overlay"
    @click.self="closeVisitorsForDay"
  >
    <div class="recent-activity-dialog visit-day-names-dialog">
      <div class="recent-activity-header">
        <span>Visitors on {{ formatVisitDay(selectedVisitDay) }}</span>
        <button class="close-btn" @click="closeVisitorsForDay">&times;</button>
      </div>
      <div class="recent-activity-list">
        <div
          v-for="(item, index) in visitDayNamesData"
          :key="item.peopleId || index"
          class="activity-item"
        >
          <div class="activity-user" :title="item.address || ''">
            {{ item.fullName }}
            <span
              v-if="item.address"
              style="color: #888; font-weight: normal; margin-left: 6px"
              >— {{ item.address }}</span
            >
          </div>
        </div>
        <div
          v-if="!visitDayNamesLoading && visitDayNamesData.length === 0"
          class="no-activity"
        >
          No named tenants for this date
        </div>
        <div v-if="visitDayNamesLoading" class="no-activity">Loading...</div>
      </div>
    </div>
  </div>

  <!-- Chatbot Telemetry Popup -->
  <div
    v-if="showChatbotTelemetryPopup"
    class="offline-popup-overlay chatbot-telemetry-overlay"
    @click.self="closeChatbotTelemetryPopup"
  >
    <div
      ref="chatbotTelemetryDialog"
      class="recent-activity-dialog chatbot-telemetry-dialog"
    >
      <div class="recent-activity-header chatbot-telemetry-drag-handle">
        <span>
          <i class="fas fa-grip-vertical" style="opacity: 0.75; margin-right: 6px"></i>
          Tenant App telemetry
        </span>
        <div style="display: flex; align-items: center; gap: 8px">
          <button
            class="refresh-btn telemetry-new-only-btn"
            :class="{ 'is-on': chatbotTelemetryShowNewOnly }"
            @click="toggleChatbotTelemetryNewOnly"
            title="Show new telemetry only"
          >
            New
          </button>
          <button
            class="refresh-btn"
            :disabled="chatbotTelemetryLoading"
            @click="refreshChatbotTelemetry"
            title="Refresh"
          >
            <i
              :class="[
                'fas',
                'fa-sync-alt',
                { 'fa-spin': chatbotTelemetryLoading },
              ]"
            ></i>
          </button>
          <button class="close-btn" @click="closeChatbotTelemetryPopup">
            &times;
          </button>
        </div>
      </div>

      <div class="chatbot-telemetry-toolbar">
        <input
          v-model="chatbotTelemetrySearch"
          class="chatbot-telemetry-search"
          type="search"
          placeholder="Search flow, event, tenant, endpoint, or details"
          @input="scheduleChatbotTelemetrySearch"
        />
        <div class="chatbot-telemetry-summary">
          {{ visibleChatbotTelemetryRows.length }} /
          {{ chatbotTelemetryTotal }}
          {{
            chatbotTelemetryShowNewOnly && chatbotTelemetryApplyNewFilter
              ? 'new'
              : 'rows'
          }}
        </div>
      </div>

      <div class="chatbot-telemetry-list">
        <div
          v-for="row in visibleChatbotTelemetryRows"
          :key="row.id"
          class="telemetry-card"
        >
          <div class="telemetry-card-top">
            <div class="telemetry-card-title">
              {{ row.flow }} · {{ row.event }}
            </div>
            <div class="telemetry-badges">
              <span
                v-if="row.status"
                class="telemetry-badge telemetry-badge-status"
                :class="getTelemetryStatusClass(row.status)"
              >
                {{ row.status }}
              </span>
              <span v-if="row.httpMethod" class="telemetry-badge">
                {{ row.httpMethod }}
              </span>
            </div>
          </div>

          <div class="telemetry-card-meta">
            <div>{{ formatActivityDateTime(row.createdAt) }}</div>
            <div v-if="row.tenantName">{{ row.tenantName }}</div>
            <div v-if="row.peopleId || row.propertyId">
              <span v-if="row.peopleId">peopleId {{ row.peopleId }}</span>
              <span v-if="row.peopleId && row.propertyId"> · </span>
              <span v-if="row.propertyId">propertyId {{ row.propertyId }}</span>
            </div>
            <div v-if="row.endpoint">{{ row.endpoint }}</div>
            <div v-if="row.ipAddress">{{ row.ipAddress }}</div>
          </div>

          <pre
            v-if="formatTelemetryDetails(row.details)"
            class="telemetry-details"
            >{{ formatTelemetryDetails(row.details) }}</pre
          >
        </div>

        <div
          v-if="
            !chatbotTelemetryLoading &&
            visibleChatbotTelemetryRows.length === 0
          "
          class="no-activity"
        >
          {{
            chatbotTelemetryShowNewOnly && chatbotTelemetryApplyNewFilter
              ? 'No new telemetry rows'
              : 'No telemetry rows found'
          }}
        </div>

        <div
          v-if="chatbotTelemetryLoading && chatbotTelemetryRows.length === 0"
          class="no-activity"
        >
          Loading telemetry...
        </div>
      </div>

      <div class="chatbot-telemetry-footer">
        <div v-if="chatbotTelemetryError" class="telemetry-error">
          {{ chatbotTelemetryError }}
        </div>
        <button
          class="telemetry-load-more"
          :disabled="
            chatbotTelemetryLoading ||
            chatbotTelemetryRows.length >= chatbotTelemetryTotal
          "
          @click="loadMoreChatbotTelemetry"
        >
          {{ chatbotTelemetryLoading ? 'Loading...' : 'Load more 500' }}
        </button>
      </div>
    </div>
  </div>

  <DndStatusPopup
    v-if="showDndStatus"
    :rows="dndStatusRows"
    :loading="dndStatusLoading"
    :error="dndStatusError"
    @close="closeDndStatus"
  />

  <StaffTelemetryPopup
    v-if="showStaffTelemetry"
    @close="closeStaffTelemetry"
  />

  <!-- Polling Locks Popup -->
  <div
    v-if="showPollingLocksPopup"
    class="offline-popup-overlay"
    @click.self="closePollingLocksPopup"
  >
    <div class="recent-activity-dialog polling-locks-dialog">
      <div class="recent-activity-header">
        <span>Poll Lock Monitor</span>
        <div style="display: flex; align-items: center; gap: 8px">
          <button
            class="refresh-btn"
            :disabled="
              pollingLocksLoading ||
              pollingLocksResetting ||
              !pollingLocksHasActiveErrors
            "
            @click="resetPollingLocksErrors"
            title="Reset errors"
          >
            <i
              :class="[
                'fas',
                'fa-undo',
                { 'fa-spin': pollingLocksResetting },
              ]"
            ></i>
          </button>
          <button
            class="refresh-btn"
            :disabled="pollingLocksLoading || pollingLocksResetting"
            @click="refreshPollingLocks"
            title="Refresh"
          >
            <i
              :class="[
                'fas',
                'fa-sync-alt',
                { 'fa-spin': pollingLocksLoading },
              ]"
            ></i>
          </button>
          <button class="close-btn" @click="closePollingLocksPopup">
            &times;
          </button>
        </div>
      </div>

      <div class="polling-locks-body">
        <div class="polling-locks-updated">
          Updated: {{ pollingLocksUpdatedAt || '—' }}
        </div>

        <div v-if="pollingLocksError" class="polling-locks-error">
          {{ pollingLocksError }}
        </div>

        <div
          v-else-if="pollingLocksLoading && !pollingLocksServices.length"
          class="polling-locks-empty"
        >
          Loading polling locks...
        </div>

        <div
          v-else-if="!pollingLocksServices.length"
          class="polling-locks-empty"
        >
          No polling locks found. Services may not be initialized yet.
        </div>

        <template v-else>
          <div class="polling-locks-table">
            <div class="polling-locks-row polling-locks-row-header">
              <span>SERVICE</span>
              <span>SERVER</span>
              <span>VERSION</span>
              <span>HEARTBEAT</span>
              <span>LAST SUCCESS</span>
              <span>ERRORS</span>
            </div>

            <div
              v-for="service in pollingLocksServices"
              :key="service.serviceName"
              class="polling-locks-service"
            >
              <div class="polling-locks-row">
                <span class="polling-service-cell">
                  <template v-if="isDev">
                    <button
                      v-if="!isDevPollingLockOwner(service)"
                      type="button"
                      class="polling-lock-action-btn"
                      :disabled="
                        isPollingLockActionBusy(service.serviceName) ||
                        pollingLocksLoading ||
                        pollingLocksResetting
                      "
                      :title="`Claim ${service.serviceName} for EricMiniPC`"
                      @click="claimPollingLock(service.serviceName)"
                    >
                      <i
                        :class="[
                          'fas',
                          'fa-lock',
                          {
                            'fa-spin':
                              pollingLocksClaimingService ===
                              service.serviceName,
                          },
                        ]"
                      ></i>
                    </button>
                    <button
                      v-else
                      type="button"
                      class="polling-lock-action-btn polling-lock-action-btn-release"
                      :disabled="
                        isPollingLockActionBusy(service.serviceName) ||
                        pollingLocksLoading ||
                        pollingLocksResetting
                      "
                      :title="`Release ${service.serviceName} from EricMiniPC`"
                      @click="releasePollingLock(service.serviceName)"
                    >
                      <i
                        :class="[
                          'fas',
                          'fa-unlock',
                          {
                            'fa-spin':
                              pollingLocksReleasingService ===
                              service.serviceName,
                          },
                        ]"
                      ></i>
                    </button>
                  </template>
                  <span
                    class="polling-service-name"
                    title="Double-click to run this service once"
                    @dblclick.stop="openRunServiceDialog(service)"
                    >{{ service.serviceName }}</span
                  >
                </span>
                <span class="polling-server">{{ service.lockedByShort }}</span>
                <span class="polling-version">{{
                  service.appVersionShort
                }}</span>
                <span
                  :class="[
                    'polling-heartbeat',
                    `polling-heartbeat-${service.heartbeatStatus}`,
                  ]"
                >
                  {{ service.heartbeatAgo }}
                </span>
                <span class="polling-last-success">
                  <template v-if="service.lastSuccessAt">
                    <span class="polling-when-primary">{{
                      formatPollingTimestamp(service.lastSuccessAt)
                    }}</span>
                    <span
                      v-if="service.lastSuccessAgo"
                      class="polling-when-ago"
                    >
                      ({{ service.lastSuccessAgo }})
                    </span>
                  </template>
                  <template v-else>—</template>
                </span>
                <span
                  :class="[
                    'polling-errors',
                    { 'polling-errors-active': service.hasErrors },
                  ]"
                >
                  <template v-if="service.hasErrors">
                    <span class="polling-when-primary">
                      {{ service.errorCount }} errors
                      <template v-if="service.lastErrorAt">
                        · {{ formatPollingTimestamp(service.lastErrorAt) }}
                      </template>
                    </span>
                    <span
                      v-if="service.lastErrorAgo"
                      class="polling-when-ago"
                    >
                      ({{ service.lastErrorAgo }})
                    </span>
                  </template>
                  <template v-else>None</template>
                </span>
              </div>

              <div
                v-if="service.hasErrors && service.lastErrorShort"
                class="polling-error-detail"
              >
                └─ {{ service.lastErrorShort }}
              </div>

              <div class="polling-meta">
                Version: {{ service.appVersion }} | Locked:
                {{ formatPollingTimestamp(service.lockedAt) }} | Interval:
                {{ service.intervalMinutes }} min
              </div>
            </div>
          </div>

          <div class="polling-locks-legend">
            Legend:
            <span class="polling-heartbeat-healthy">● Healthy</span> |
            <span class="polling-heartbeat-warning">● Warning</span> |
            <span class="polling-heartbeat-expired">● Expired</span>
          </div>
        </template>
      </div>
    </div>
  </div>

  <!-- Manual one-shot service run dialog -->
  <div
    v-if="showRunServiceDialog"
    class="offline-popup-overlay run-service-overlay"
    @click.self="closeRunServiceDialog"
  >
    <div class="run-service-dialog">
      <div class="recent-activity-header">
        <span>Run service once</span>
        <button
          class="close-btn"
          type="button"
          :disabled="runServiceRunning"
          @click="closeRunServiceDialog"
        >
          &times;
        </button>
      </div>
      <div class="run-service-body">
        <p class="run-service-intro">
          Manually run
          <strong>{{ runServiceName }}</strong>
          one time on this server (skips scheduled stagger delays).
        </p>
        <div
          ref="runServiceOutput"
          class="run-service-output"
          :class="{
            'run-service-output-running': runServiceRunning,
            'run-service-output-failed': runServiceStatus === 'failed',
            'run-service-output-done': runServiceStatus === 'completed',
          }"
        >
          <pre v-if="runServiceLogs.length">{{ runServiceLogsText }}</pre>
          <div v-else class="run-service-output-placeholder">
            {{
              runServiceRunning
                ? 'Starting…'
                : 'Output will appear here when the service runs.'
            }}
          </div>
        </div>
        <div v-if="runServiceStatus === 'failed'" class="run-service-error">
          {{ runServiceError || 'Service run failed' }}
        </div>
        <div class="run-service-actions">
          <button
            type="button"
            class="run-service-download-btn"
            :disabled="!runServiceLogs.length"
            title="Download log output"
            @click="downloadRunServiceLog"
          >
            <i class="fas fa-download" style="margin-right: 6px"></i>
            Download log
          </button>
          <button
            type="button"
            class="run-service-run-btn"
            :disabled="runServiceRunning"
            @click="startRunServiceOnce"
          >
            <i
              v-if="runServiceRunning"
              class="fas fa-spinner fa-spin"
              style="margin-right: 6px"
            ></i>
            {{ runServiceRunning ? 'Running…' : 'Run once' }}
          </button>
          <button
            type="button"
            class="run-service-close-btn"
            :disabled="runServiceRunning"
            @click="closeRunServiceDialog"
          >
            {{ runServiceRunning ? 'Please wait…' : 'Close' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="bottom-right-alerts">
    <!-- Chatbot first in DOM so column-reverse stacks polling above it -->
    <div v-if="showChatbotTelemetryAlert" class="chatbot-telemetry-alert">
      <span>{{
        ignorePrompt === navAlertIds.telemetry
          ? 'Ignore this alert?'
          : 'New Tenant App telemetry received.'
      }}</span>
      <div class="chatbot-telemetry-alert-actions">
        <template v-if="ignorePrompt === navAlertIds.telemetry">
          <button
            type="button"
            @click="confirmIgnoreAlert(navAlertIds.telemetry, false)"
          >
            just once
          </button>
          <button
            type="button"
            @click="confirmIgnoreAlert(navAlertIds.telemetry, true)"
          >
            always
          </button>
        </template>
        <template v-else>
          <button type="button" @click="openChatbotTelemetryFromAlert">
            open
          </button>
          <button
            type="button"
            @click="promptIgnoreAlert(navAlertIds.telemetry)"
          >
            ignore
          </button>
        </template>
      </div>
    </div>

    <div v-if="showPollingLocksAlert" class="polling-locks-alert">
      <span>{{
        ignorePrompt === navAlertIds.pollingLocks
          ? 'Ignore this alert?'
          : 'New polling lock errors detected.'
      }}</span>
      <div class="polling-locks-alert-actions">
        <template v-if="ignorePrompt === navAlertIds.pollingLocks">
          <button
            type="button"
            @click="confirmIgnoreAlert(navAlertIds.pollingLocks, false)"
          >
            just once
          </button>
          <button
            type="button"
            @click="confirmIgnoreAlert(navAlertIds.pollingLocks, true)"
          >
            always
          </button>
        </template>
        <template v-else>
          <button type="button" @click="openPollingLocksFromAlert">open</button>
          <button
            type="button"
            @click="promptIgnoreAlert(navAlertIds.pollingLocks)"
          >
            ignore
          </button>
        </template>
      </div>
    </div>

    <!-- Rent Me last in DOM so column-reverse stacks it above polling -->
    <div v-if="showRentMeAlert" class="rent-me-alert">
      <span>{{
        ignorePrompt === navAlertIds.rentMe
          ? 'Ignore this alert?'
          : 'New Rent Me agreement received.'
      }}</span>
      <div class="rent-me-alert-actions">
        <template v-if="ignorePrompt === navAlertIds.rentMe">
          <button
            type="button"
            @click="confirmIgnoreAlert(navAlertIds.rentMe, false)"
          >
            just once
          </button>
          <button
            type="button"
            @click="confirmIgnoreAlert(navAlertIds.rentMe, true)"
          >
            always
          </button>
        </template>
        <template v-else>
          <button type="button" @click="openRentMeFromAlert">open</button>
          <button
            type="button"
            @click="promptIgnoreAlert(navAlertIds.rentMe)"
          >
            ignore
          </button>
        </template>
      </div>
    </div>
  </div>

  <!-- Daily Chatbot New Users Popup -->
  <div
    v-if="showChatbotNewUsersPopup"
    class="offline-popup-overlay"
    @click.self="closeChatbotNewUsersPopup"
  >
    <div class="recent-activity-dialog chatbot-new-users-dialog">
      <div class="recent-activity-header">
        <span>New Tenant App Users</span>
        <button class="close-btn" @click="closeChatbotNewUsersPopup">
          &times;
        </button>
      </div>
      <div class="recent-activity-list">
        <div
          v-for="(item, index) in chatbotNewUsersData"
          :key="index"
          class="activity-item"
        >
          <div class="activity-user" :title="item.address || ''">
            {{ item.fullName }}
            <span
              v-if="item.address"
              style="color: #888; font-weight: normal; margin-left: 6px"
              >- {{ item.address }}</span
            >
          </div>
          <div class="activity-time">
            {{ formatActivityDateTime(item.agreedAt) }}
          </div>
        </div>
        <div v-if="chatbotNewUsersData.length === 0" class="no-activity">
          No new users since last open
        </div>
      </div>
      <div style="padding: 8px 0 2px 0; font-size: 12px; color: #666">
        This alert appears once daily at 9:00 AM.
      </div>
    </div>
  </div>

  <div
    v-if="smsInboundPopup"
    class="sms-inbound-popup"
    role="dialog"
    aria-label="New SMS Message arrived."
  >
    <div class="sms-inbound-popup-title">
      <span>New SMS Message arrived.</span>
      <button
        type="button"
        class="sms-inbound-popup-close"
        aria-label="Dismiss"
        @click="dismissSmsInboundPopup"
      >
        <i class="fas fa-close"></i>
      </button>
    </div>
    <div class="sms-inbound-popup-main">
      <div class="sms-inbound-popup-sender">{{ smsInboundPopup.sender }}</div>
      <div v-if="smsInboundPopup.time" class="sms-inbound-popup-time">
        {{ smsInboundPopup.time }}
      </div>
      <div v-if="smsInboundPopup.body" class="sms-inbound-popup-body">
        {{ smsInboundPopup.body }}
      </div>
      <div class="sms-inbound-popup-actions">
        <button type="button" @click="goToSmsFromPopup">go to messages</button>
      </div>
    </div>
  </div>
</template>

<script>
import eventBus from './eventBus.js';
import { shouldCountOnHomeBadge } from '@/utils/chatNotificationRules';
import { chatPanelClass, chatPanelBoxStyle } from '@/utils/chatPanelPlacement';
import {
  panelFlagsKeptOnClose,
  rightColumnOpenChanges,
  workOrderPanelClass,
} from '@/utils/workOrderPanelPlacement';
import {
  applyStoredDndAlwaysOn,
  currentDndDevice,
  readDndAlwaysOn,
  shouldShowInsideChatAlert,
} from '@/utils/insideChatDnd';
import {
  smsAlertBadge,
  smsAlertShowsFooter,
  unreadAlwaysOnParam,
  publishDndAlwaysOn,
} from '@/utils/smsAlertQuiet';
import {
  readDismissedSmsPopupId,
  rememberDismissedSmsPopup,
  smsInboundPopupFromPayload,
  shouldShowSmsInboundPopup,
} from '@/utils/smsInboundPopup';
import { smsTabIsVisible } from '@/utils/smsSeen';
import { dndStatusEntries, dndStatusPayload } from '@/utils/dndStatus';
import { getLastEmailId, setLastEmailId } from '@/utils/emailLocalStorage';
import {
  applyPageScrollFromLocation,
  isPageScrollEnabled,
  setPageScrollEnabled,
} from '@/utils/pageScroll';
import ReminderDialog from '@/components/ReminderDialog.vue';
import BulkEmail from '@/components/BulkEmail.vue';
import QuickEmail from '@/components/QuickEmail.vue';
import NotesPanel from '@/components/NotesPanel.vue';
import PayrollPopup from '@/components/PayrollPopup.vue';
import axios from '@/axiosConfig';
import { marked } from 'marked';
import InsideChatTool from './InsideChatTool.vue';
import MobileNotes from './MobileNotes.vue';
import MSmsInbox from './MSmsInbox.vue';
import MVoiceMessages from './MVoiceMessages.vue';
import MCashPosition from './MCashPosition.vue';
import MLockCodes from './MLockCodes.vue';
import MWorkOrders from './MWorkOrders.vue';
import MAppliances from './MAppliances.vue';
import MPropertySummary from './MPropertySummary.vue';
import QuarterTracker from './QuarterTracker.vue';
import CollectorSetup from './CollectorSetup.vue';
import SurveySetup from './SurveySetup.vue';
import RentMeSetup from './RentMeSetup.vue';
import ChatBotMotdDialog from './ChatBotMotdDialog.vue';
import VoicemailGreetingDialog from './VoicemailGreetingDialog.vue';
import StaffSmsDialog from './StaffSmsDialog.vue';
import DndStatusPopup from './DndStatusPopup.vue';
import StaffTelemetryPopup from './StaffTelemetryPopup.vue';
import PastDueSmsDialog from './PastDueSmsDialog.vue';
import CalendarTool from './CalendarTool.vue';
import MotdModal from './MotdModal.vue';
import HelpVideos from './HelpVideos.vue';
import FinancialSummary from './FinancialSummary.vue';
import MaintenanceTrends from './MaintenanceTrends.vue';
import UtilitiesTrend from './UtilitiesTrend.vue';
import InsuranceTrend from './InsuranceTrend.vue';
import TempCharts from './TempCharts.vue';
import PayeeSummary from './PayeeSummary.vue';
import TransactionsDrilldown from './TransactionsDrilldown.vue';
import AnnualCategoryDetail from './AnnualCategoryDetail.vue';
import LateFees from './LateFees.vue';
import PropertyAnalysis from './PropertyAnalysis.vue';
import UnclearedChecks from './UnclearedChecks.vue';
import AceStatementImport from './AceStatementImport.vue';
import BrysonLawncareImport from './BrysonLawncareImport.vue';
import { io } from 'socket.io-client'; // Import socket.io-client
import { makeDraggable } from '@/utils/makeDraggable';
import {
  NAV_ALERT_IDS,
  setAlertAlwaysIgnored,
  shouldShowNavAlert,
} from '@/utils/navAlertSuppress';
import {
  applyNavPanels,
  initialNavPanelFlags,
  readNavPanelsForRoute,
  snapshotNavPanels,
  writeNavPanelsForRoute,
} from '@/utils/navPanelState';

export default {
  components: {
    ReminderDialog,
    BulkEmail,
    QuickEmail,
    NotesPanel,
    PayrollPopup,
    InsideChatTool,
    MobileNotes,
    MSmsInbox,
    MVoiceMessages,
    MCashPosition,
    MLockCodes,
    MWorkOrders,
    MAppliances,
    MPropertySummary,
    QuarterTracker,
    CollectorSetup,
    SurveySetup,
    RentMeSetup,
    ChatBotMotdDialog,
    VoicemailGreetingDialog,
    StaffSmsDialog,
    DndStatusPopup,
    StaffTelemetryPopup,
    PastDueSmsDialog,
    CalendarTool,
    MotdModal,
    HelpVideos,
    FinancialSummary,
    MaintenanceTrends,
    UtilitiesTrend,
    InsuranceTrend,
    TempCharts,
    PayeeSummary,
    TransactionsDrilldown,
    AnnualCategoryDetail,
    LateFees,
    PropertyAnalysis,
    UnclearedChecks,
    AceStatementImport,
    BrysonLawncareImport,
  },

  data() {
    return {
      // users
      users: [],
      myUserId: null,
      myUserRole: null,
      myUserName: '',

      // for appliances
      showAppliances: false,

      // for property summary
      showPropertySummary: false,
      propertySummaryStart: 'menu',

      // for late fees
      showLateFees: false,

      // for Ace statement import
      showAceStatementImport: false,

      // for Bryson lawncare import
      showBrysonLawncareImport: false,

      // for chat tool popup
      showChatTool: false,
      chatMessage: {},
      unreadTenantChatCount: 0,
      insideChatDnd: {
        until: null,
        exceptEmergencies: true,
        exceptStaff: true,
        keywords: '',
      },
      insideChatDndTimer: null,
      staffSmsCount: 0,
      smsInboundPopup: null,
      pendingTenantMessage: null,
      // Track messages this tab has already processed to prevent duplicates
      processedMessageIds: new Set(),
      // Socket connection
      socket: null,
      // Tab activity tracking for active tab strategy
      isTabActive: true,
      chatToolManuallyClosed: false,
      chatToolManualCloseTime: null, // Should be null on fresh page load

      // for payroll popup
      showPayrollPopup: false,

      // for quick reminders
      quickReminders: [],
      showQuickReminderPopup: false,
      reminderPopupCollapsed: false,
      alertingTasks: [],
      remindersCheckInterval: null,
      reminderPopupPollInterval: null, // Poll for changes while popup is visible
      voiceMemoCheckInterval: null,
      calendarEventsCheckInterval: null, // Poll for calendar events count every hour
      voiceMemosCheckInterval: null, // Poll for voice memos count every 5 minutes
      workOrdersCheckInterval: null, // Poll for work orders count every 5 minutes
      documentsCheckInterval: null, // Poll for waiting documents count every 5 minutes
      receiptsCheckInterval: null, // Poll for unread receipts count every 3 minutes
      notesCheckInterval: null, // Poll for notes count every 5 minutes
      mobileNotesCheckInterval: null, // Poll for mobile notes count every 5 minutes
      centerNote: false,
      showQuickRemindersList: false,
      soundEnabled: false,
      selectedReminder: {},
      showQuickNotesHelp: false,

      // for mouse movement detection
      lastMouseX: 0,
      lastMouseY: 0,
      // to check emails
      newEmailsAvailable: false, // Tracks if there are new emails
      unseenEmailsCount: 0,
      lastEmailId: null, // Stores the last seen email ID
      lastEmailTimestamp: null, // Stores the last seen email timestamp
      emailCheckInterval: null, // Interval for checking emails

      // for simpleNotes
      notes: [],
      isGridView: false,
      showPlaceholderIcons: false,
      isAddingNote: false,
      saving: false,
      currentHeightState: 4, // Initial state
      containerHeight: '100%', // Default height
      prevContainerHeight: null,
      showIcons: true,
      mouseOverSimpleNotesContainer: false,
      isEditing: false,
      selectedNote: null,
      hideNotes: true,

      // for regular notes panel
      showNotesPanel: false,
      noteType: '',
      noteId: null,
      newNoteAvailable: true,

      menus: {
        home: false,
        view: false,
        bank: false,
        tools: false,
        help: false,
      },
      menuTimeouts: {},
      userMenuOpen: false,
      pageScrollEnabled: false,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      showScreenSizeWarning: false,
      showReminders: false,
      remindersPanelDragCleanup: null,
      showHelp: false,
      showNote: true,
      showBulkEmail: false,
      showQuickEmail: false,
      displayMessage: '',
      footerMessage: '',
      emailTo: '',
      emailSubject: '',
      emailBody: '',
      isDragMode: false,
      popupPosition: { x: 300, y: 300 }, // Initial popup position
      currentPopup: null, // Holds the currently targeted popup reference
      currentIconOffset: { x: 0, y: 0 }, // Holds the icon offset for dynamic calculations
      searchQuery: '',

      showVoiceMessages: false,
      showMobileNotes: false,
      // for Mobile Cash Pos
      showCashPosition: false,
      // for Lock Codes
      showLockCodes: false,
      // for Lock Codes
      showWorkOrders: false,
      // for Financial Summary
      showFinancialSummary: false,
      // for Maintenance Trends chart
      showMaintenanceTrends: false,
      // for Utilities Trend chart
      showUtilitiesTrend: false,
      // for Insurance Trend chart
      showInsuranceTrend: false,
      // for Temperature Charts
      showTempCharts: false,
      // for Property Analysis
      showPropertyAnalysis: false,
      // for Payee Summary
      showPayeeSummary: false,
      // for Uncleared Checks
      showUnclearedChecks: false,
      payeeSummaryFilter: '',
      // for Transaction Drilldown
      showUncategorizedTransactions: false,
      drilldownCategoryName: 'Transactions',
      uncategorizedTransactions: [],
      uncategorizedMonth: 0,
      uncategorizedYear: new Date().getFullYear(),
      uncategorizedRefreshing: false,
      showAnnualCategoryDetail: false,
      annualDetailCategoryName: 'Transactions',
      annualDetailTransactions: [],
      annualDetailYear: new Date().getFullYear(),
      // for Quarters
      showQuarters: false,
      // for Collector Setup
      showCollectorSetup: false,
      // for Survey Setup
      showSurveySetup: false,
      // for Rent Me Setup
      showRentMeSetup: false,
      showRentMeAlert: false,
      rentMeAlertPendingItemId: null,
      newRentMeAgreementsCount: 0,
      rentMeLatestUnseenItemId: null,
      rentMeLatestUnseenAgreementId: null,
      showChatBotMotd: false,
      showVoicemailCustomize: false,
      showStaffSms: false,
      showDndStatus: false,
      showStaffTelemetry: false,
      dndStatusRows: [],
      dndStatusLoading: false,
      dndStatusError: '',
      showPastDueTexts: false,
      showSmsInbox: false,
      // for Calendar
      showCalendarModal: false,
      calendarInitialMonth: null,
      calendarInitialYear: null,
      calendarInitialDay: null,
      calendarPersonalMode: false,
      calendarInitialGridTab: null,
      // Availability warning
      showAvailabilityWarning: false,
      availabilityWarningWeeks: '',
      todayEventsCount: 0,
      unheardVoiceMemosCount: 0,
      newWorkOrdersCount: 0,
      waitingDocumentsCount: 0,
      unreadReceipts: 0,
      newNotesTodayCount: 0,
      newMobileNotesTodayCount: 0,
      // for Help Videos
      showHelpVideos: false,
      // Health check
      serviceOnline: true,
      showOfflinePopup: false,
      healthCheckInterval: null,
      healthCheckFailureCount: 0,
      healthCheckResumeSuppressUntil: 0,
      // Recent activity
      showRecentActivityPopup: false,
      recentActivityData: [],
      // Rent Payment beta signups / Tenant App Stats
      showRentPaymentSignupsPopup: false,
      rentPaymentSignupsData: [],
      recentVisitorsData: [],
      chatbotVisitsData: [],
      chatbotVersionData: [],
      smsOptInByDayData: [],
      tenantNotificationData: [],
      showNotificationTenantsPopup: false,
      notificationTenantsData: [],
      notificationTenantsLoading: false,
      selectedNotificationState: '',
      selectedNotificationLabel: '',
      showSmsOptInNamesPopup: false,
      smsOptInNamesData: [],
      smsOptInNamesLoading: false,
      selectedSmsOptInDay: '',
      showVersionTenantsPopup: false,
      versionTenantsData: [],
      versionTenantsLoading: false,
      selectedTenantVersion: '',
      rentSignupsLoading: false,
      showVisitDayNamesPopup: false,
      visitDayNamesData: [],
      visitDayNamesLoading: false,
      selectedVisitDay: '',

      // Chatbot telemetry
      showChatbotTelemetryPopup: false,
      chatbotTelemetryRows: [],
      chatbotTelemetryLoading: false,
      chatbotTelemetrySearch: '',
      chatbotTelemetrySearchTimer: null,
      chatbotTelemetryOffset: 0,
      chatbotTelemetryTotal: 0,
      chatbotTelemetryError: '',
      chatbotTelemetryLimit: 500,
      chatbotTelemetryDragCleanup: null,
      chatbotTelemetryShowNewOnly:
        localStorage.getItem('chatbotTelemetryShowNewOnly') === '1',
      chatbotTelemetryApplyNewFilter: false,
      chatbotTelemetryFilterAfterId: 0,

      // Daily chatbot new-users alert
      showChatbotNewUsersPopup: false,
      chatbotNewUsersData: [],
      chatbotDailyPopupInterval: null,

      // Chatbot telemetry new-entry alert
      ignorePrompt: null,
      navAlertIds: NAV_ALERT_IDS,
      showChatbotTelemetryAlert: false,
      chatbotTelemetryAlertPendingId: null,
      chatbotTelemetryMonitorInterval: null,

      // Polling lock monitor
      showPollingLocksPopup: false,
      showPollingLocksAlert: false,
      pollingLocksServices: [],
      pollingLocksLoading: false,
      pollingLocksResetting: false,
      pollingLocksClaimingService: null,
      pollingLocksReleasingService: null,
      pollingLocksError: '',
      pollingLocksUpdatedAt: '',
      pollingLocksMonitorInterval: null,
      showRunServiceDialog: false,
      runServiceName: '',
      runServiceJobId: null,
      runServiceLogs: [],
      runServiceLogOffset: 0,
      runServiceStatus: '',
      runServiceError: '',
      runServiceRunning: false,
      runServicePollTimer: null,
      runServicePollInFlight: false,
      ...initialNavPanelFlags(
        (typeof window !== 'undefined' && window.location.pathname) || '',
      ),
    };
  },

  async created() {
    this.pageScrollEnabled = isPageScrollEnabled();
    await this.fetchUsers();
    if (this.shouldDisplayNavBar()) {
      this.loadReminders();
      await this.syncRemindersFromDatabase();
      this.checkReminders();
      this.setupStorageListener(); // Add storage event listener

      eventBus.on('update:showReminderDialog', (status) => {
        console.log(
          'Received update:showReminderDialog in NavBar with status:',
          status,
        );
        this.showReminders = status;
      });

      // Listen for the 'footerMessage' event to update footerMessage
      eventBus.on('footerMessage', (message) => {
        this.footerMessage = message;

        // Set a timeout to clear the footerMessage after 10 seconds
        setTimeout(() => {
          this.footerMessage = '';
        }, 10000); // 10000 milliseconds = 10 seconds
      });

      eventBus.on(
        'select-document-from-reminder',
        this.handleReminderSelection,
      );

      // Registering the event listener
      eventBus.on('setEmailDetails', this.emailListener);
      eventBus.on('showBulkEmail', this.openBulkEmail);
      eventBus.on('requestCloseChat', this.handleCloseChat);
      eventBus.on('requestCloseCash', this.handleCloseCash);
      eventBus.on('requestCloseLockCodes', this.handleCloseLockCodes);
      eventBus.on('requestCloseMnotes', this.handleCloseMnotes);
      eventBus.on('requestCloseSmsInbox', this.handleCloseSmsInbox);
      eventBus.on('openSmsCompose', this.openSmsCompose);
      eventBus.on('requestCloseVoiceMessages', this.handleCloseVoiceMessages);
      eventBus.on('requestCloseMWorkOrders', this.handleCloseMWorkOrders);
      eventBus.on('requestCloseAppliances', this.handleCloseAppliances);
      eventBus.on('requestCloseProperties', this.handleClosePropertySummary);
      eventBus.on('requestShowShortcuts', this.handleShowShortcuts);
      eventBus.on('closeQuickEmails', this.handleCloseQuickEmails);
      eventBus.on('showQuickEmail', this.handleShowQuickEmails);
      eventBus.on('emailsUnseenCountChanged', this.fetchUnseenEmailsCount);
      eventBus.on('requestCloseQuarters', this.handleCloseQuarters);
      eventBus.on('auto-open-financials', this.openFinancialSummary);
      eventBus.on('close-financial-summary', this.closeFinancialSummary);
      eventBus.on('openCashPosition', this.openCashPosition);
      eventBus.on('run-automatch-only', this.handleRunAutomatchOnlyRequest);
    }
  },

  mounted() {
    if (this.shouldDisplayNavBar()) {
      console.log('🔄 NavBar mounted - Initial state:');
      console.log('🔄 chatToolManuallyClosed:', this.chatToolManuallyClosed);
      console.log('🔄 chatToolManualCloseTime:', this.chatToolManualCloseTime);
      console.log('🔄 showChatTool:', this.showChatTool);

      // Set up auto-open-financials listener in mounted as well (in case of timing issues)
      eventBus.on('auto-open-financials', this.openFinancialSummary);

      // Set up calendar listener for opening to specific dates
      eventBus.on('openCalendarToDate', this.openCalendarToDate);

      // Set up listener to close NavBar panels when ledger row is selected
      eventBus.on('closeNavBarPanels', this.handleCloseNavBarPanels);
      eventBus.on('inside-chat-dnd-changed', this.onInsideChatDndChanged);

      // Set up global debugging access
      this.setupGlobalDebugging();

      // Add visibility change listener for tab activity detection
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );

      this.connectSocket();
      window.addEventListener('mousemove', this.handleUserInteraction);
      window.addEventListener('keydown', this.handleKeyDown);
      window.addEventListener('resize', this.handleWindowResize);
      document.addEventListener('click', this.handleDocumentClick);

      this.fetchNotes();
      this.initializeEmailState();
      this.fetchUnseenEmailsCount();
      this.fetchTodayEventsCount();
      this.fetchNewRentMeAgreementsCount();
      this.fetchUnheardVoiceMemosCount();
      this.fetchNewWorkOrdersCount();
      this.fetchWaitingDocumentsCount();
      this.fetchUnreadReceipts();
      this.fetchNewNotesTodayCount();
      this.fetchNewMobileNotesTodayCount();
      this.checkUnreadTenantMessages();
      this.checkAvailabilitySlots();

      this.remindersCheckInterval = setInterval(
        () => this.checkReminders(),
        60000,
      );

      this.isTabActive = !document.hidden;
      this.initHealthCheckPolling();

      // Check calendar events count every hour (3600000 ms = 1 hour)
      this.calendarEventsCheckInterval = setInterval(
        () => this.fetchTodayEventsCount(),
        3600000,
      );

      // Check unseen emails every 5 minutes (300000 ms = 5 minutes)
      this.emailCheckInterval = setInterval(
        () => this.fetchUnseenEmailsCount(),
        300000,
      );

      // Check voice memos count every 5 minutes (300000 ms = 5 minutes)
      this.voiceMemosCheckInterval = setInterval(
        () => this.fetchUnheardVoiceMemosCount(),
        300000,
      );

      // Check work orders count every 5 minutes (300000 ms = 5 minutes)
      this.workOrdersCheckInterval = setInterval(
        () => this.fetchNewWorkOrdersCount(),
        300000,
      );

      // Check waiting documents count every 5 minutes (300000 ms = 5 minutes)
      this.documentsCheckInterval = setInterval(
        () => this.fetchWaitingDocumentsCount(),
        300000,
      );

      // Check unread receipts every 3 minutes (matches server poll interval)
      this.receiptsCheckInterval = setInterval(
        () => this.fetchUnreadReceipts(),
        180000,
      );

      // Check notes count every 5 minutes (300000 ms = 5 minutes)
      this.notesCheckInterval = setInterval(
        () => this.fetchNewNotesTodayCount(),
        300000,
      );

      // Check mobile notes count every 5 minutes (300000 ms = 5 minutes)
      this.mobileNotesCheckInterval = setInterval(
        () => this.fetchNewMobileNotesTodayCount(),
        300000,
      );

      // start quicknotes mostly collapsed
      this.currentHeightState = 1;

      // then shrink to minumum
      setTimeout(() => {
        if (!this.isEditing) {
          this.currentHeightState = 3;
          let noteHeight =
            document.querySelector('.note-tile')?.offsetHeight || 0;
          this.containerHeight = `${noteHeight + 0}px`;
        }
      }, 1000);

      // Check if we should show the screen size warning
      this.checkScreenSizeWarning();

      // Start daily 9:00am chatbot new-user alert monitor
      this.startChatbotDailyPopupMonitor();

      // Poll for new chatbot telemetry every 5 minutes
      this.startChatbotTelemetryMonitor();

      // Poll for new polling lock errors every 5 minutes
      this.startPollingLocksMonitor();
    }
  },

  beforeUnmount() {
    eventBus.off('inside-chat-dnd-changed', this.onInsideChatDndChanged);
    clearTimeout(this.insideChatDndTimer);
    // Clean up visibility change listener
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );

    if (this.socket && this.socket.connected) {
      this.socket.disconnect();
      this.socket.off('newChatMessages', this.handleNewChatMessages);
      this.socket.off('tenantChatMessage', this.handleTenantChatMessage);
      this.socket.off('newEmailMessages', this.handleNewEmails);
      this.socket.off('unreadReceiptsChanged', this.fetchUnreadReceipts);
      this.socket.off('staffSmsInbound', this.handleStaffSmsInbound);
      this.socket.off('voiceMessagesInbound', this.handleVoiceMessagesInbound);
      this.socket.off('newSimpleNote', this.handleNewSimpleNote);
      this.socket.off('updatedSimpleNote', this.handleUpdatedSimpleNote);
      this.socket.off('deleteSimpleNote', this.handleDeletedSimpleNote);
      // Quarter Tracker events
      this.socket.off('quarterRecordCreated', this.handleQuarterRecordCreated);
      this.socket.off('quarterRecordUpdated', this.handleQuarterRecordUpdated);
      this.socket.off('quarterRecordDeleted', this.handleQuarterRecordDeleted);
      this.socket.off('reminderForUser', this.handleReminderForUser);
      // QR Collector events
      this.socket.off(
        'collectorCampaignCreated',
        this.handleCollectorCampaignCreated,
      );
      this.socket.off(
        'collectorCampaignUpdated',
        this.handleCollectorCampaignUpdated,
      );
      this.socket.off(
        'collectorCampaignDeleted',
        this.handleCollectorCampaignDeleted,
      );
      this.socket.off(
        'collectorResponseCreated',
        this.handleCollectorResponseCreated,
      );
      this.socket.off(
        'rentMeAgreementCreated',
        this.handleRentMeAgreementCreated,
      );
    }

    if (this.remindersCheckInterval) {
      clearInterval(this.remindersCheckInterval); // Clear the reminders check interval
    }

    if (this.reminderPopupPollInterval) {
      clearInterval(this.reminderPopupPollInterval); // Clear the popup polling interval
    }

    if (this.calendarEventsCheckInterval) {
      clearInterval(this.calendarEventsCheckInterval); // Clear the calendar events check interval
    }

    if (this.emailCheckInterval) {
      clearInterval(this.emailCheckInterval);
    }

    if (this.voiceMemosCheckInterval) {
      clearInterval(this.voiceMemosCheckInterval); // Clear the voice memos check interval
    }

    if (this.workOrdersCheckInterval) {
      clearInterval(this.workOrdersCheckInterval); // Clear the work orders check interval
    }

    if (this.documentsCheckInterval) {
      clearInterval(this.documentsCheckInterval); // Clear the documents check interval
    }

    if (this.receiptsCheckInterval) {
      clearInterval(this.receiptsCheckInterval);
    }

    if (this.notesCheckInterval) {
      clearInterval(this.notesCheckInterval); // Clear the notes check interval
    }

    if (this.mobileNotesCheckInterval) {
      clearInterval(this.mobileNotesCheckInterval); // Clear the mobile notes check interval
    }

    this.stopHealthCheckPolling();

    if (this.chatbotDailyPopupInterval) {
      clearInterval(this.chatbotDailyPopupInterval);
    }

    if (this.chatbotTelemetryMonitorInterval) {
      clearInterval(this.chatbotTelemetryMonitorInterval);
    }

    if (this.pollingLocksMonitorInterval) {
      clearInterval(this.pollingLocksMonitorInterval);
    }

    this.stopRunServicePoll();
    this.teardownChatbotTelemetryDrag();
    this.teardownRemindersPanelDrag();

    if (this.chatbotTelemetrySearchTimer) {
      clearTimeout(this.chatbotTelemetrySearchTimer);
    }

    if (this.emailListener) {
      eventBus.off('setEmailDetails', this.emailListener);
    }

    window.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleDocumentClick);

    eventBus.off('showBulkEmail', this.openBulkEmail);
    eventBus.off('select-document-from-reminder', this.handleReminderSelection);
    eventBus.off('requestCloseChat', this.handleCloseChat);
    eventBus.off('requestCloseCash', this.handleCloseCash);
    eventBus.off('requestCloseMnotes', this.handleCloseMnotes);
    eventBus.off('requestCloseSmsInbox', this.handleCloseSmsInbox);
    eventBus.off('openSmsCompose', this.openSmsCompose);
    eventBus.off('requestCloseVoiceMessages', this.handleCloseVoiceMessages);
    eventBus.off('requestCloseLockCodes', this.handleCloseLockCodes);
    eventBus.off('requestCloseMWorkOrders', this.handleCloseMWorkOrders);
    eventBus.off('requestCloseAppliances', this.handleCloseAppliances);
    eventBus.off('requestCloseProperties', this.handleClosePropertySummary);
    eventBus.off('requestCloseQuarters', this.handleCloseQuarters);
    eventBus.off('closeQuickEmails', this.handleCloseQuickEmails);
    eventBus.off('showQuickEmail', this.handleShowQuickEmails);
    eventBus.off('emailsUnseenCountChanged', this.fetchUnseenEmailsCount);
    eventBus.off('auto-open-financials', this.openFinancialSummary);
    eventBus.off('openCalendarToDate', this.openCalendarToDate);
    eventBus.off('closeNavBarPanels', this.handleCloseNavBarPanels);
    eventBus.off('run-automatch-only', this.handleRunAutomatchOnlyRequest);
    eventBus.off('update:showReminderDialog', (status) => {
      this.showReminders = status;
    });
    eventBus.off('footerMessage', (message) => {
      this.footerMessage = message;
    });
    eventBus.off('requestShowShortcuts', this.handleShowShortcuts);
  },

  computed: {
    // add a function called isDev that returns true if the URL contains :8080
    isDev() {
      return window.location.href.includes(':8080');
    },

    runServiceLogsText() {
      return (this.runServiceLogs || []).join('\n');
    },

    openNavPanelSnapshot() {
      return snapshotNavPanels(this).join(',');
    },
    pollingLocksHasActiveErrors() {
      return (this.pollingLocksServices || []).some(
        (service) => service.hasErrors,
      );
    },

    visibleChatbotTelemetryRows() {
      if (
        !this.chatbotTelemetryShowNewOnly ||
        !this.chatbotTelemetryApplyNewFilter
      ) {
        return this.chatbotTelemetryRows;
      }
      const afterId = this.chatbotTelemetryFilterAfterId || 0;
      return this.chatbotTelemetryRows.filter(
        (row) => Number(row.id) > afterId,
      );
    },

    quickEmailWrapperClass() {
      // Determine classes or styles dynamically based on the route
      if (this.$route.path === '/main') {
        return 'quick-email-wrapper wrapper-absolute-position-3 wrapper-main-right-column';
      } else if (this.$route.path === '/lease-search') {
        return 'quick-email-wrapper wrapper-absolute-position-2';
      } else if (this.$route.path === '/documents') {
        return 'quick-email-wrapper wrapper-absolute-position-3';
      }
      // Default or other routes handling
      return 'quick-email-wrapper';
    },

    notesWrapperClass() {
      // Determine classes or styles dynamically based on the route
      if (this.$route.path === '/main') {
        // Add appropriate classes for '/main'
        return 'mobile-notes-wrapper wrapper-absolute-position';
      } else if (this.$route.path === '/lease-search') {
        return 'mobile-notes-wrapper wrapper-absolute-position-2';
      }
      // Default or other routes handling
      return 'mobile-notes-wrapper';
    },

    lockCodesWrapperClass() {
      // Determine classes or styles dynamically based on the route
      if (this.$route.path === '/main') {
        // Add appropriate classes for '/main'
        return 'chat-wrapper wrapper-absolute-position';
      } else if (this.$route.path === '/lease-search') {
        return 'chat-wrapper wrapper-absolute-position-2';
      } else if (this.$route.path === '/documents') {
        return 'chat-wrapper wrapper-absolute-position-3';
      }
      // Default or other routes handling
      return 'chat-wrapper';
    },
    chatWrapperClass() {
      return chatPanelClass(this.$route.path);
    },
    chatWrapperStyle() {
      return chatPanelBoxStyle(this.$route.path);
    },

    workOrderWrapperClass() {
      return workOrderPanelClass(this.$route.path);
    },

    appliancesWrapperClass() {
      // Determine classes or styles dynamically based on the route
      if (this.$route.path === '/main') {
        // Add appropriate classes for '/main'
        return 'appliances-wrapper wrapper-absolute-position';
      } else if (this.$route.path === '/lease-search') {
        return 'appliances-wrapper wrapper-absolute-position-2';
      } else if (this.$route.path === '/documents') {
        return 'appliances-wrapper wrapper-absolute-position-3';
      }
      // Default or other routes handling
      return 'chat-wrapper';
    },

    quartersWrapperClass() {
      // Determine classes or styles dynamically based on the route
      if (this.$route.path === '/main') {
        // Add appropriate classes for '/main'
        return 'quarters-wrapper wrapper-absolute-position';
      } else if (this.$route.path === '/lease-search') {
        return 'quarters-wrapper wrapper-absolute-position-2';
      } else if (this.$route.path === '/documents') {
        return 'quarters-wrapper wrapper-absolute-position-3';
      }
      // Default or other routes handling
      return 'chat-wrapper';
    },

    collectorSetupWrapperClass() {
      // Determine classes or styles dynamically based on the route
      if (this.$route.path === '/main') {
        return 'collector-setup-wrapper wrapper-absolute-position';
      } else if (this.$route.path === '/lease-search') {
        return 'collector-setup-wrapper wrapper-absolute-position-2';
      } else if (this.$route.path === '/documents') {
        return 'collector-setup-wrapper wrapper-absolute-position-3';
      }
      // Default or other routes handling
      return 'chat-wrapper';
    },

    surveySetupWrapperClass() {
      // Determine classes or styles dynamically based on the route
      if (this.$route.path === '/main') {
        return 'survey-setup-wrapper wrapper-absolute-position';
      } else if (this.$route.path === '/lease-search') {
        return 'survey-setup-wrapper wrapper-absolute-position-2';
      } else if (this.$route.path === '/documents') {
        return 'survey-setup-wrapper wrapper-absolute-position-3';
      }
      // Default or other routes handling
      return 'chat-wrapper';
    },

    rentMeSetupWrapperClass() {
      if (this.$route.path === '/main') {
        return 'collector-setup-wrapper wrapper-absolute-position';
      } else if (this.$route.path === '/lease-search') {
        return 'collector-setup-wrapper wrapper-absolute-position-2';
      } else if (this.$route.path === '/documents') {
        return 'collector-setup-wrapper wrapper-absolute-position-3';
      }
      return 'chat-wrapper';
    },

    cashWrapperClass() {
      // Determine classes or styles dynamically based on the route
      if (this.$route.path === '/main') {
        return 'chat-wrapper wrapper-absolute-position';
      } else if (this.$route.path === '/lease-search') {
        return 'cash-position-wrapper wrapper-absolute-position-2';
      } else if (this.$route.path === '/documents') {
        return 'chat-wrapper wrapper-absolute-position-3';
      }
      // Default or other routes handling
      return 'chat-wrapper';
    },

    notesContainerHeight() {
      return this.hideNotes ? '60px' : '675px';
    },

    sortedQuickReminders() {
      return this.quickReminders.slice().sort((a, b) => {
        return new Date(b.dateTime) - new Date(a.dateTime);
      });
    },

    isMainPage() {
      return this.$route.path === '/main';
    },

    filteredNotes() {
      if (!this.searchQuery) {
        return this.notes;
      }

      const query = this.searchQuery.toLowerCase();
      if (query === ':t') {
        // Filter notes from today
        const today = new Date(); // Get the current date

        const isSameDay = (date1, date2) =>
          date1.getFullYear() === date2.getFullYear() &&
          date1.getMonth() === date2.getMonth() &&
          date1.getDate() === date2.getDate();

        return this.notes.filter((note) => {
          const noteDate = new Date(note.modifiedOn); // Assuming `note.modifiedOn` stores the date
          return isSameDay(noteDate, today);
        });
      }

      if (query === ':y') {
        // Filter notes from yesterday
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);

        const isSameDay = (date1, date2) =>
          date1.getFullYear() === date2.getFullYear() &&
          date1.getMonth() === date2.getMonth() &&
          date1.getDate() === date2.getDate();

        return this.notes.filter((note) => {
          const noteDate = new Date(note.modifiedOn); // Assuming `note.date` stores the creation date
          return isSameDay(noteDate, yesterday);
        });
      }
      if (query === ':o') {
        // Filter notes older than yesterday
        const dayBeforeYesterday = new Date();
        dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2); // Two days ago

        return this.notes.filter((note) => {
          const noteDate = new Date(note.modifiedOn); // Assuming `note.modifiedOn` stores the date
          return noteDate < dayBeforeYesterday; // Check if the note is older than two days ago
        });
      }

      return this.notes
        .filter((note) => note.note.toLowerCase().includes(query))
        .map((note) => ({
          ...note,
          highlightedNote: this.highlightMatch(note.note), // Add highlighted content
        }));
    },
  },

  methods: {
    initHealthCheckPolling() {
      if (document.hidden) {
        return;
      }
      this.performHealthCheck();
      this.startHealthCheckPolling();
    },

    startHealthCheckPolling() {
      this.stopHealthCheckPolling();
      if (document.hidden) {
        return;
      }
      this.healthCheckInterval = setInterval(
        () => this.performHealthCheck(),
        30000,
      );
    },

    stopHealthCheckPolling() {
      if (this.healthCheckInterval) {
        clearInterval(this.healthCheckInterval);
        this.healthCheckInterval = null;
      }
    },

    async performHealthCheck() {
      const failureThreshold = 3;
      const inResumeGracePeriod =
        Date.now() < this.healthCheckResumeSuppressUntil;

      try {
        await axios.get('/api/health', { timeout: 10000 });
        this.healthCheckFailureCount = 0;
        if (!this.serviceOnline) {
          this.showOfflinePopup = false;
        }
        this.serviceOnline = true;
      } catch (error) {
        this.healthCheckFailureCount += 1;

        if (
          this.healthCheckFailureCount < failureThreshold ||
          inResumeGracePeriod
        ) {
          return;
        }

        const wasOnline = this.serviceOnline;
        this.serviceOnline = false;
        if (wasOnline) {
          this.showOfflinePopup = true;
          console.warn(
            '[NavBar] Service appears offline after',
            this.healthCheckFailureCount,
            'failed /api/health checks:',
            error?.message || error,
          );
        }
      }
    },

    async fetchUsers() {
      try {
        const response = await axios.get('/api/users');
        this.users = response.data || [];
        const currentUser = this.users
          ? this.users.find((user) => user.you === 1)
          : null; // Find the user where 'you' === 1
        console.log('userdata:', this.users);
        console.log('Current user:', currentUser);

        if (currentUser) {
          this.myUserId = currentUser.id;
          this.myUserRole = Number(currentUser.role);
          this.insideChatDnd = applyStoredDndAlwaysOn(
            this.insideChatDnd,
            this.myUserRole,
            window.localStorage,
          );
          this.myUserName =
            currentUser.displayName || currentUser.username || 'User';
          this.publishSmsAlwaysOn();
          console.log('✅ User ID set to:', this.myUserId);
          console.log('✅ User Name set to:', this.myUserName);

          if (this.myUserRole <= 1) {
            this.checkForNewChatbotTelemetry();
            this.checkForNewPollingLockErrors();
          }
        } else {
          console.log('❌ No current user found with you === 1');
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },

    persistOpenNavPanels() {
      if (this._skipNavPanelPersist) return;
      const path = this.$route && this.$route.path;
      if (!path) return;
      writeNavPanelsForRoute(path, snapshotNavPanels(this));
    },
    restoreOpenNavPanels(path) {
      this._skipNavPanelPersist = true;
      Object.assign(
        this,
        applyNavPanels(readNavPanelsForRoute(path)),
      );
      this.$nextTick(() => {
        this._skipNavPanelPersist = false;
      });
    },
    handleCloseNavBarPanels() {
      // Close tool panels. On /properties the right column (Quick Email and
      // Work Orders) stays up so a left-column tool such as Chat Bot can
      // sit beside it.
      const keepRightColumn = new Set(
        panelFlagsKeptOnClose(this.$route.path),
      );
      this.showMobileNotes = false;
      if (!keepRightColumn.has('showWorkOrders')) {
        this.showWorkOrders = false;
      }
      if (!keepRightColumn.has('showQuickEmail')) {
        this.showQuickEmail = false;
      }
      this.showCashPosition = false;
      this.showLockCodes = false;
      this.showAppliances = false;
      this.showPropertySummary = false;
      this.showBulkEmail = false;
      this.showPayrollPopup = false;
      this.showNotesPanel = false;
      this.showVoiceMessages = false;
      this.showChatTool = false;
      this.showHelpVideos = false;
      this.showCalendarModal = false;
      this.showQuarters = false;
      this.showCollectorSetup = false;
      this.showSurveySetup = false;
      this.showRentMeSetup = false;
      this.showChatBotMotd = false;
      this.showVoicemailCustomize = false;
      this.showStaffSms = false;
      this.showDndStatus = false;
      this.showStaffTelemetry = false;
      this.showPastDueTexts = false;
      this.showSmsInbox = false;
      this.showLateFees = false;
      this.showAceStatementImport = false;
      this.showBrysonLawncareImport = false;
      this.showReminders = false;
      this.showHelp = false;
      this.showQuickRemindersList = false;
      this.showFinancialSummary = false;
      this.showPayeeSummary = false;
      this.showPropertyAnalysis = false;
      this.showUnclearedChecks = false;
      this.showMaintenanceTrends = false;
      this.showUtilitiesTrend = false;
      this.showInsuranceTrend = false;
      this.showTempCharts = false;
      this.showAnnualCategoryDetail = false;
      this.showUncategorizedTransactions = false;
      this.closeChatbotTelemetryPopup();
    },

    // Function to get auth token from localStorage
    getAuthToken() {
      return localStorage.getItem('token');
    },

    // Function to connect to WebSocket server
    connectSocket() {
      console.log('🔌 NavBar: Starting socket connection...');
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;
        console.log('🔌 Socket URL:', socketUrl);

        if (!socketUrl) {
          console.error('Socket URL is undefined.');
          return;
        }

        const token = this.getAuthToken();
        console.log('🔌 Auth token exists:', !!token);

        this.socket = io(socketUrl, {
          query: {
            token: token,
            source: 'navbar',
          },
        });

        console.log('🔌 Socket instance created:', !!this.socket);

        this.socket.on('connect', () => {
          console.log('🔌 Navbar: Connected to WebSocket server');
          console.log('🔌 Socket ID:', this.socket.id);
          console.log('🔌 Socket connected status:', this.socket.connected);
          console.log(
            '🔌 Socket transport type:',
            this.socket.io.engine.transport.name,
          );

          // Authenticate with the server to join user-specific rooms
          console.log('🔌 Navbar: Sending authentication with token');
          this.socket.emit('authenticate', token);

          // Debug: Check what rooms we're in after a short delay
          setTimeout(() => {
            console.log('🔌 Navbar: Post-auth Socket ID:', this.socket.id);
            console.log(
              '🔌 Navbar: Post-auth Connected:',
              this.socket.connected,
            );

            // Test if we can emit any event
            console.log('🔌 Testing basic emit...');
            this.socket.emit('test-navbar-connection', {
              userId: this.myUserId,
              timestamp: Date.now(),
              message: 'Hello from NavBar',
            });
          }, 1000);
        });

        // Add debugging for all socket events
        this.socket.onAny((eventName, ...args) => {
          console.log(`🔔 Navbar received socket event: ${eventName}`, args);
          // Special logging for newChatMessages
          if (eventName === 'newChatMessages') {
            console.log('🔔 NEW CHAT MESSAGE EVENT DETAILS:', {
              eventName,
              dataReceived: args,
              socketId: this.socket.id,
              isTabActive: this.isTabActive,
              currentTimestamp: Date.now(),
            });
          }
        });

        this.socket.on('connect_error', (error) => {
          console.error('🔌 NavBar socket connection error:', error);
        });

        this.socket.on('disconnect', (reason) => {
          console.log(
            '🔌 NavBar disconnected from WebSocket server. Reason:',
            reason,
          );
        });

        this.socket.on('reconnect', () => {
          console.log('🔌 Navbar: Reconnected to WebSocket server');
          // Re-authenticate after reconnect
          console.log('🔌 Navbar: Re-authenticating after reconnect');
          this.socket.emit('authenticate', token);
        });

        // Clean up any existing listeners before registering new ones
        console.log('🔌 Cleaning up old socket event handlers...');
        this.socket.off('newChatMessages', this.handleNewChatMessages);
        this.socket.off('tenantChatMessage', this.handleTenantChatMessage);
        this.socket.off('newEmailMessages', this.handleNewEmails);
        this.socket.off('staffSmsInbound', this.handleStaffSmsInbound);
        this.socket.off('voiceMessagesInbound', this.handleVoiceMessagesInbound);
        this.socket.off('newSimpleNote', this.handleNewSimpleNote);
        this.socket.off('updatedSimpleNote', this.handleUpdatedSimpleNote);
        this.socket.off('deleteSimpleNote', this.handleDeletedSimpleNote);
        this.socket.off(
          'quarterRecordCreated',
          this.handleQuarterRecordCreated,
        );
        this.socket.off(
          'quarterRecordUpdated',
          this.handleQuarterRecordUpdated,
        );
        this.socket.off(
          'quarterRecordDeleted',
          this.handleQuarterRecordDeleted,
        );
        this.socket.off('reminderForUser', this.handleReminderForUser);
        this.socket.off(
          'collectorCampaignCreated',
          this.handleCollectorCampaignCreated,
        );
        this.socket.off(
          'collectorCampaignUpdated',
          this.handleCollectorCampaignUpdated,
        );
        this.socket.off(
          'collectorCampaignDeleted',
          this.handleCollectorCampaignDeleted,
        );
        this.socket.off(
          'collectorResponseCreated',
          this.handleCollectorResponseCreated,
        );
        this.socket.off(
          'rentMeAgreementCreated',
          this.handleRentMeAgreementCreated,
        );

        // Register all application event listeners
        console.log('🔌 Registering socket event handlers...');
        this.socket.on('newChatMessages', this.handleNewChatMessages);
        this.socket.on('tenantChatMessage', this.handleTenantChatMessage);
        this.socket.on('newEmailMessages', this.handleNewEmails);
        this.socket.on('unreadReceiptsChanged', this.fetchUnreadReceipts);
        this.socket.on('staffSmsInbound', this.handleStaffSmsInbound);
        this.socket.on('voiceMessagesInbound', this.handleVoiceMessagesInbound);
        this.socket.on('newSimpleNote', this.handleNewSimpleNote);
        this.socket.on('updatedSimpleNote', this.handleUpdatedSimpleNote);
        this.socket.on('deleteSimpleNote', this.handleDeletedSimpleNote);
        // Quarter Tracker events
        this.socket.on('quarterRecordCreated', this.handleQuarterRecordCreated);
        this.socket.on('quarterRecordUpdated', this.handleQuarterRecordUpdated);
        this.socket.on('quarterRecordDeleted', this.handleQuarterRecordDeleted);
        this.socket.on('reminderForUser', this.handleReminderForUser);
        // QR Collector events
        this.socket.on(
          'collectorCampaignCreated',
          this.handleCollectorCampaignCreated,
        );
        this.socket.on(
          'collectorCampaignUpdated',
          this.handleCollectorCampaignUpdated,
        );
        this.socket.on(
          'collectorCampaignDeleted',
          this.handleCollectorCampaignDeleted,
        );
        this.socket.on(
          'collectorResponseCreated',
          this.handleCollectorResponseCreated,
        );
        this.socket.on(
          'rentMeAgreementCreated',
          this.handleRentMeAgreementCreated,
        );
        console.log('🔌 Socket event handlers registered');
      } catch (error) {
        console.error('🔌 Error connecting to WebSocket server:', error);
      }
    },

    // Function to handle new chat messages
    handleNewChatMessages(data) {
      console.log('🔔 Navbar got socket message:', data);
      console.log('🔔 Current myUserId:', this.myUserId);
      console.log('🔔 Tab is active:', this.isTabActive);
      console.log('🔔 Socket connected:', this.socket?.connected);
      console.log('🔔 Current showChatTool:', this.showChatTool);
      console.log('🔔 Chat tool manually closed:', this.chatToolManuallyClosed);

      if (!data) return;
      if (Array.isArray(data)) data = data[0];

      // Only process chat messages for UI updates if this tab is active
      if (!this.isTabActive) {
        console.log(
          '⏸️ Tab is inactive - ignoring chat message for UI updates',
        );
        return;
      }

      // Check if this tab has already processed this message for UI notifications
      const isFirstTimeInTab = !this.processedMessageIds.has(data.id);

      if (isFirstTimeInTab) {
        // Mark this message as processed by this tab
        this.processedMessageIds.add(data.id);
        console.log(
          `📌 Processing message ${data.id} for the first time in this tab`,
        );

        // Keep the Set size manageable (only keep last 100 message IDs)
        if (this.processedMessageIds.size > 100) {
          const oldestId = this.processedMessageIds.values().next().value;
          this.processedMessageIds.delete(oldestId);
        }
      } else {
        console.log(
          `📌 Message ${data.id} already seen by this tab, but still processing for active tab`,
        );
      }

      this.chatMessage = data;

      console.log('Chat message details:', {
        fromUserId: this.chatMessage?.fromUserId,
        toUserId: this.chatMessage?.toUserId,
        fromPeopleId: this.chatMessage?.fromPeopleId,
        myUserId: this.myUserId,
      });

      // only show the chat tool if the message is to or from the current user
      // or from a person to no one in particular or from system
      const fromUid =
        this.chatMessage?.fromUserId == null
          ? null
          : Number(this.chatMessage.fromUserId);
      const toUid =
        this.chatMessage?.toUserId == null
          ? null
          : Number(this.chatMessage.toUserId);

      // Live Chat Bot badge: staff DM/group only. System-channel rows
      // badge the System chat head, not this tile.
      if (
        isFirstTimeInTab &&
        shouldCountOnHomeBadge(this.chatMessage, this.myUserId) &&
        shouldShowInsideChatAlert(this.chatMessage, this.insideChatDnd)
      ) {
        this.checkUnreadTenantMessages();
      }

      const condition1 = fromUid === 0;
      const condition2 = toUid === Number(this.myUserId);
      const condition3 = fromUid === Number(this.myUserId);
      const condition4 =
        this.chatMessage?.fromPeopleId && !this.chatMessage?.toUserId;

      console.log('🔔 Condition checks:', {
        condition1: `fromUserId === 0: ${condition1}`,
        condition2: `toUserId === myUserId: ${condition2}`,
        condition3: `fromUserId === myUserId: ${condition3}`,
        condition4: `fromPeopleId && toUserId === null: ${condition4}`,
        overallCondition: condition1 || condition2 || condition3 || condition4,
      });

      if (
        (condition1 || condition2 || condition3 || condition4) &&
        shouldShowInsideChatAlert(this.chatMessage, this.insideChatDnd)
      ) {
        console.log('✅ Condition met - checking if should show chat tool');
        console.log(
          '🔔 chatToolManuallyClosed status:',
          this.chatToolManuallyClosed,
        );
        console.log(
          '🔔 chatToolManualCloseTime:',
          this.chatToolManualCloseTime,
        );
        console.log('🔔 Current timestamp:', Date.now());

        // Check if enough time has passed since manual close (backup check)
        const now = Date.now();
        const timeSinceManualClose = this.chatToolManualCloseTime
          ? now - this.chatToolManualCloseTime
          : Infinity; // If never manually closed, consider grace period as expired
        const graceExpired = timeSinceManualClose > 10000; // 10 seconds

        console.log('🔔 Time since manual close:', timeSinceManualClose + 'ms');
        console.log('🔔 Grace period expired:', graceExpired);

        // Only auto-open if not manually closed OR if grace period has expired
        if (!this.chatToolManuallyClosed || graceExpired) {
          if (graceExpired && this.chatToolManuallyClosed) {
            console.log('🟢 Grace period expired - clearing manual close flag');
            this.chatToolManuallyClosed = false;
            this.chatToolManualCloseTime = null;
          }
          console.log(
            '✅ Auto-opening chat tool - conditions met for auto-open',
          );
          this.showChatTool = true;
          // Keep badge until threads are actually marked read
        } else {
          console.log(
            '⏸️ Not auto-opening - chat tool was manually closed recently',
          );
          console.log(
            '⏸️ Will auto-open again after',
            10000 - timeSinceManualClose + 'ms',
          );
        }

        this.chatMessage = data;
        this.$nextTick(() => {
          // console.log('Navbar attempting relay', data);
          // socket.emit('relayChatMessage', data);
        });
      } else {
        console.log('❌ Condition not met - not showing chat tool');
      }
    },

    handleTenantChatMessage(data) {
      if (!data || data.senderType !== 'tenant') return;
      console.log('📱 Navbar got tenant chat message:', data);

      const transformed = {
        id: `tenant_${data.id}`,
        fromPeopleId: data.senderPeopleId || data._peopleId,
        toPeopleId: null,
        fromUserId: null,
        toUserId: null,
        toGroupId: null,
        message: data.message,
        datetime: data.createdAt,
        fullName: data.fullName || 'Unknown Tenant',
        _sourceClient: 'chatbot',
        _tenantConversationId: data._conversationId || data.conversationId,
      };

      if (!this.isTabActive) {
        // Store for when tab becomes active
        this.pendingTenantMessage = transformed;
        return;
      }

      this.handleNewChatMessages(transformed);
    },

    /**
     * Nav "Chat Bot" badge — same total as MHomePage (/api/chat/unseen-count):
     * unread staff direct + group + unread tenant_messages. System-channel
     * rows are excluded (they badge the System chat head only).
     */
    scheduleInsideChatDndRefresh() {
      clearTimeout(this.insideChatDndTimer);
      this.insideChatDndTimer = null;
      const until = this.insideChatDnd && this.insideChatDnd.until;
      if (!until) return;
      const ts = new Date(until).getTime();
      if (Number.isNaN(ts) || ts <= Date.now()) return;
      const delay = Math.min(ts - Date.now() + 500, 2147483647);
      this.insideChatDndTimer = setTimeout(() => {
        this.checkUnreadTenantMessages();
      }, delay);
    },

    async checkUnreadTenantMessages() {
      try {
        const resp = await axios.get('/api/chat/unseen-count', {
          params: {
            alwaysOn: unreadAlwaysOnParam(window.localStorage, this.myUserRole),
          },
        });
        const total = Number(resp.data?.count) || 0;
        this.unreadTenantChatCount = total;
        if (resp.data?.dnd) {
          this.insideChatDnd = applyStoredDndAlwaysOn(
            {
              until: resp.data.dnd.until || null,
              exceptEmergencies: resp.data.dnd.exceptEmergencies !== false,
              exceptStaff: resp.data.dnd.exceptStaff !== false,
              keywords: resp.data.dnd.keywords || '',
            },
            this.myUserRole,
            window.localStorage,
          );
        }
        this.scheduleInsideChatDndRefresh();
        if (total > 0) {
          console.log(
            `📱 Chat unseen total=${total} (direct=${resp.data?.directUnread}, group=${resp.data?.groupUnread}, system=${resp.data?.systemUnread}, tenant=${resp.data?.tenantUnread})`,
          );
        }
      } catch (e) {
        console.error('Error checking unread chat count:', e);
      }
    },

    debugSocketConnection() {
      console.log('=== SOCKET DEBUG INFO ===');
      console.log('Socket connected:', this.socket?.connected);
      console.log('Socket ID:', this.socket?.id);
      console.log('Socket URL:', this.socket?.io?.uri);
      console.log('Socket rooms:', this.socket?.rooms);
      console.log('My User ID:', this.myUserId);
      console.log('Auth Token exists:', !!this.getAuthToken());
      console.log('Tab is active:', this.isTabActive);
      console.log('Chat tool manually closed:', this.chatToolManuallyClosed);
      console.log('Show chat tool:', this.showChatTool);

      // Test manual authentication
      if (this.socket) {
        console.log('Sending test authentication...');
        this.socket.emit('authenticate', this.getAuthToken());

        // Test if we can receive any events
        console.log('Requesting server test...');
        this.socket.emit('test-connection', { userId: this.myUserId });
      } else {
        console.log('❌ No socket connection available');
      }
    },

    handleChatToolClick() {
      const wasVisible = this.showChatTool;
      if (!wasVisible) {
        this.handleCloseNavBarPanels();
        // Do not zero the badge on open — opening the panel does not mean
        // messages were read. Heads clear as each thread is viewed.
      }
      this.showChatTool = !wasVisible;

      // Track when the chat tool is manually closed
      if (wasVisible && !this.showChatTool) {
        console.log('🔴 Chat tool manually closed - setting flag');
        this.chatToolManuallyClosed = true;
        this.chatToolManualCloseTime = Date.now();
        // Refresh badge from server after close (reads may have cleared some)
        this.checkUnreadTenantMessages();

        console.log('🔴 Manual close time:', this.chatToolManualCloseTime);

        // Debug socket connection after manual close
        console.log('🔍 Socket status after manual close:', {
          connected: this.socket?.connected,
          socketId: this.socket?.id,
          isTabActive: this.isTabActive,
        });

        // Reset the flag after a short delay to allow auto-opening again
        setTimeout(() => {
          const resetTime = Date.now();
          console.log('🟢 Resetting manually closed flag after timeout');
          console.log(
            '🟢 Reset time:',
            resetTime,
            'Duration:',
            resetTime - this.chatToolManualCloseTime + 'ms',
          );
          this.chatToolManuallyClosed = false;
          this.chatToolManualCloseTime = null;
        }, 10000); // 10 seconds grace period
      } else if (!wasVisible && this.showChatTool) {
        console.log('🟢 Chat tool manually opened - clearing flag');
        this.chatToolManuallyClosed = false;
        this.chatToolManualCloseTime = null;
      }
    },

    handleNewVoiceClick() {
      this.hideMenu('tools');
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showVoiceMessages = true;
    },

    handleCloseVoiceMessages() {
      this.showVoiceMessages = false;
      this.fetchUnheardVoiceMemosCount();
    },

    handleNewNoteClick() {
      console.log(
        '[handleNewNoteClick] Closing NavBar panels before opening notes',
      );
      this.handleCloseNavBarPanels();
      this.showMobileNotes = !this.showMobileNotes;
      // Always close document viewer when this button is clicked
      eventBus.emit('closeDocumentViewer');
    },

    // Tab activity management methods
    handleVisibilityChange() {
      const wasActive = this.isTabActive;
      this.isTabActive = !document.hidden;

      console.log(
        `Tab visibility changed: ${this.isTabActive ? 'Active' : 'Inactive'}`,
      );

      if (this.isTabActive && !wasActive) {
        // Tab became active
        console.log(
          'Tab became active - binding socket listeners and refreshing chat data',
        );

        // Suppress false offline popups while background-suspended requests settle
        this.healthCheckResumeSuppressUntil = Date.now() + 5000;
        this.healthCheckFailureCount = 0;
        this.showOfflinePopup = false;
        this.performHealthCheck();
        this.startHealthCheckPolling();

        this.bindSocketEventListeners();
        this.refreshChatDataOnTabActivation();

        // Check for unread tenant messages via API (socket events may be delayed while inactive)
        this.checkUnreadTenantMessages();

        if (this.myUserRole <= 1) {
          this.checkForNewPollingLockErrors();
        }
        this.fetchUnseenEmailsCount();
        this.fetchUnreadReceipts();
        eventBus.emit('quickEmailMaybeRefresh');

        // Process pending tenant message
        if (this.pendingTenantMessage) {
          console.log(
            '📱 Processing pending tenant message after tab activation',
          );
          this.handleNewChatMessages(this.pendingTenantMessage);
          this.pendingTenantMessage = null;
        }
      } else if (!this.isTabActive && wasActive) {
        // Tab became inactive
        console.log('Tab became inactive - unbinding socket listeners');
        this.stopHealthCheckPolling();
        this.unbindSocketEventListeners();
      }
    },

    bindSocketEventListeners() {
      // Event listeners are now always bound in connectSocket()
      // This method is kept for future extensibility if needed
      console.log('Socket event listeners are permanently bound');
    },

    unbindSocketEventListeners() {
      // Event listeners remain bound but chat processing is controlled by tab activity
      // This method is kept for future extensibility if needed
      console.log(
        'Chat processing controlled by tab activity, not listener binding',
      );
    },

    refreshChatDataOnTabActivation() {
      // Don't reset manually closed state immediately - let the timeout handle it
      // This preserves the user's intent to keep the chat tool closed for the grace period
      console.log(
        '🔄 Tab activated - not resetting manually closed flag (respecting timeout)',
      );
      console.log(
        '🔄 Current chatToolManuallyClosed status:',
        this.chatToolManuallyClosed,
      );

      // Check if we should auto-open the chat tool based on current state
      if (
        !this.showChatTool &&
        !this.chatToolManuallyClosed &&
        this.chatMessage?.id
      ) {
        console.log(
          '🔄 Tab activated with unprocessed message - checking if should auto-open',
        );

        // Re-evaluate if the current chat message should trigger auto-open
        const condition1 = this.chatMessage?.fromUserId === 0;
        const condition2 = this.chatMessage?.toUserId === this.myUserId;
        const condition3 = this.chatMessage?.fromUserId === this.myUserId;
        const condition4 =
          this.chatMessage?.fromPeopleId && this.chatMessage?.toUserId === null;

        if (
          (condition1 || condition2 || condition3 || condition4) &&
          shouldShowInsideChatAlert(this.chatMessage, this.insideChatDnd)
        ) {
          console.log(
            '🔄 Conditions met for auto-open on tab activation - opening chat tool',
          );
          this.showChatTool = true;
        }
      }

      // Trigger refresh in InsideChatTool if it exists
      const chatToolRef = this.$refs.insideChatTool;
      if (chatToolRef && typeof chatToolRef.refreshMessages === 'function') {
        console.log('Triggering chat tool refresh on tab activation');
        chatToolRef.refreshMessages();
      }
    },

    // Make this component accessible from browser console for debugging
    setupGlobalDebugging() {
      window.navBarComponent = this;
      window.testNavBarSocket = () => {
        console.log('=== NAVBAR SOCKET TEST ===');
        console.log('Socket exists:', !!this.socket);
        console.log('Socket connected:', this.socket?.connected);
        console.log('Socket ID:', this.socket?.id);
        console.log('MyUserId:', this.myUserId);
        console.log('Tab is active:', this.isTabActive);
        console.log('Show chat tool:', this.showChatTool);
        console.log('Chat tool manually closed:', this.chatToolManuallyClosed);
        console.log(
          'Chat tool manual close time:',
          this.chatToolManualCloseTime,
        );
        console.log('Current timestamp:', Date.now());

        if (this.socket) {
          // Test emit
          console.log('Testing socket emit...');
          this.socket.emit('test-ping', {
            from: 'navbar',
            timestamp: Date.now(),
          });

          // Test if we can manually trigger handleNewChatMessages
          console.log('Testing manual message handling...');
          const testMessage = {
            id: 'test-' + Date.now(),
            message: 'Test message from console',
            fromPeopleId: 2,
            toUserId: null,
            datetime: new Date().toISOString(),
          };
          console.log(
            'Manually triggering handleNewChatMessages with test data...',
          );
          this.handleNewChatMessages(testMessage);

          // Test with different message types
          console.log('Testing with system message (fromUserId = 0)...');
          const systemMessage = {
            id: 'system-test-' + Date.now(),
            message: 'System test message',
            fromUserId: 0,
            toUserId: this.myUserId,
            fromPeopleId: null,
            toPeopleId: null,
            datetime: new Date().toISOString(),
          };
          this.handleNewChatMessages(systemMessage);
        }
      };
      console.log('Added global debugging: window.testNavBarSocket()');
    },

    shouldDisplayNavBar() {
      const hiddenNavRoutes = [
        '/',
        '/login',
        '/home',
        '/mdocs',
        '/mhome',
        '/nothing',
        '/mnotes',
        '/ichat',
        '/quicklook',
        '/mworkorders',
        '/msms',
        '/mvoice',
        '/lockcodes',
        '/quickemail',
        '/appliances',
        '/download',
        '/mproperties',
        '/calendar',
        '/collector',
        '/survey',
        '/workorder-schedule',
        '/rent-me',
      ]; // List of routes without nav bar
      let currentRoute = this.$route.path;

      const toShow = !hiddenNavRoutes.includes(currentRoute); // Check if current route is not in the hidden list
      return toShow;
    },

    openPayrollPopup() {
      eventBus.emit('closeDocumentViewer');
      this.showPayrollPopup = true;
    },

    toggleViewMode() {
      this.isGridView = !this.isGridView;
    },

    handleUserInteraction() {
      if (!this.soundEnabled) {
        this.soundEnabled = true;
        // console.log('sound enabled');
        // remove the event listener after enabling sound once.
        window.removeEventListener('mousemove', this.handleUserInteraction);
      }
    },

    handleWindowResize() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
    },

    checkScreenSizeWarning() {
      if (this.windowWidth < 2500) {
        const warningCount = parseInt(
          localStorage.getItem('screenSizeWarningCount') || '0',
          10,
        );
        if (warningCount < 2) {
          this.showScreenSizeWarning = true;
        }
      }
    },

    dismissScreenSizeWarning(acknowledged) {
      this.showScreenSizeWarning = false;
      if (acknowledged) {
        const warningCount = parseInt(
          localStorage.getItem('screenSizeWarningCount') || '0',
          10,
        );
        localStorage.setItem(
          'screenSizeWarningCount',
          String(warningCount + 1),
        );
      }
    },

    updateMousePosition(event) {
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
    },

    initializeEmailState() {
      this.newEmailsAvailable = false;
      this.lastEmailId = parseInt(getLastEmailId(this.myUserId), 10) || 0;
    },

    async fetchUnseenEmailsCount() {
      try {
        const lastEmailId = getLastEmailId(this.myUserId);
        const response = await axios.get('/api/emails/unseen-count', {
          params: { lastEmailId },
        });
        this.unseenEmailsCount = response.data.count || 0;
        this.newEmailsAvailable = this.unseenEmailsCount > 0;
      } catch (error) {
        console.error('Error fetching unseen emails count:', error);
        this.unseenEmailsCount = 0;
        this.newEmailsAvailable = false;
      }
    },

    applyRightColumnOpen(flag) {
      const changes = rightColumnOpenChanges(this.$route.path, flag);
      if (!changes) return false;
      Object.assign(this, changes);
      return true;
    },

    handleEmailNavClick() {
      if (!this.applyRightColumnOpen('showQuickEmail')) {
        this.showQuickEmail = true;
      }
      eventBus.emit('quickEmailMaybeRefresh');
    },

    // Call this method after acknowledging the latest email
    acknowledgeLatestEmail() {
      if (this.lastEmailId) {
        setLastEmailId(this.myUserId, this.lastEmailId);
        this.newEmailsAvailable = false;
        this.fetchUnseenEmailsCount();
      }
    },

    renderMarkdown(markdown) {
      return marked(markdown || '');
    },

    highlightMatch(noteContent) {
      if (!this.searchQuery || !noteContent) return noteContent;

      const query = this.searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${query})`, 'gi');
      return noteContent.replace(regex, '<mark>$1</mark>');
    },

    cycleContainerHeight(state) {
      let noteHeight;

      // Check if the state parameter is provided and valid
      if (typeof state === 'number' && state >= 1 && state <= 4) {
        this.currentHeightState = state;
      } else {
        // Cycle through states as before
        this.currentHeightState = (this.currentHeightState % 4) + 1;
      }

      switch (this.currentHeightState) {
        case 1:
          this.containerHeight = '100%';
          break;
        case 2:
          this.containerHeight = '50%';
          break;
        case 3:
          noteHeight = document.querySelector('.note-tile')?.offsetHeight || 0;
          this.containerHeight = `${noteHeight + 0}px`;
          break;
        case 4:
          this.containerHeight = '0px';
          break;
      }
    },

    openNotesPanel() {
      this.noteType = 'Bug';
      this.noteId = null;
      // Close Work Orders, Mobile Notes, and Appliances if they're open
      this.handleCloseNavBarPanels;
      eventBus.emit('closeDocumentViewer');
      this.showNotesPanel = true;
    },

    updateShowNotesPanel(newVal) {
      this.handleCloseNavBarPanels;
      this.showNotesPanel = newVal;
    },

    showMenu(menu) {
      // Clear any pending hide timeout for this menu
      if (this.menuTimeouts[menu]) {
        clearTimeout(this.menuTimeouts[menu]);
        this.menuTimeouts[menu] = null;
      }
      this.menus[menu] = true;
    },

    hideMenu(menu) {
      // Add delay for menus with cascading submenus to allow mouse to reach submenu
      const delay = menu === 'bank' ? 150 : 0;
      if (delay > 0) {
        this.menuTimeouts[menu] = setTimeout(() => {
          this.menus[menu] = false;
          this.menuTimeouts[menu] = null;
        }, delay);
      } else {
        this.menus[menu] = false;
      }
    },

    toggleUserMenu() {
      this.userMenuOpen = !this.userMenuOpen;
    },

    togglePageScroll() {
      const next = !isPageScrollEnabled();
      setPageScrollEnabled(next);
      this.pageScrollEnabled = next;
      this.userMenuOpen = false;

      const query = { ...this.$route.query };
      if (next) {
        query.scroll = '1';
      } else {
        delete query.scroll;
      }
      if (query.scroll !== this.$route.query.scroll) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    handleDocumentClick(event) {
      if (!this.userMenuOpen) {
        return;
      }

      const target = event.target;
      if (target && target.closest('.user-menu-wrapper')) {
        return;
      }

      this.userMenuOpen = false;
    },

    async logoutFromUserMenu() {
      this.userMenuOpen = false;

      try {
        await axios.post('/api/logout');
      } catch (error) {
        console.warn('Logout API call failed, proceeding with local logout');
      }

      if (this.socket) {
        this.socket.disconnect();
      }

      localStorage.removeItem('token');
      this.$router.push('/login');
    },

    showShortcuts() {
      eventBus.emit('show-shortcuts-app');
    },

    uploadBankFile() {
      eventBus.emit('upload-bankfile-app');
    },

    handleRunAutomatchOnlyRequest() {
      this.runAutomatchOnly();
    },

    async runAutomatchOnly(menuEvent) {
      try {
        const currentYear = new Date().getFullYear();
        const onOrAfterDate = `${currentYear}-01-01`;

        const originalText = menuEvent?.target?.innerText;
        if (menuEvent?.target) menuEvent.target.innerText = 'Running...';

        const response = await axios.get('/api/automatch', {
          params: { onOrAfterDate },
        });

        eventBus.emit('bankStatementUpdated');

        const matched = response.data?.matched || 0;
        const message = `Automatch completed. ${matched} transactions matched.`;
        eventBus.emit('mainPageFooterMessage', message);
        eventBus.emit('automatch-only-complete', {
          success: true,
          matched,
          message,
        });

        if (menuEvent?.target && originalText) {
          menuEvent.target.innerText = originalText;
        }
      } catch (error) {
        console.error('Error running automatch:', error);
        const message = `Error running automatch: ${error.message || 'Unknown error'}`;
        eventBus.emit('mainPageFooterMessage', message);
        eventBus.emit('automatch-only-complete', {
          success: false,
          matched: 0,
          message,
        });
      }
    },

    async handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.searchQuery = '';
        this.isGridView = false;
      }
      if (event.altKey && event.key === 'r') {
        this.showReminders = !this.showReminders;
      } else if (event.altKey && event.key === 'm') {
        this.showHelp = false;
        this.openQuickEmail();
      } else if (event.altKey && event.key === 'D') {
        this.$router.push('/documents');
        this.showHelp = false;
      } else if (event.altKey && event.key === 'p') {
        this.$router.push({
          path: '/properties',
          query: { tab: 'overview' },
        });
        this.showHelp = false;
      } else if (event.altKey && event.key === 'h') {
        this.$router.push('/home');
        this.showHelp = false;
      } else if (event.altKey && event.key === 'l') {
        this.$router.push('/lease-search');
        this.showHelp = false;
      } else if (event.altKey && event.key === 'k') {
        this.$router.push('/clip');
        this.showHelp = false;
      } else if (event.altKey && event.key === 'c') {
        // Open the calendar tool
        this.openCalendar();
        this.showHelp = false;
      } else if (event.altKey && event.key === 'y') {
        this.$router.push({ path: '/properties', query: { tab: 'payments' } });
        this.showHelp = false;
      } else if (event.altKey && event.key === 't') {
        this.$router.push('/people');
        this.showHelp = false;
      } else if (event.altKey && event.key === 'b') {
        this.$router.push('/main');
        this.showHelp = false;
      } else if (event.altKey && event.key === 'o') {
        this.$router.push('/tools');
        this.showHelp = false;
      } else if (event.altKey && event.key === 'q') {
        this.hideNotes = false;
        this.showHelp = false;
        this.addNewNoteAbove();
        this.centerNote = true;
      } else if (event.altKey && event.key === 'w') {
        this.isGridView = !this.isGridView;
      } else if (event.altKey && event.key === 'g') {
        this.$router.push('/reports');
        this.showHelp = false;
      } else if (event.altKey && (event.key === '?' || event.key === '/')) {
        this.showHelp = !this.showHelp;
      }
      // Additional key bindings as needed
    },

    handleReminderSelection(linkedDocId) {
      eventBus.emit('select-document', linkedDocId);
    },

    openBulkEmail() {
      eventBus.emit('closeDocumentViewer');
      this.showBulkEmail = true;
    },

    openMobileNotes() {
      window.open('/mnotes', '_blank');
    },

    openQuickEmail(to = '', subject = '', body = '') {
      // Trigger the showQuickEmail modal through eventBus with optional parameters
      eventBus.emit('openQuickEmail', { to, subject, body });
      if (!this.applyRightColumnOpen('showQuickEmail')) {
        this.showQuickEmail = true;
      }
    },

    teardownRemindersPanelDrag() {
      if (this.remindersPanelDragCleanup) {
        this.remindersPanelDragCleanup();
        this.remindersPanelDragCleanup = null;
      }
    },

    initRemindersPanelDrag() {
      this.teardownRemindersPanelDrag();
      const el = this.$refs.remindersPanel;
      if (!el) return;

      const hasSaved =
        localStorage.getItem('remindersPanelTop') &&
        localStorage.getItem('remindersPanelLeft');
      if (!hasSaved) {
        el.style.top = '125px';
        el.style.left = '97px';
      }

      this.remindersPanelDragCleanup = makeDraggable(el, {
        handleSelector: '.reminders-panel-drag-handle',
        storageKeyPrefix: 'remindersPanel',
      });
    },

    closeRemindersPanel() {
      this.teardownRemindersPanelDrag();
      this.showReminders = false;
      eventBus.emit('update:showReminderDialog', false);
    },

    toggleDragMode(event) {
      // Set drag mode to true and identify the targeted icon and popup container
      this.isDragMode = !this.isDragMode;

      if (this.isDragMode) {
        // Capture the icon clicked and determine its closest popup container
        const iconElement = event.target;
        const popupContainer = iconElement.closest('.draggable-popup'); // Generalize by class

        if (popupContainer) {
          this.currentPopup = popupContainer;

          // Get bounding rectangles to calculate the offset
          const iconRect = iconElement.getBoundingClientRect();
          const popupRect = popupContainer.getBoundingClientRect();

          // Calculate offset between the icon and the popup's top-left corner
          this.currentIconOffset.x = iconRect.left - popupRect.left;
          this.currentIconOffset.y = iconRect.top - popupRect.top;

          // Add a one-time event listener to capture the next click location
          document.addEventListener('click', this.setPopupPosition);
        }
      } else {
        // If drag mode is off, remove the listener
        document.removeEventListener('click', this.setPopupPosition);
      }
    },

    bringToFront(event) {
      // Find the parent element of the clicked title bar
      const targetElement = event.target.closest('.bring-to-frontable');
      console.log('bringToFront - targetElement:', targetElement);

      if (targetElement) {
        // Get all elements with .bring-to-frontable class and calculate the highest z-index
        const allElements = Array.from(
          document.querySelectorAll('.bring-to-frontable'),
        );
        const maxZIndex = allElements.reduce((max, el) => {
          const zIndex = parseInt(window.getComputedStyle(el).zIndex) || 0;
          return Math.max(max, zIndex);
        }, 0);

        // Set the z-index of the clicked modal to be one higher than the current highest
        targetElement.style.zIndex = maxZIndex + 1;
      }
    },

    setPopupPosition(event) {
      // Ensure there is a popup to position
      if (this.currentPopup) {
        // Move the popup to align the icon with the new click location
        this.popupPosition.x = event.clientX - this.currentIconOffset.x;
        this.popupPosition.y = event.clientY - this.currentIconOffset.y;

        // Apply inline styles dynamically to the popup's position
        this.currentPopup.style.left = `${this.popupPosition.x}px`;
        this.currentPopup.style.top = `${this.popupPosition.y}px`;

        // Reset drag mode and remove event listener
        this.isDragMode = false;
        document.removeEventListener('click', this.setPopupPosition);
      }
    },

    /* for simpleNotes */
    async fetchNotes() {
      //console.log('fetching notes: isEditing=', this.isEditing);
      if (this.isEditing) return;
      axios
        .get('/api/simple-notes')
        .then((response) => {
          console.log(
            '📝 Fetched notes:',
            response.data.length,
            'First note:',
            response.data[0],
          );
          this.notes = response.data.map((note) => ({
            ...note,
            highlightedNote: null,
            showIcons: false,
            isEditing: false,
          }));
          // Update badge count after loading notes
          this.fetchNewNotesTodayCount();
        })
        .catch(() => {
          //console.error('Error fetching notes:', error);
        });
    },

    async addNewNoteAbove(text = null) {
      console.log('add new note above');
      if (this.isAddingNote) return;

      this.isAddingNote = true;
      this.isEditing = true;

      try {
        const newNote = text ? { note: text } : { note: 'New Note....' };

        const response = await axios.post('/api/simple-notes', newNote);

        if (response && response.data) {
          const addedNote = {
            ...response.data,
            showIcons: false,
            isEditing: true,
          };

          // the socket now inserts this
          this.notes.unshift(addedNote);

          // Ensure the DOM updates before focusing
          this.$nextTick(() => {
            // Access the textarea by its id instead of ref
            const textareaId = this.createRef(addedNote.id);
            const textarea = document.getElementById(textareaId);

            if (textarea) {
              textarea.focus();
              this.$nextTick(() => {
                textarea.select();
                if (this.centerNote) {
                  this.centerNote = false;
                  // Apply CSS for centering
                  textarea.style.top = 'calc(50% - 75px)';
                  textarea.style.right = 'calc(50% - 268px)';
                } else {
                  // Apply alternative position CSS
                  textarea.style.top = 'calc(50% - 75px)';
                  textarea.style.right = 'calc(50% - 268px)';
                }
              });
            }
          });
        }
      } catch (error) {
        //console.error('Error adding new note:', error);
      } finally {
        this.isAddingNote = false;
      }
    },

    createRef(id) {
      return 'editingNoteArea' + id;
    },

    async deleteNote(id) {
      try {
        await axios.delete(`/api/simple-notes/${id}`);
        this.notes = this.notes.filter((note) => note.id !== id);
      } catch (error) {
        //console.error('Error deleting note:', error);
      }
    },

    closeGridView() {
      console.log('close grid View: 1165');
      this.isGridView = false;
    },

    editNote(note) {
      note.isEditing = true;
      this.isEditing = true;
      note.originalNote = note.note;

      // Focus the textarea once it's in editing mode
      this.$nextTick(() => {
        // Access the textarea by its id instead of ref
        const textareaId = this.createRef(note.id);
        const textarea = document.getElementById(textareaId);

        if (textarea && !note.isEditing) {
          textarea.focus();
          this.$nextTick(() => {
            // Set the selection range to the end of the current text
            const textLength = textarea.value.length;
            textarea.setSelectionRange(textLength, textLength);
          });
        }
      });
    },

    checkToAddNote() {
      // Check if the search query starts with "+ "
      if (this.searchQuery.startsWith('+ ')) {
        // Extract text after "+ "
        const text = this.searchQuery.slice(2).trim();

        // Call the method to add a new note above
        this.addNewNoteAbove(text);

        // Optionally, clear the search query after adding the note
        this.searchQuery = '';
      }
    },

    // just used for debugging
    donothing() {
      return;
    },

    /**
     * Persist note content to the server.
     * @param {{ close?: boolean }} options — when true (default), leave edit mode after save
     */
    async persistSimpleNote(note, { close = true } = {}) {
      if (note.note === 'New Note....') {
        if (close) {
          await this.deleteNote(note.id);
        }
        return;
      }

      try {
        // Process "smart note" features like "calculate"
        note.note = this.processCalculateFeature(note.note);

        // Process reminders
        this.processReminders(note);

        // Save the updated note to the server
        await axios.put(`/api/simple-notes/${note.id}`, {
          note: note.note,
          displayOrder: note.displayOrder,
        });

        note.modifiedOn = new Date().toISOString();
        note.originalNote = note.note;

        if (close) {
          note.isEditing = false;
          this.isEditing = false;
          note.isPinned = false;
          this.moveNote('up', note);
          this.fetchNotes();
        }
      } catch (error) {
        //console.error('Error saving note:', error);
      }
    },

    /** Save and close the Quick Note editor. */
    async saveEdit(note) {
      await this.persistSimpleNote(note, { close: true });
    },

    /** Save while keeping the Quick Note editor open. */
    async saveNoteKeepOpen(note) {
      await this.persistSimpleNote(note, { close: false });
    },

    /* reminders feature */
    processReminders(note) {
      const noteText = note.note;
      let startPhrase = 'remind me to';
      let targetUser = null;

      // Check for "remind [username] to" pattern first (but not "remind me to")
      const userReminderMatch = noteText.toLowerCase().match(/remind (\w+) to/);
      if (userReminderMatch && userReminderMatch[1] !== 'me') {
        targetUser = userReminderMatch[1];
        startPhrase = `remind ${targetUser} to`;
      }

      const startPhraseIndex = noteText
        .toLowerCase()
        .indexOf(startPhrase.toLowerCase());

      // console.log('Note Text:', noteText);
      // console.log('Start Phrase Index:', startPhraseIndex);
      // console.log('Target User:', targetUser);

      if (startPhraseIndex === -1) {
        return; // Not a reminder, exit early
      }

      const now = new Date(); // Add this for the snooze check

      let workingTask = noteText
        .substring(startPhraseIndex + startPhrase.length)
        .trim();
      let reminderDate = new Date();
      let reminderTime = new Date(reminderDate.setHours(12, 0, 0, 0)); // Default to 12:00 PM

      // console.log('Initial Working Task:', workingTask);
      // console.log('Default Reminder Date:', reminderDate);
      // console.log('Default Reminder Time:', reminderTime);

      // Step 1: Check for special words.
      const daysOfWeek = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ];

      if (workingTask.toLowerCase().includes('tomorrow')) {
        reminderDate.setDate(reminderDate.getDate() + 1);
        workingTask = workingTask.replace(/tomorrow/i, '').trim();
        // console.log('Updated for Tomorrow:', { workingTask, reminderDate });
      } else if (workingTask.toLowerCase().includes('next week')) {
        reminderDate.setDate(reminderDate.getDate() + 7);
        workingTask = workingTask.replace(/next week/i, '').trim();
        // console.log('Updated for Next Week:', { workingTask, reminderDate });
      } else {
        const dayMatch = workingTask.toLowerCase().match(/\bon (\w+day)\b/); // Match any specific day
        if (dayMatch) {
          const todayIndex = reminderDate.getDay(); // Get current day index (0 for Sunday, ..., 6 for Saturday)
          const targetDay = dayMatch[1];
          const targetIndex = daysOfWeek.indexOf(targetDay);

          // Calculate the number of days to add
          let daysToAdd;
          if (targetIndex > todayIndex) {
            daysToAdd = targetIndex - todayIndex;
          } else {
            daysToAdd = 7 - (todayIndex - targetIndex); // Wrap around to the next week
          }

          reminderDate.setDate(reminderDate.getDate() + daysToAdd);
          workingTask = workingTask
            .replace(new RegExp(`on ${targetDay}`, 'i'), '')
            .trim();
          // console.log(`Updated for ${targetDay.charAt(0).toUpperCase() + targetDay.slice(1)}:`, { workingTask, reminderDate });
        } else {
          // console.log('No specific date mentioned, defaulting to today.');
        }
      }

      // Step 2: Parse time
      const atIndex = workingTask.toLowerCase().lastIndexOf('at');
      // console.log('At Index:', atIndex);

      if (atIndex !== -1) {
        const timeParts = workingTask.split(/at/i);
        const lastPart = timeParts[timeParts.length - 1].trim();
        const timeMatch = lastPart.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i);
        // console.log('Time Match:', timeMatch);

        if (timeMatch) {
          let [, hours, minutes = '00', period = 'AM'] = timeMatch;
          hours = parseInt(hours, 10);
          minutes = parseInt(minutes, 10);
          if (period.toLowerCase() === 'pm' && hours !== 12) hours += 12;
          if (period.toLowerCase() === 'am' && hours === 12) hours = 0;
          reminderTime.setHours(hours, minutes, 0, 0);
          workingTask = timeParts.slice(0, -1).join('at').trim();

          // console.log('Parsed Time:', { hours, minutes, period });
          // console.log('Updated Working Task after parsing time:', workingTask);
        }
      }

      // Step 3: Parse date
      // console.log('workingTask: 1034', workingTask);
      // Look for "on" followed by a date pattern (e.g., "on 10/17/25")
      const datePattern = /\bon\s+(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)/i;
      const datePatternMatch = workingTask.match(datePattern);
      // console.log('Date Pattern Match:', datePatternMatch);

      if (datePatternMatch) {
        const dateString = datePatternMatch[1];
        const dateMatch = dateString.match(
          /(\d{1,2})\/(\d{1,2})(\/(\d{2,4}))?/,
        );
        // console.log('Date Match:', dateMatch);

        if (dateMatch) {
          let [, month, day, , year] = dateMatch.map(
            (part) => parseInt(part, 10) || part,
          );
          if (!year) year = reminderDate.getFullYear();
          if (year.toString().length === 2) year += 2000; // Assuming 2000-2099
          reminderDate.setFullYear(year, month - 1, day); // Months are 0-indexed
          workingTask = workingTask.replace(datePattern, '').trim();

          // console.log('Parsed Date:', { month, day, year });
          // console.log('Updated Working Task after parsing date:', workingTask);
        }
      }

      const task = workingTask.charAt(0).toUpperCase() + workingTask.slice(1);

      const reminderDateTime = new Date(
        reminderDate.setHours(
          reminderTime.getHours(),
          reminderTime.getMinutes(),
          reminderTime.getSeconds(),
          reminderTime.getMilliseconds(),
        ),
      );

      // console.log('Final Task:', task);
      // console.log('Final Reminder DateTime:', reminderDateTime);

      // If targetUser is specified, send reminder to that user via socket
      if (targetUser) {
        this.sendReminderToUser(targetUser, {
          task,
          dateTime: reminderDateTime.toISOString(),
          noteId: note.id,
          fromUser: this.myUserName || 'Unknown User',
        });
        return; // Exit early since we're sending to another user
      }

      const existingReminderIndex = this.quickReminders.findIndex(
        (reminder) => reminder.noteId === note.id,
      );

      if (existingReminderIndex !== -1) {
        // Check if the existing reminder has been snoozed (future date)
        const existingDateTime = new Date(
          this.quickReminders[existingReminderIndex].dateTime,
        );
        if (existingDateTime > now) {
          // Don't overwrite snoozed reminders, just update the task text
          this.quickReminders[existingReminderIndex].task = task;
          localStorage.setItem(
            'quickReminders',
            JSON.stringify(this.quickReminders),
          );
          return; // Exit early to preserve the snoozed time
        }

        // Update existing reminder that hasn't been snoozed
        this.quickReminders[existingReminderIndex] = {
          task,
          dateTime: reminderDateTime.toISOString(),
          noteId: note.id,
        };
        // console.log( 'Updated existing reminder:', this.quickReminders[existingReminderIndex],);
      } else {
        this.quickReminders.push({
          task,
          dateTime: reminderDateTime.toISOString(),
          noteId: note.id,
        });
        // console.log( 'Added new reminder:', this.quickReminders[this.quickReminders.length - 1],);
      }

      localStorage.setItem(
        'quickReminders',
        JSON.stringify(this.quickReminders),
      );
    },

    async sendReminderToUser(targetUsername, reminderData) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          '/api/send-reminder-to-user',
          {
            targetUsername,
            reminderData,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.success) {
          console.log(`Reminder sent to ${targetUsername}:`, reminderData.task);
          // Optionally show a success message to the sender
          this.$toast?.success(`Reminder sent to ${targetUsername}!`);
        } else {
          console.error(
            `Failed to send reminder to ${targetUsername}:`,
            response.data.message,
          );
          this.$toast?.error(
            response.data.message ||
              `Failed to send reminder to ${targetUsername}`,
          );
        }
      } catch (error) {
        console.error('Error sending reminder to user:', error);
        this.$toast?.error(`Error sending reminder to ${targetUsername}`);
      }
    },

    loadReminders() {
      const storedReminders =
        JSON.parse(localStorage.getItem('quickReminders')) || [];
      this.quickReminders = storedReminders;
    },

    async syncRemindersFromDatabase() {
      try {
        // Get existing quickReminders from localStorage
        const existingReminders =
          JSON.parse(localStorage.getItem('quickReminders')) || [];

        // Query for unprocessed reminder notes (last 30 days)
        const response = await axios.get(
          '/api/simple-notes/unprocessed-reminders',
          {
            params: { days: 30 },
          },
        );

        const unprocessedNotes = response.data || [];
        let newRemindersAdded = false;

        // Check each unprocessed note
        for (const note of unprocessedNotes) {
          // Skip if already in quickReminders
          const alreadyExists = existingReminders.some(
            (r) => r.noteId === note.id,
          );

          if (!alreadyExists) {
            // Extract reminder info from note text
            const reminderMatch = note.note.match(
              /remind\s+(me|\w+)\s+to\s+(.+?)(?:\s+(?:on|at)\s+(.+))?$/i,
            );

            if (reminderMatch) {
              const targetUser = reminderMatch[1].toLowerCase();
              const task = reminderMatch[2].trim();
              const dateTimeStr = reminderMatch[3];

              // Check if this reminder should be added for the current user
              let shouldAddReminder = false;

              if (targetUser === 'me') {
                // For "remind me to..." - only add if current user created the note
                shouldAddReminder = note.modifiedBy === this.myUserId;
              } else {
                // For "remind [username] to..." - only add if username matches current user's username
                shouldAddReminder =
                  targetUser === this.myUserName.toLowerCase();
              }

              if (!shouldAddReminder) {
                console.log(
                  `Skipping reminder (not for current user): ${note.note.substring(
                    0,
                    50,
                  )}...`,
                );
                continue; // Skip this reminder
              }

              // Create a default reminder time (e.g., tomorrow at 9 AM if no time specified)
              let reminderDateTime = new Date();
              if (dateTimeStr) {
                // Try to parse the date/time from the note
                const parsedDate = new Date(dateTimeStr);
                if (!isNaN(parsedDate.getTime())) {
                  reminderDateTime = parsedDate;
                } else {
                  reminderDateTime.setDate(reminderDateTime.getDate() + 1);
                  reminderDateTime.setHours(9, 0, 0, 0);
                }
              } else {
                reminderDateTime.setDate(reminderDateTime.getDate() + 1);
                reminderDateTime.setHours(9, 0, 0, 0);
              }

              // Add to existingReminders array
              existingReminders.push({
                noteId: note.id,
                task: task,
                dateTime: reminderDateTime.toISOString(),
              });

              newRemindersAdded = true;
              console.log(
                `Synced reminder from database: ${task} (noteId: ${note.id})`,
              );
            }
          }
        }

        // Save back to localStorage if new reminders were added
        if (newRemindersAdded) {
          localStorage.setItem(
            'quickReminders',
            JSON.stringify(existingReminders),
          );
          this.quickReminders = existingReminders;
          console.log(
            `Added ${newRemindersAdded} new reminders from database sync`,
          );
        }
      } catch (error) {
        console.error('Error syncing reminders from database:', error);
      }
    },

    checkReminders() {
      const now = new Date();

      // Reload from localStorage before deciding whether to popup
      this.loadReminders();

      // Handle any reminders that are due
      this.quickReminders.forEach((reminder) => {
        const reminderDateTime = new Date(reminder.dateTime);
        console.log(
          `Checking reminder: ${
            reminder.task
          }, Time: ${reminderDateTime.toISOString()}, Now: ${now.toISOString()}, Due: ${
            reminderDateTime <= now
          }`,
        );
        if (reminderDateTime <= now) {
          this.showReminderPopup(reminder);
        }
      });

      // Examine alertingTasks for stale reminders
      // A task is valid only if it exists in quickReminders AND is currently due
      this.alertingTasks = this.alertingTasks.filter((task) => {
        const matchingReminder = this.quickReminders.find(
          (reminder) => reminder.noteId === task.noteId,
        );

        // If reminder doesn't exist in quickReminders, it was dismissed
        if (!matchingReminder) {
          return false;
        }

        // If reminder exists but is not yet due, it was snoozed
        const reminderDateTime = new Date(matchingReminder.dateTime);
        const isDue = reminderDateTime <= now;

        return isDue;
      });

      // Close the quickReminderPopup if there are no valid alerting tasks
      if (this.alertingTasks.length === 0) {
        this.showQuickReminderPopup = false;
      }
    },

    startReminderPopupPolling() {
      // Clear any existing interval
      if (this.reminderPopupPollInterval) {
        clearInterval(this.reminderPopupPollInterval);
      }

      // Poll every 15 seconds to check if popup should remain visible
      this.reminderPopupPollInterval = setInterval(() => {
        if (this.showQuickReminderPopup) {
          console.log('Polling reminder status while popup is visible...');
          this.checkReminders();
        }
      }, 15000); // 15 seconds
    },

    stopReminderPopupPolling() {
      if (this.reminderPopupPollInterval) {
        clearInterval(this.reminderPopupPollInterval);
        this.reminderPopupPollInterval = null;
      }
    },

    showReminderPopup(reminder) {
      if (this.showQuickReminderPopup) {
        //console.log('already open');
      } else {
        // not already open: play sound
        this.$nextTick(() => {
          if (this.$refs.reminderSound && this.soundEnabled) {
            this.$refs.reminderSound.play();
          }
        });
      }
      const existingTask = this.alertingTasks.find(
        (task) => task.noteId === reminder.noteId,
      );
      if (!existingTask) {
        //console.log('pushing to alertingTasks');
        this.alertingTasks.push(reminder);
      }

      this.showQuickReminderPopup = true;
    },

    dismissReminder(reminder) {
      // Check if reminder is null or an empty object
      const isEmptyObject = (obj) =>
        Object.keys(obj).length === 0 && obj.constructor === Object;

      let targetReminder = reminder;

      if (
        (!targetReminder || isEmptyObject(targetReminder)) &&
        this.alertingTasks.length === 1
      ) {
        // Assume the single alertingTask is the one to dismiss
        targetReminder = this.alertingTasks[0];
      }

      if (targetReminder && targetReminder.noteId) {
        // Find the index of the current reminder before removing it
        const currentIndex = this.alertingTasks.findIndex(
          (task) => task.noteId === targetReminder.noteId,
        );

        // Mark the reminder as dismissed in the database
        axios
          .put(`/api/simple-notes/${targetReminder.noteId}/dismiss-reminder`)
          .then(() => {
            console.log(
              `Reminder ${targetReminder.noteId} marked as dismissed in database`,
            );
          })
          .catch((error) => {
            console.error('Error dismissing reminder in database:', error);
          });

        // Remove the reminder from quickReminders
        this.quickReminders = this.quickReminders.filter(
          (task) => task.noteId !== targetReminder.noteId,
        );
        localStorage.setItem(
          'quickReminders',
          JSON.stringify(this.quickReminders),
        );

        // Also delete the note associated with the reminder
        this.deleteNote(targetReminder.noteId);

        // Remove the dismissed reminder from alerting tasks
        this.alertingTasks = this.alertingTasks.filter(
          (task) => task.noteId !== targetReminder.noteId,
        );

        // Auto-select the next reminder if there are more
        if (this.alertingTasks.length > 0) {
          // If we removed an item, select the one at the same index (or the last one if we were at the end)
          const nextIndex = Math.min(
            currentIndex,
            this.alertingTasks.length - 1,
          );
          this.selectedReminder = this.alertingTasks[nextIndex];
        } else {
          // Close the popup if there are no more alerting tasks
          this.showQuickReminderPopup = false;
        }
      }

      // Optionally re-enable sound, depending on your application logic
      this.soundEnabled = true;
    },

    snoozeReminder(selectedReminder, amount, unit) {
      const isEmptyObject = (obj) =>
        Object.keys(obj).length === 0 && obj.constructor === Object;

      let reminder = selectedReminder;

      if (
        (!reminder || isEmptyObject(reminder)) &&
        this.alertingTasks.length === 1
      ) {
        // Assume the single alertingTask is the one to snooze
        reminder = this.alertingTasks[0];
      }

      if (reminder && reminder.noteId) {
        console.log('Snoozing reminder with noteId:', reminder.noteId);
        console.log(
          'Available reminders:',
          this.quickReminders.map((r) => ({
            noteId: r.noteId,
            task: r.task.substring(0, 50) + '...',
          })),
        );

        // Find the index of the current reminder before removing it
        const currentIndex = this.alertingTasks.findIndex(
          (task) => task.noteId === reminder.noteId,
        );

        const targetReminder = this.quickReminders.find(
          (task) => task.noteId === reminder.noteId,
        );

        console.log('Found target reminder:', targetReminder ? 'YES' : 'NO');
        if (targetReminder) {
          console.log('Target reminder before snooze:', {
            noteId: targetReminder.noteId,
            dateTime: targetReminder.dateTime,
            task: targetReminder.task.substring(0, 50) + '...',
          });

          // Calculate snooze time from NOW, not from the original reminder time
          const now = new Date();
          const snoozeTime = new Date(now.getTime());

          switch (unit) {
            case 'hour':
              snoozeTime.setHours(snoozeTime.getHours() + amount);
              break;
            case 'day':
              snoozeTime.setDate(snoozeTime.getDate() + amount);
              break;
            case 'minute':
              snoozeTime.setMinutes(snoozeTime.getMinutes() + amount);
              break;
            default:
              console.warn('Unsupported time unit for snooze:', unit);
              return;
          }

          // Store as ISO string to ensure proper serialization
          targetReminder.dateTime = snoozeTime.toISOString();

          console.log(
            `Reminder snoozed: ${amount} ${unit}(s). New time: ${snoozeTime.toISOString()}`,
          );
          localStorage.setItem(
            'quickReminders',
            JSON.stringify(this.quickReminders),
          );

          // Remove the snoozed reminder from alerting tasks
          this.alertingTasks = this.alertingTasks.filter(
            (task) => task.noteId !== reminder.noteId,
          );

          // Auto-select the next reminder if there are more
          if (this.alertingTasks.length > 0) {
            // If we removed an item, select the one at the same index (or the last one if we were at the end)
            const nextIndex = Math.min(
              currentIndex,
              this.alertingTasks.length - 1,
            );
            this.selectedReminder = this.alertingTasks[nextIndex];
          } else {
            // Close the popup if there are no more alerting tasks
            this.showQuickReminderPopup = false;
          }
        }
      }
    },

    snoozeAllReminders() {
      // Snooze all alerting tasks for 1 hour
      const tasksToSnooze = [...this.alertingTasks]; // Create a copy to iterate over

      tasksToSnooze.forEach((task) => {
        this.snoozeReminder(task, 60, 'minute');
      });

      // Close the popup after snoozing all
      this.showQuickReminderPopup = false;
    },

    selectTask(task) {
      this.selectedReminder = task;
    },

    evaluateExpression(noteContent) {
      // Check if the note contains 'calc '
      if (noteContent.includes('calc ')) {
        const startIndex = noteContent.indexOf('calc ') + 'calc '.length;

        // Find the end index safely
        let endIndex = noteContent.indexOf('\n', startIndex);
        if (endIndex === -1) {
          endIndex = noteContent.length; // Handle case where expression is at end of string
        }

        const expression = noteContent.substring(startIndex, endIndex).trim();

        // Remove any existing "Result: ..." line following the expression
        const restOfNote = noteContent
          .substring(endIndex)
          .replace(/^\s*Result:.*\n?/, '');

        try {
          // Use a regular expression to check if the expression is valid
          if (/^[0-9+\-*/().\s]+$/.test(expression)) {
            // Evaluate the expression safely
            const result = new Function(`return ${expression}`)();

            // Check if the result is a number
            if (!isNaN(result)) {
              // Construct the new note content by appending the result below 'calc '
              return (
                noteContent.substring(0, startIndex) +
                `${expression}\nResult: ${result}` +
                restOfNote
              );
            }
          }
        } catch (error) {
          console.error(
            `Error evaluating expression: "${expression}", Error:`,
            error,
          );
        }
      }

      // Return the original note content if no valid calculation is made
      return noteContent;
    },

    processCalculateFeature(noteContent) {
      console.log('recalculating');
      const lines = noteContent.split('\n');
      const resultLines = [];
      let i = 0;

      while (i < lines.length) {
        const line = lines[i];

        if (line.trim().toLowerCase() === 'calculate:') {
          // Start a new calculate block
          resultLines.push(line);
          i++;

          const keyValueLines = [];
          let blockTotal = 0;
          let maxKeyLength = 5; // Minimum padding for alignment

          // Collect key-value pairs
          while (i < lines.length) {
            const currentLine = lines[i];
            const colonIndex = currentLine.indexOf(':');

            if (colonIndex > 0) {
              const key = currentLine.substring(0, colonIndex).trim();
              const value = currentLine.substring(colonIndex + 1).trim();
              let normalizedValue = value.replace(/,/g, '');

              // Handle values ending in 'K' (e.g., 1K = 1000)
              if (normalizedValue.toUpperCase().endsWith('K')) {
                normalizedValue =
                  parseFloat(normalizedValue.slice(0, -1)) * 1000;
              }

              const numericValue = parseFloat(normalizedValue);

              if (!isNaN(numericValue)) {
                blockTotal += numericValue;
                maxKeyLength = Math.max(maxKeyLength, key.length);
                keyValueLines.push({ key, value: numericValue });
                i++;
                continue;
              }
            }
            // Stop at the first non-key-value line
            break;
          }

          // Format and add key-value lines
          if (keyValueLines.length > 0) {
            const maxValueLength = Math.max(
              ...keyValueLines.map((kv) => kv.value.toLocaleString().length),
              blockTotal.toLocaleString().length,
            );

            keyValueLines.forEach(({ key, value }) => {
              const paddedKey = key.padEnd(maxKeyLength);
              const paddedValue = value
                .toLocaleString()
                .padStart(maxValueLength);
              resultLines.push(`${paddedKey} : ${paddedValue}`);
            });

            // Add separator and total (exactly once per block)
            resultLines.push('======');
            const totalLabel = 'Total';
            const paddedArrow =
              ' '.repeat(maxKeyLength - totalLabel.length) + '->';
            const formattedTotal = blockTotal
              .toLocaleString()
              .padStart(maxValueLength);
            resultLines.push(`${totalLabel}${paddedArrow} ${formattedTotal}`);
          }

          // Skip any existing separators or total lines in the input
          while (i < lines.length) {
            const currentLine = lines[i].trim();
            if (
              currentLine === '======' ||
              (currentLine.includes('Total') && currentLine.includes('->'))
            ) {
              i++;
            } else {
              break;
            }
          }
        } else {
          // Copy non-calculate lines as-is
          resultLines.push(line);
          i++;
        }
      }

      return resultLines.join('\n');
    },

    cancelEdit(note) {
      console.log('cancel edit');
      note.note = note.originalNote;
      note.isEditing = false;
    },

    onMouseEnter(note) {
      this.activeNote = note;
      note.showIcons = true;
    },

    onMouseLeave(note) {
      note.showIcons = false;
      if (this.activeNote === note) {
        this.activeNote = null;
      }
    },

    async moveNote(dir, note) {
      if (dir === 'up') {
        // Find the maximum displayOrder value in the notes array
        const maxDisplayOrder = Math.max(
          ...this.notes.map((n) => n.displayOrder),
        );
        // Set the displayOrder of the current note to be one more than the maximum
        note.displayOrder = maxDisplayOrder + 1;
      } else {
        // Decrease displayOrder only if it is greater than zero
        note.displayOrder > 0 ? note.displayOrder-- : null;
      }

      try {
        //console.log('moving note');
        await axios.put(`/api/simple-notes/${note.id}`, {
          note: note.note,
          displayOrder: note.displayOrder,
        });
        this.fetchNotes(); // Fetch updated notes to reflect new order
      } catch (error) {
        console.error('Error updating note order:', error);
      }
    },

    mouseOverNotesTitle(val) {
      this.currentHeightState;
      this.mouseOverSimpleNotesContainer = val;
      if (val) {
        this.cycleContainerHeight(1);
      }
    },

    handleMouseLeave(event) {
      const deltaX = Math.abs(event.clientX - this.lastMouseX);
      const deltaY = Math.abs(event.clientY - this.lastMouseY);

      if (deltaX > deltaY) {
        // Horizontal movement
        this.cycleContainerHeight(3);
      } else {
        // Vertical movement
        this.cycleContainerHeight(1);
      }

      // Reset state after handling
      this.mouseOverNotesTitle(false);
    },

    wrapTextAtWhitespace(text, maxLineLength = 25) {
      const words = text.split(' ');
      let currentLineLength = 0;
      let wrappedText = '';

      for (let i = 0; i < words.length; i++) {
        const word = words[i];

        // Check if adding the new word exceeds the maxLineLength
        if (currentLineLength + word.length > maxLineLength) {
          // If not the first word of the line, add a newline character and reset line length
          wrappedText += '\n';
          currentLineLength = 0;
        } else if (currentLineLength > 0) {
          // Add a space if it's not the start of a new line
          wrappedText += ' ';
          currentLineLength += 1;
        }

        // Add the word to the wrapped text
        wrappedText += word;
        currentLineLength += word.length;
      }

      return wrappedText;
    },

    openRemindersList() {
      this.showQuickRemindersList = true;
    },

    closeRemindersList() {
      this.showQuickRemindersList = false;
    },

    deleteReminderItem(reminder) {
      // Find the index of the reminder passed in within quickReminders
      const index = this.quickReminders.findIndex(
        (item) => item.noteId === reminder.noteId,
      );
      //console.log(reminder);
      //console.log(this.quickReminders);
      //console.log(index);

      // Check if the reminder was found
      if (index !== -1) {
        // Remove the reminder from the array
        this.quickReminders.splice(index, 1);

        // Update localStorage with the new quickReminders list
        localStorage.setItem(
          'quickReminders',
          JSON.stringify(this.quickReminders),
        );
      }
    },

    formatDateTime(dateTime) {
      const options = {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      };

      // Create a formatter
      const dateTimeFormatter = new Intl.DateTimeFormat('en-US', options);

      // Get the formatted parts
      const parts = dateTimeFormatter.formatToParts(new Date(dateTime));

      // Construct the desired format without commas
      let formattedDateTime = '';
      let firstLiteralEncountered = false;

      parts.forEach((part) => {
        if (part.type !== 'literal') {
          formattedDateTime += part.value;
        } else if (!firstLiteralEncountered && part.value === ', ') {
          // Replace the first comma with a space
          formattedDateTime += ' ';
          firstLiteralEncountered = true;
        } else {
          formattedDateTime += part.value;
        }
      });

      return formattedDateTime;
    },

    togglePin(note) {
      console.log('note for pin', note);
      note.isPinned = !note.isPinned;
      console.log('pin is toggled', note.isPinned);
    },

    handleBlur(note) {
      this.$nextTick(() => {
        if (!note.isPinned) {
          this.saveEdit(note);
        }
      });
    },

    formatWho(who) {
      function getInitials(name) {
        if (!name) return '';

        // Split the name into parts by spaces
        const nameParts = name.split(' ');

        // Map through the name parts, taking the first letter of each and converting to uppercase
        const initials = nameParts
          .map((part) => part[0].toUpperCase())
          .join('');

        return initials;
      }
      if (!who) {
        return '?';
      } else {
        return getInitials(who);
      }
    },

    formatDate(dateString, ago = false) {
      const date = new Date(dateString);

      // Calculate the difference in milliseconds
      const now = new Date();
      const diffInMs = now - date;

      // Convert the difference to hours
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInHours / 24);

      // Format the initial date string
      let formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      // Append the "ago" text if the parameter is true
      if (ago) {
        if (diffInHours < 24) {
          formattedDate += ` (${diffInHours} hr${
            diffInHours !== 1 ? 's' : ''
          } ago)`;
        } else {
          formattedDate += ` (${diffInDays} day${
            diffInDays !== 1 ? 's' : ''
          } ago)`;
        }
      }

      return formattedDate;
    },

    selectSingleNote(note) {
      this.isGridView = false;
      this.searchQuery = '';

      // Find the note in notes[] whose id matches the parameter note.id
      const foundNote = this.notes.find((n) => n.id === note.id);

      // Check if the note is found before attempting to edit
      if (foundNote) {
        this.editNote(foundNote);
      }
    },

    closeSimpleNote(note) {
      // Call saveEdit to process reminders and save the note
      this.saveEdit(note);
    },

    openCashPosition() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showCashPosition = true;
    },

    openLeaseYearEnd() {
      window.open('/reports?q=Property%20Year-End%20Balance', '_blank');
    },

    openSecurityDepositsYearEnd() {
      window.open('/reports?q=Security%20Deposits%20Year%20End', '_blank');
    },

    openTerminatedLeases() {
      window.open(
        '/reports?q=non-zero%20terminated%20leases%20prior%20year',
        '_blank',
      );
    },

    openUtilityReport(utilityType) {
      window.open(`/reports?q=${encodeURIComponent(utilityType)}`, '_blank');
    },

    openLockCodes() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showLockCodes = true;
    },

    openAppliances() {
      eventBus.emit('closeDocumentViewer');
      // Close Mobile Notes and Work Orders when opening Appliances
      this.handleCloseNavBarPanels();
      this.showAppliances = true;
    },

    openPropertySummary() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.propertySummaryStart = 'menu';
      this.showPropertySummary = true;
    },

    openServiceContacts() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.propertySummaryStart = 'service-contacts';
      this.showPropertySummary = true;
    },

    openShopInventory() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.propertySummaryStart = 'shop-inventory';
      this.showPropertySummary = true;
    },

    openLateFees() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showLateFees = true;
    },

    openAceStatementImport() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showAceStatementImport = true;
    },

    openBrysonLawncareImport() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showBrysonLawncareImport = true;
    },

    navigateToDocuments() {
      // Navigate to Documents page without triggering download
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.$router.push('/documents');
    },

    navigateToPayments() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.$router.push('/properties?tab=payments');
    },

    handlePaymentBadgeClick() {
      // Navigate to payments tab first, then trigger download after component mounts
      this.$router.push('/properties?tab=payments').then(() => {
        // Wait for component to fully mount and register event listeners
        setTimeout(() => {
          eventBus.emit('triggerPaymentDownload');
        }, 100);
      });
    },

    openDocuments() {
      // Navigate to Documents page AND trigger download if there are waiting documents
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      const url =
        this.waitingDocumentsCount > 0
          ? '/documents?importdocs=true'
          : '/documents';
      this.$router.push(url);
    },

    openWorkOrders() {
      // On /properties this takes the Quick Email column and leaves Chat Bot open.
      eventBus.emit('closeDocumentViewer');
      if (this.applyRightColumnOpen('showWorkOrders')) return;
      this.handleCloseNavBarPanels();
      this.showWorkOrders = true;
    },

    openQuarters() {
      // Close CollectorSetup if it's open
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showQuarters = true;
    },

    openCollectorSetup() {
      // Close Quarters if it's open
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showCollectorSetup = true;
    },

    openSurveySetup() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showSurveySetup = true;
    },

    openRentMeSetup() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showRentMeSetup = true;
    },

    getRentMeLastSeenAgreementId() {
      const stored = localStorage.getItem('rentMeLastSeenAgreementId');
      const parsed = Number.parseInt(stored, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    },

    setRentMeLastSeenAgreementId(agreementId) {
      const parsed = Number.parseInt(agreementId, 10);
      if (!Number.isFinite(parsed) || parsed <= 0) return;
      const current = this.getRentMeLastSeenAgreementId();
      if (parsed > current) {
        localStorage.setItem('rentMeLastSeenAgreementId', String(parsed));
      }
    },

    async fetchNewRentMeAgreementsCount() {
      if (this.myUserRole == null || this.myUserRole > 2) return;

      try {
        const sinceId = this.getRentMeLastSeenAgreementId();
        const { data } = await axios.get('/api/rent-me/agreements/unseen', {
          params: { sinceId },
        });
        this.newRentMeAgreementsCount = data.count || 0;
        this.rentMeLatestUnseenItemId = data.latestItemId || null;
        this.rentMeLatestUnseenAgreementId = data.latestAgreementId || null;
      } catch (error) {
        console.error('Error fetching new Rent Me agreements:', error);
      }
    },

    markRentMeAgreementsSeen() {
      if (this.rentMeLatestUnseenAgreementId) {
        this.setRentMeLastSeenAgreementId(this.rentMeLatestUnseenAgreementId);
      }
      this.newRentMeAgreementsCount = 0;
      this.rentMeLatestUnseenItemId = null;
      this.rentMeLatestUnseenAgreementId = null;
    },

    async openRentMeFromNavButton() {
      const itemId =
        this.rentMeLatestUnseenItemId || this.rentMeAlertPendingItemId;
      this.markRentMeAgreementsSeen();
      this.showRentMeAlert = false;
      this.rentMeAlertPendingItemId = null;
      this.ignorePrompt = null;

      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showRentMeSetup = true;

      if (itemId != null) {
        await this.$nextTick();
        eventBus.emit('rentMeSelectItem', { itemId });
      }
    },

    handleRentMeAgreementCreated(data) {
      if (this.myUserRole == null || this.myUserRole > 2) return;

      const itemId = Number.parseInt(data?.itemId, 10);
      if (!Number.isFinite(itemId) || itemId <= 0) return;

      if (data.agreementId) {
        const sinceId = this.getRentMeLastSeenAgreementId();
        if (data.agreementId > sinceId) {
          this.fetchNewRentMeAgreementsCount();
        }
      } else {
        this.fetchNewRentMeAgreementsCount();
      }

      if (this.showRentMeSetup) {
        eventBus.emit('rentMeSelectItem', { itemId });
        return;
      }

      this.rentMeAlertPendingItemId = itemId;
      if (shouldShowNavAlert(NAV_ALERT_IDS.rentMe)) {
        this.showRentMeAlert = true;
      }
    },

    promptIgnoreAlert(alertId) {
      this.ignorePrompt = alertId;
    },

    confirmIgnoreAlert(alertId, always) {
      if (always) setAlertAlwaysIgnored(alertId);
      this.ignorePrompt = null;
      if (alertId === NAV_ALERT_IDS.telemetry) {
        this.ignoreChatbotTelemetryAlert();
      } else if (alertId === NAV_ALERT_IDS.pollingLocks) {
        this.ignorePollingLocksAlert();
      } else if (alertId === NAV_ALERT_IDS.rentMe) {
        this.ignoreRentMeAlert();
      }
    },

    ignoreRentMeAlert() {
      this.showRentMeAlert = false;
      this.rentMeAlertPendingItemId = null;
      this.ignorePrompt = null;
    },

    async openRentMeFromAlert() {
      await this.openRentMeFromNavButton();
    },

    openChatBotMotd() {
      this.hideMenu('tools');
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showChatBotMotd = true;
    },

    openVoicemailCustomize() {
      this.hideMenu('tools');
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showVoicemailCustomize = true;
    },

    async openDndStatus() {
      this.hideMenu('tools');
      this.showDndStatus = true;
      this.dndStatusLoading = true;
      this.dndStatusError = '';
      try {
        const device = currentDndDevice(window.localStorage, navigator.userAgent);
        const alwaysOn = readDndAlwaysOn(window.localStorage, this.myUserRole);
        try {
          await axios.put('/api/inside-chat/dnd-device', { ...device, alwaysOn });
        } catch (syncError) {
          console.error('Error saving DND device:', syncError);
        }
        const response = await axios.get('/api/inside-chat/dnd-status');
        const status = dndStatusPayload(response.data);
        this.dndStatusRows = dndStatusEntries(status.rows, {
          userId: this.myUserId,
          name: this.myUserName,
          alwaysOn,
          deviceId: device.deviceId,
          deviceLabel: device.label,
          devices: status.devices,
          exceptEmergencies: this.insideChatDnd.exceptEmergencies,
          exceptStaff: this.insideChatDnd.exceptStaff,
          keywords: this.insideChatDnd.keywords,
        });
      } catch (error) {
        console.error('Error loading DND status:', error);
        this.dndStatusError = 'Could not load Do Not Disturb status.';
        this.dndStatusRows = [];
      } finally {
        this.dndStatusLoading = false;
      }
    },
    closeDndStatus() {
      this.showDndStatus = false;
    },
    openStaffTelemetry() {
      this.hideMenu('tools');
      this.showStaffTelemetry = true;
    },
    closeStaffTelemetry() {
      this.showStaffTelemetry = false;
    },
    async openStaffSms() {
      this.hideMenu('tools');
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showStaffSms = true;
      this.staffSmsCount = 0;
    },

    openPastDueTexts() {
      this.hideMenu('tools');
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showPastDueTexts = true;
    },

    openSmsInboxColumn() {
      this.dismissSmsInboundPopup();
      this.hideMenu('tools');
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.showSmsInbox = true;
      this.staffSmsCount = 0;
    },

    offerSmsInboundPopup(payload) {
      const popup = smsInboundPopupFromPayload(payload);
      const show =
        popup &&
        shouldShowSmsInboundPopup({
          inboxOpen: this.showSmsInbox,
          staffDialogOpen: this.showStaffSms,
          badgeCount: this.staffSmsCount,
          messageId: popup.id,
          dismissedId: readDismissedSmsPopupId(window.localStorage),
        });
      if (!show) {
        if (
          this.showSmsInbox ||
          this.showStaffSms ||
          !(this.staffSmsCount > 0)
        ) {
          this.smsInboundPopup = null;
        }
        return;
      }
      if (!this.smsInboundPopup || this.smsInboundPopup.id !== popup.id) {
        this.smsInboundPopup = popup;
      }
    },

    dismissSmsInboundPopup() {
      if (this.smsInboundPopup && this.smsInboundPopup.id) {
        rememberDismissedSmsPopup(
          window.localStorage,
          this.smsInboundPopup.id,
        );
      }
      this.smsInboundPopup = null;
    },

    goToSmsFromPopup() {
      this.openSmsInboxColumn();
      this.$nextTick(() => {
        eventBus.emit('smsInboxScrollLatest');
      });
    },

    openSmsCompose(payload) {
      this.openSmsInboxColumn();
      const target = payload || {};
      this.$nextTick(() => {
        eventBus.emit('smsComposeTo', target);
      });
    },

    publishSmsAlwaysOn() {
      publishDndAlwaysOn(
        readDndAlwaysOn(window.localStorage, this.myUserRole),
      );
    },
    onInsideChatDndChanged() {
      this.publishSmsAlwaysOn();
      this.checkUnreadTenantMessages();
      this.fetchUnreadReceipts();
    },
    async handleStaffSmsInbound(payload) {
      const alwaysOn =
        readDndAlwaysOn(window.localStorage, this.myUserRole) === true;
      if (smsAlertShowsFooter(payload, alwaysOn)) {
        const fromName = (payload && payload.fromName) || 'staff';
        eventBus.emit('footerMessage', `SMS reply from ${fromName}`);
      }
      if (this.showStaffSms || this.showSmsInbox) {
        if (!smsTabIsVisible(document)) return;
        this.smsInboundPopup = null;
        try {
          await axios.post('/api/staff-sms/seen');
          this.staffSmsCount = 0;
        } catch (err) {
          this.staffSmsCount = smsAlertBadge(payload, alwaysOn);
        }
        return;
      }
      this.staffSmsCount = smsAlertBadge(payload, alwaysOn);
      this.offerSmsInboundPopup(payload);
      if (this.smsInboundPopup) this._smsPopupHoldUntil = Date.now() + 5000;
    },

    handleVoiceMessagesInbound(payload) {
      this.unheardVoiceMemosCount = Number(payload && payload.count) || 0;
      const title = (payload && payload.title) || 'Voice message';
      eventBus.emit('footerMessage', title);
    },

    openMapIt() {
      eventBus.emit('closeDocumentViewer');
      this.handleCloseNavBarPanels();
      this.$router.push('/mapit');
    },

    openCalendar() {
      eventBus.emit('closeDocumentViewer');
      this.calendarInitialMonth = null;
      this.calendarInitialYear = null;
      this.calendarInitialDay = null;
      this.calendarPersonalMode = false;
      this.showCalendarModal = true;
    },

    async fetchTodayEventsCount() {
      try {
        const response = await axios.get('/api/calendar/events');
        const events = response.data.data || [];

        // Get today's date string in YYYY-MM-DD format
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];

        // Count events for today
        this.todayEventsCount = events.filter((event) => {
          const eventDate = new Date(event.event_date)
            .toISOString()
            .split('T')[0];
          return eventDate === todayStr;
        }).length;
      } catch (error) {
        console.error('Error fetching todays events count:', error);
        this.todayEventsCount = 0;
      }
    },

    async fetchUnheardVoiceMemosCount() {
      try {
        const response = await axios.get('/api/checkUnreadState', {
          params: { live: 0 },
        });
        this.unheardVoiceMemosCount =
          Number(response.data && response.data.voiceMessages) || 0;
      } catch (error) {
        console.error('Error fetching unheard voice memos count:', error);
        this.unheardVoiceMemosCount = 0;
      }
    },

    async fetchNewWorkOrdersCount() {
      try {
        const response = await axios.get('/api/work-orders/summary');
        const summary = response.data || {};

        // Get count of new work orders received today
        this.newWorkOrdersCount = summary.newToday?.count || 0;
      } catch (error) {
        console.error('Error fetching new work orders count:', error);
        this.newWorkOrdersCount = 0;
      }
    },

    async fetchWaitingDocumentsCount() {
      try {
        const response = await axios.get('/api/checkUnreadState');
        this.waitingDocumentsCount = response.data.scanDocuments || 0;
      } catch (error) {
        console.error('Error fetching waiting documents count:', error);
        this.waitingDocumentsCount = 0;
      }
    },

    async fetchUnreadReceipts() {
      try {
        const response = await axios.get('/api/checkUnreadState', {
          params: {
            alwaysOn: unreadAlwaysOnParam(window.localStorage, this.myUserRole),
          },
        });
        this.unreadReceipts = response.data.receipts || 0;
        const latest = response.data.latestStaffSms;
        const latestId = Number(latest && latest.id) || 0;
        const showingId = this.smsInboundPopup && this.smsInboundPopup.id;
        if (
          showingId &&
          Date.now() < (this._smsPopupHoldUntil || 0) &&
          latestId < showingId
        ) {
          return;
        }
        this.staffSmsCount = response.data.staffSms || 0;
        this.offerSmsInboundPopup(latest);
      } catch (error) {
        console.error('Error fetching unread receipts count:', error);
        this.unreadReceipts = 0;
      }
    },

    async fetchNewNotesTodayCount() {
      try {
        // Count notes from today (compare dates ignoring time)
        const now = new Date();
        const todayStart = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
        );
        const todayEnd = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() + 1,
        );

        console.log(
          '📝 Today range:',
          todayStart.toISOString(),
          'to',
          todayEnd.toISOString(),
        );
        console.log('📝 Total notes to check:', this.notes.length);

        const todayNotes = this.notes.filter((note) => {
          const noteDate = new Date(note.modifiedOn);
          const isToday = noteDate >= todayStart && noteDate < todayEnd;
          return isToday;
        });

        console.log(
          '📝 Today notes found:',
          todayNotes.map((n) => ({ id: n.id, modifiedOn: n.modifiedOn })),
        );

        this.newNotesTodayCount = todayNotes.length;
        console.log('📝 Final count set to:', this.newNotesTodayCount);
      } catch (error) {
        console.error('Error counting notes from today:', error);
        this.newNotesTodayCount = 0;
      }
    },

    async fetchNewMobileNotesTodayCount() {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        // Fetch all notes from the mobile notes API
        const response = await axios.get('/api/all-notes');
        const allNotes = response.data || [];

        // Count notes from today
        const now = new Date();
        const todayStart = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
        );
        const todayEnd = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() + 1,
        );

        console.log(
          '📱 Mobile notes today range:',
          todayStart.toISOString(),
          'to',
          todayEnd.toISOString(),
        );
        console.log('📱 Total mobile notes:', allNotes.length);

        const todayNotes = allNotes.filter((note) => {
          const noteDate = new Date(note.ModifiedOn);
          const isToday = noteDate >= todayStart && noteDate < todayEnd;
          return isToday;
        });

        console.log(
          '📱 Mobile notes today found:',
          todayNotes.map((n) => ({
            id: n.Id,
            type: n.type,
            ModifiedOn: n.ModifiedOn,
          })),
        );

        this.newMobileNotesTodayCount = todayNotes.length;
        console.log(
          '📱 Mobile notes count set to:',
          this.newMobileNotesTodayCount,
        );
      } catch (error) {
        console.error('Error counting mobile notes from today:', error);
        this.newMobileNotesTodayCount = 0;
      }
    },

    // Helper function to get user ID from token
    getUserIdFromToken() {
      try {
        const token = localStorage.getItem('token');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub;
      } catch (error) {
        console.error('Error extracting user ID from token:', error);
        return null;
      }
    },

    closeCalendar() {
      this.showCalendarModal = false;
      this.calendarInitialMonth = null;
      this.calendarInitialYear = null;
      this.calendarInitialDay = null;
      this.calendarPersonalMode = false;
      this.calendarInitialGridTab = null;
      // Refresh today's events count when calendar closes
      this.fetchTodayEventsCount();
    },

    handleCalendarNotification(message, type, duration = 3000) {
      // Re-emit notification through eventBus or handle it locally
      console.log('📅 [NAVBAR] Calendar notification:', message, type);
      // You can add toast notifications here if needed
      eventBus.emit('showNotification', { message, type, duration });
    },

    async checkAvailabilitySlots() {
      try {
        const now = new Date();
        const dayOfWeek = now.getDay();
        // Start of current week (Sunday)
        const thisWeekStart = new Date(now);
        thisWeekStart.setDate(now.getDate() - dayOfWeek);
        thisWeekStart.setHours(0, 0, 0, 0);
        // End of next week (Saturday)
        const nextWeekEnd = new Date(thisWeekStart);
        nextWeekEnd.setDate(thisWeekStart.getDate() + 13);

        const fmt = (d) => d.toISOString().split('T')[0];
        const thisWeekEnd = new Date(thisWeekStart);
        thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
        const nextWeekStart = new Date(thisWeekStart);
        nextWeekStart.setDate(thisWeekStart.getDate() + 7);

        const res = await axios.get('/api/scheduling/availability', {
          params: {
            start_date: fmt(thisWeekStart),
            end_date: fmt(nextWeekEnd),
          },
        });

        const slots = (res.data.data || []).filter((s) => s.available === 1);
        const thisWeekEndStr = fmt(thisWeekEnd);
        const nextWeekStartStr = fmt(nextWeekStart);

        const hasThisWeek = slots.some(
          (s) => s.slot_date.split('T')[0] <= thisWeekEndStr,
        );
        const hasNextWeek = slots.some(
          (s) => s.slot_date.split('T')[0] >= nextWeekStartStr,
        );

        if (!hasThisWeek || !hasNextWeek) {
          const missing = [];
          if (!hasThisWeek) missing.push('this week');
          if (!hasNextWeek) missing.push('next week');
          this.availabilityWarningWeeks = missing.join(' and ');
          this.showAvailabilityWarning = true;
        }
      } catch (err) {
        console.error('Error checking availability slots:', err);
      }
    },

    openCalendarAvailability() {
      this.showAvailabilityWarning = false;
      this.calendarInitialGridTab = 'availability';
      this.showCalendarModal = true;
    },

    openCalendarToDate({ month, year, day, personal = false }) {
      console.log('📅 [NAVBAR] Opening calendar to date:', {
        month,
        year,
        day,
        personal,
      });
      this.calendarInitialMonth = month;
      this.calendarInitialYear = year;
      this.calendarInitialDay = day;
      this.calendarPersonalMode = personal;
      this.showCalendarModal = true;
    },

    openFinancialSummary() {
      // Check user role - only allow access for role < 2
      if (this.myUserRole >= 2) {
        console.warn(
          'Financial Summary access denied - insufficient permissions',
        );
        return;
      }
      this.showFinancialSummary = true;
    },

    openMaintenanceTrends() {
      if (this.myUserRole >= 2) {
        console.warn(
          'Maintenance Trends access denied - insufficient permissions',
        );
        return;
      }
      this.showMaintenanceTrends = true;
    },

    closeMaintenanceTrends() {
      this.showMaintenanceTrends = false;
    },

    openUtilitiesTrend() {
      if (this.myUserRole >= 2) {
        console.warn(
          'Utilities Trend access denied - insufficient permissions',
        );
        return;
      }
      this.showUtilitiesTrend = true;
    },

    closeUtilitiesTrend() {
      this.showUtilitiesTrend = false;
    },

    openInsuranceTrend() {
      if (this.myUserRole >= 2) {
        console.warn(
          'Insurance Trend access denied - insufficient permissions',
        );
        return;
      }
      this.showInsuranceTrend = true;
    },

    closeInsuranceTrend() {
      this.showInsuranceTrend = false;
    },

    openTempCharts() {
      this.showTempCharts = true;
    },

    closeTempCharts() {
      this.showTempCharts = false;
    },

    navigateTrend(current, direction) {
      const order = ['maintenance', 'utilities', 'insurance', 'tempcharts'];
      const idx = order.indexOf(current);
      const next = order[(idx + direction + order.length) % order.length];
      // Close current
      this.showMaintenanceTrends = false;
      this.showUtilitiesTrend = false;
      this.showInsuranceTrend = false;
      this.showTempCharts = false;
      // Open next
      this.$nextTick(() => {
        if (next === 'maintenance') this.showMaintenanceTrends = true;
        else if (next === 'utilities') this.showUtilitiesTrend = true;
        else if (next === 'insurance') this.showInsuranceTrend = true;
        else if (next === 'tempcharts') this.showTempCharts = true;
      });
    },

    openPayeeSummary(filter = '') {
      // Check user role - only allow access for role < 2
      if (this.myUserRole >= 2) {
        console.warn('Payee Summary access denied - insufficient permissions');
        return;
      }
      // Ensure filter is a string (click events pass MouseEvent as first arg)
      this.payeeSummaryFilter = typeof filter === 'string' ? filter : '';
      this.showPayeeSummary = true;
    },

    closePayeeSummary() {
      this.showPayeeSummary = false;
    },

    openPropertyAnalysis() {
      if (this.myUserRole >= 2) {
        console.warn(
          'Property Analysis access denied - insufficient permissions',
        );
        return;
      }
      this.showPropertyAnalysis = true;
    },

    closePropertyAnalysis() {
      this.showPropertyAnalysis = false;
    },

    openUnclearedChecks() {
      if (this.myUserRole >= 2) {
        console.warn(
          'Uncleared Checks access denied - insufficient permissions',
        );
        return;
      }
      this.showUnclearedChecks = true;
    },

    closeUnclearedChecks() {
      this.showUnclearedChecks = false;
    },

    showComingSoon(featureName) {
      alert(`${featureName} - Coming Soon`);
    },

    openAutomate() {
      eventBus.emit('closeDocumentViewer');
      this.$router.push({ path: '/automate' });
    },

    handleShowQuickEmails() {
      eventBus.emit('closeDocumentViewer');
      this.handleEmailNavClick();
    },

    handleCloseQuickEmails() {
      this.showQuickEmail = false;
    },

    handleCloseChat() {
      this.showChatTool = false;
    },

    handleCloseCash() {
      this.showCashPosition = false;
    },

    handleCloseLockCodes() {
      this.showLockCodes = false;
    },

    handleCloseMWorkOrders() {
      this.showWorkOrders = false;
    },

    handleCloseAppliances() {
      this.showAppliances = false;
    },

    handleClosePropertySummary() {
      this.showPropertySummary = false;
    },

    handleCloseQuarters() {
      this.showQuarters = false;
    },

    closeFinancialSummary() {
      this.showFinancialSummary = false;
    },

    handleMonthFilter(monthIndex) {
      // Emit event to MainPage to set month range
      eventBus.emit('set-month-range', monthIndex);
    },

    handleShowUncategorized(data) {
      this.drilldownCategoryName =
        data.categoryName || 'Uncategorized Expenses';
      this.uncategorizedTransactions = data.transactions;
      this.uncategorizedMonth = data.month;
      this.uncategorizedYear = data.year;
      this.showUncategorizedTransactions = true;
    },

    closeUncategorizedTransactions() {
      this.showUncategorizedTransactions = false;
      this.uncategorizedTransactions = [];
      this.uncategorizedRefreshing = false;
    },

    async refreshUncategorizedTransactions() {
      if (this.uncategorizedRefreshing) return;
      this.uncategorizedRefreshing = true;
      try {
        const category = this.drilldownCategoryName || 'Uncategorized Expenses';
        if (category === 'Uncategorized Expenses') {
          const response = await axios.get(
            `/api/financial-summary/${this.uncategorizedYear}/${this.uncategorizedMonth}/uncategorized`,
          );
          this.uncategorizedTransactions = response.data || [];
        } else {
          // Other category drilldowns: re-fetch by current transaction IDs
          const ids = (this.uncategorizedTransactions || [])
            .map((t) => t.Id)
            .filter((id) => id != null);
          if (ids.length === 0) {
            this.uncategorizedTransactions = [];
          } else {
            const response = await axios.post('/api/bank-transactions/by-ids', {
              ids,
            });
            this.uncategorizedTransactions = response.data || [];
          }
        }
      } catch (error) {
        console.error('Error refreshing transaction drilldown:', error);
      } finally {
        this.uncategorizedRefreshing = false;
      }
    },

    handleShowAnnualDetail(data) {
      this.annualDetailCategoryName = data.categoryName || 'Transactions';
      this.annualDetailTransactions = data.transactions;
      this.annualDetailYear = data.year;
      this.showAnnualCategoryDetail = true;
    },

    closeAnnualCategoryDetail() {
      this.showAnnualCategoryDetail = false;
      this.annualDetailTransactions = [];
    },

    async refreshAnnualCategoryDetail() {
      try {
        const response = await axios.get(
          `/api/financial-summary/${this.annualDetailYear}/category-annual/${encodeURIComponent(this.annualDetailCategoryName)}`,
        );
        if (response.data) {
          this.annualDetailTransactions = response.data;
        }
      } catch (error) {
        console.error('Error refreshing annual category detail:', error);
      }
    },

    handleCloseMnotes() {
      this.showMobileNotes = false;
    },

    handleCloseSmsInbox() {
      this.showSmsInbox = false;
    },

    handleNewEmails() {
      this.newEmailsAvailable = true;
      this.fetchUnseenEmailsCount();
    },

    // Quarter Tracker event handlers - forward to EventBus
    handleQuarterRecordCreated(data) {
      console.log(
        '📢 NavBar forwarding quarterRecordCreated to EventBus:',
        data,
      );
      eventBus.emit('quarterRecordCreated', data);
    },

    handleQuarterRecordUpdated(data) {
      console.log(
        '📢 NavBar forwarding quarterRecordUpdated to EventBus:',
        data,
      );
      eventBus.emit('quarterRecordUpdated', data);
    },

    handleQuarterRecordDeleted(data) {
      console.log(
        '📢 NavBar forwarding quarterRecordDeleted to EventBus:',
        data,
      );
      eventBus.emit('quarterRecordDeleted', data);
    },

    // QR Collector event handlers - forward to EventBus
    handleCollectorCampaignCreated(data) {
      console.log(
        '📢 NavBar forwarding collectorCampaignCreated to EventBus:',
        data,
      );
      eventBus.emit('collectorCampaignCreated', data);
    },

    handleCollectorCampaignUpdated(data) {
      console.log(
        '📢 NavBar forwarding collectorCampaignUpdated to EventBus:',
        data,
      );
      eventBus.emit('collectorCampaignUpdated', data);
    },

    handleCollectorCampaignDeleted(data) {
      console.log(
        '📢 NavBar forwarding collectorCampaignDeleted to EventBus:',
        data,
      );
      eventBus.emit('collectorCampaignDeleted', data);
    },

    handleCollectorResponseCreated(data) {
      console.log(
        '📢 NavBar forwarding collectorResponseCreated to EventBus:',
        data,
      );
      eventBus.emit('collectorResponseCreated', data);
    },

    // Handle new simple note
    handleNewSimpleNote(newNote) {
      const note = {
        ...newNote,
        highlightedNote: null,
        showIcons: false,
        isEditing: false,
      };
      // if the note is from the current user, it's already in the array.
      // only need to do this for notes from other users
      if (newNote.modifiedBy !== this.myUserId) {
        this.notes.unshift(note);
        this.hideNotes = false;
      }
      // Update the badge count
      this.fetchNewNotesTodayCount();
    },

    handleUpdatedSimpleNote(updatedNote) {
      const index = this.notes.findIndex(
        (n) => n.id === parseInt(updatedNote.id),
      );
      if (index !== -1) {
        // console.log('updated note', updatedNote, index);
        // Update existing note
        this.notes[index].note = updatedNote.note;
        this.notes[index].displayOrder = updatedNote.displayOrder;
        this.notes[index].modifiedBy = updatedNote.modifiedBy;
        // Optionally update the modifiedOn field
        this.notes[index].modifiedOn = new Date().toISOString();
        this.hideNotes = false;
      }
    },

    handleReminderForUser(reminderData) {
      console.log('📧 Received reminder from another user:', reminderData);

      // Add the reminder to this user's local storage
      const reminder = {
        task: reminderData.task,
        dateTime: reminderData.dateTime,
        noteId: `remote-${Date.now()}-${Math.random()}`, // Generate unique ID for remote reminder
        fromUser: reminderData.fromUser,
      };

      this.quickReminders.push(reminder);
      localStorage.setItem(
        'quickReminders',
        JSON.stringify(this.quickReminders),
      );

      // Show a notification to the user
      this.$toast?.success(
        `New reminder from ${reminderData.fromUser}: ${reminderData.task}`,
      );

      // Force check reminders to see if it's due immediately
      this.checkReminders();
    },

    handleDeletedSimpleNote(deletedNote) {
      // console.log('socket handler: deleted note', deletedNote);
      this.notes = this.notes.filter(
        (note) => note.id !== parseInt(deletedNote.id),
      );
    },

    emailListener({ to, subject, body }) {
      this.openQuickEmail(to, subject, body);
    },

    handleShowShortcuts() {
      console.log('here');
      this.showShortcuts;
    },

    getServiceStatus() {
      axios
        .get('/api/service-check')
        .then((response) => {
          this.footerMessage =
            response.data.message || 'Service status checked';

          // Set a timeout to clear the footerMessage after 10 seconds
          setTimeout(() => {
            this.footerMessage = '';
          }, 10000);
        })
        .catch((error) => {
          console.error('Error checking service status:', error);
          this.footerMessage = 'Unable to check service status';

          // Clear error message after 10 seconds
          setTimeout(() => {
            this.footerMessage = '';
          }, 10000);
        });
    },

    openHelpVideos() {
      eventBus.emit('closeDocumentViewer');
      this.showHelpVideos = true;
    },

    closeHelpVideos() {
      this.showHelpVideos = false;
    },

    async openRecentActivity() {
      this.hideMenu('tools');
      try {
        const response = await axios.get('/api/activity-logs/recent-by-user');
        if (response.data.success) {
          this.recentActivityData = response.data.data;
        } else {
          this.recentActivityData = [];
        }
      } catch (error) {
        console.error('Error fetching recent activity:', error);
        this.recentActivityData = [];
      }
      this.showRecentActivityPopup = true;
    },

    async openRentPaymentSignups() {
      this.hideMenu('tools');
      this.rentSignupsLoading = true;
      try {
        const [visitorsRes, visitsRes, versionsRes, smsOptInsRes, notificationsRes] =
          await Promise.all([
            axios.get('/api/chatbot-recent-visitors'),
            axios.get('/api/chatbot-visits-by-day'),
            axios.get('/api/chatbot-versions'),
            axios.get('/api/sms-opt-ins-by-day'),
            axios.get('/api/tenant-notifications'),
          ]);
        this.recentVisitorsData = visitorsRes.data.success
          ? visitorsRes.data.data
          : [];
        this.chatbotVisitsData = visitsRes.data.success
          ? visitsRes.data.data
          : [];
        this.chatbotVersionData = versionsRes.data.success
          ? versionsRes.data.data
          : [];
        this.smsOptInByDayData = smsOptInsRes.data.success
          ? smsOptInsRes.data.data
          : [];
        this.tenantNotificationData = notificationsRes.data.success
          ? notificationsRes.data.data
          : [];
      } catch (error) {
        console.error('Error fetching tenant app stats:', error);
        this.recentVisitorsData = [];
        this.chatbotVisitsData = [];
        this.chatbotVersionData = [];
        this.smsOptInByDayData = [];
        this.tenantNotificationData = [];
      } finally {
        this.rentSignupsLoading = false;
      }
      this.showRentPaymentSignupsPopup = true;
    },

    visitDayKey(day) {
      if (!day) return '';
      if (typeof day === 'string') {
        const match = day.match(/^(\d{4}-\d{2}-\d{2})/);
        if (match) return match[1];
      }
      const d = new Date(day);
      if (Number.isNaN(d.getTime())) return '';
      const year = d.getUTCFullYear();
      const month = String(d.getUTCMonth() + 1).padStart(2, '0');
      const date = String(d.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${date}`;
    },

    async openVisitorsForDay(row) {
      const day = this.visitDayKey(row && row.day);
      if (!day) return;
      this.selectedVisitDay = day;
      this.showVisitDayNamesPopup = true;
      this.visitDayNamesLoading = true;
      this.visitDayNamesData = [];
      try {
        const response = await axios.get('/api/chatbot-visitors-for-day', {
          params: { day },
        });
        this.visitDayNamesData = response.data.success
          ? response.data.data || []
          : [];
      } catch (error) {
        console.error('Error fetching visitors for day:', error);
        this.visitDayNamesData = [];
      } finally {
        this.visitDayNamesLoading = false;
      }
    },

    closeVisitorsForDay() {
      this.showVisitDayNamesPopup = false;
      this.visitDayNamesData = [];
      this.visitDayNamesLoading = false;
      this.selectedVisitDay = '';
    },

    async openTenantsForVersion(row) {
      const version = row && row.version;
      if (!version) return;
      this.selectedTenantVersion = version;
      this.showVersionTenantsPopup = true;
      this.versionTenantsLoading = true;
      this.versionTenantsData = [];
      try {
        const response = await axios.get('/api/chatbot-tenants-for-version', {
          params: { version },
        });
        this.versionTenantsData = response.data.success
          ? response.data.data || []
          : [];
      } catch (error) {
        console.error('Error fetching tenants for version:', error);
        this.versionTenantsData = [];
      } finally {
        this.versionTenantsLoading = false;
      }
    },

    closeTenantsForVersion() {
      this.showVersionTenantsPopup = false;
      this.versionTenantsData = [];
      this.versionTenantsLoading = false;
      this.selectedTenantVersion = '';
    },

    async openSmsOptInsForDay(row) {
      const day = this.visitDayKey(row && row.day);
      if (!day) return;
      this.selectedSmsOptInDay = day;
      this.showSmsOptInNamesPopup = true;
      this.smsOptInNamesLoading = true;
      this.smsOptInNamesData = [];
      try {
        const response = await axios.get('/api/sms-opt-ins-for-day', {
          params: { day },
        });
        this.smsOptInNamesData = response.data.success
          ? response.data.data || []
          : [];
      } catch (error) {
        console.error('Error fetching SMS opt-ins for day:', error);
        this.smsOptInNamesData = [];
      } finally {
        this.smsOptInNamesLoading = false;
      }
    },

    closeSmsOptInsForDay() {
      this.showSmsOptInNamesPopup = false;
      this.smsOptInNamesData = [];
      this.smsOptInNamesLoading = false;
      this.selectedSmsOptInDay = '';
    },

    async openTenantsForNotificationState(row) {
      const state = row && row.state;
      if (state !== 'enabled' && state !== 'off') return;
      this.selectedNotificationState = state;
      this.selectedNotificationLabel = row.label || state;
      this.showNotificationTenantsPopup = true;
      this.notificationTenantsLoading = true;
      this.notificationTenantsData = [];
      try {
        const response = await axios.get('/api/tenants-for-notification-state', {
          params: { state },
        });
        this.notificationTenantsData = response.data.success
          ? response.data.data || []
          : [];
      } catch (error) {
        console.error('Error fetching tenants for notifications:', error);
        this.notificationTenantsData = [];
      } finally {
        this.notificationTenantsLoading = false;
      }
    },

    closeTenantsForNotificationState() {
      this.showNotificationTenantsPopup = false;
      this.notificationTenantsData = [];
      this.notificationTenantsLoading = false;
      this.selectedNotificationState = '';
      this.selectedNotificationLabel = '';
    },

    closeRentPaymentSignups() {
      this.closeVisitorsForDay();
      this.closeTenantsForVersion();
      this.closeSmsOptInsForDay();
      this.closeTenantsForNotificationState();
      this.showRentPaymentSignupsPopup = false;
    },

    teardownChatbotTelemetryDrag() {
      if (this.chatbotTelemetryDragCleanup) {
        this.chatbotTelemetryDragCleanup();
        this.chatbotTelemetryDragCleanup = null;
      }
    },

    initChatbotTelemetryDrag() {
      this.teardownChatbotTelemetryDrag();
      const el = this.$refs.chatbotTelemetryDialog;
      if (!el) return;

      const hasSaved =
        localStorage.getItem('chatbotTelemetryPopupTop') &&
        localStorage.getItem('chatbotTelemetryPopupLeft');
      if (!hasSaved) {
        const width = el.offsetWidth || Math.min(window.innerWidth * 0.9, 1500);
        el.style.top = '40px';
        el.style.left = `${Math.max(16, (window.innerWidth - width) / 2)}px`;
      }

      this.chatbotTelemetryDragCleanup = makeDraggable(el, {
        handleSelector: '.chatbot-telemetry-drag-handle',
        storageKeyPrefix: 'chatbotTelemetryPopup',
      });
    },

    closeChatbotTelemetryPopup() {
      this.teardownChatbotTelemetryDrag();
      this.showChatbotTelemetryPopup = false;
    },

    closePollingLocksPopup() {
      this.showPollingLocksPopup = false;
    },

    formatPollingTimestamp(dateString) {
      if (!dateString) return '—';

      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '—';

      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short',
      });
    },

    getPollingLocksErrorBaseline() {
      const stored = localStorage.getItem('pollingLocksErrorBaseline');
      if (!stored) return null;

      try {
        const parsed = JSON.parse(stored);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        return null;
      }
    },

    setPollingLocksErrorBaseline(snapshot) {
      localStorage.setItem(
        'pollingLocksErrorBaseline',
        JSON.stringify(snapshot),
      );
    },

    buildPollingLocksErrorSnapshot(services) {
      const snapshot = {};
      for (const service of services || []) {
        snapshot[service.serviceName] = {
          errorCount: Number(service.errorCount) || 0,
          lastError: service.lastError || null,
        };
      }
      return snapshot;
    },

    hasNewPollingLockErrors(services, baseline) {
      if (!baseline) return false;

      for (const service of services || []) {
        if (!service.hasErrors) continue;

        const previous = baseline[service.serviceName];
        const errorCount = Number(service.errorCount) || 0;
        const lastError = service.lastError || null;

        if (!previous) {
          if (errorCount > 0 || lastError) return true;
          continue;
        }

        if (errorCount > previous.errorCount) return true;
        if (lastError && lastError !== previous.lastError) return true;
      }

      return false;
    },

    startPollingLocksMonitor() {
      this.checkForNewPollingLockErrors();
      this.pollingLocksMonitorInterval = setInterval(() => {
        this.checkForNewPollingLockErrors();
      }, 300000);
    },

    async fetchPollingLocks() {
      const response = await axios.get('/api/polling-locks');
      if (!response.data?.success) {
        throw new Error(
          response.data?.message || 'Unable to load polling locks',
        );
      }
      return response.data;
    },

    async checkForNewPollingLockErrors() {
      if (!Number.isFinite(this.myUserRole) || this.myUserRole > 1) return;
      if (this.showPollingLocksAlert || this.showPollingLocksPopup) return;

      try {
        const data = await this.fetchPollingLocks();
        const services = data.services || [];
        const snapshot = this.buildPollingLocksErrorSnapshot(services);
        const baseline = this.getPollingLocksErrorBaseline();

        if (!baseline) {
          this.setPollingLocksErrorBaseline(snapshot);
          return;
        }

        if (
          this.hasNewPollingLockErrors(services, baseline) &&
          shouldShowNavAlert(NAV_ALERT_IDS.pollingLocks)
        ) {
          this.showPollingLocksAlert = true;
        }
      } catch (error) {
        console.error('Error checking polling lock status:', error);
      }
    },

    ignorePollingLocksAlert() {
      this.markPollingLocksErrorsSeen();
      this.showPollingLocksAlert = false;
      this.ignorePrompt = null;
    },

    async markPollingLocksErrorsSeen() {
      try {
        const data = await this.fetchPollingLocks();
        this.setPollingLocksErrorBaseline(
          this.buildPollingLocksErrorSnapshot(data.services || []),
        );
      } catch (error) {
        console.error('Error updating polling lock error baseline:', error);
      }
    },

    async openPollingLocksFromAlert() {
      this.showPollingLocksAlert = false;
      this.ignorePrompt = null;
      await this.openPollingLocksMonitor();
      await this.markPollingLocksErrorsSeen();
    },

    async openPollingLocksMonitor() {
      this.showPollingLocksPopup = true;
      await this.refreshPollingLocks();
    },

    async openPollingLocksStatus() {
      this.hideMenu('tools');
      await this.openPollingLocksMonitor();
    },

    async refreshPollingLocks({ duringReset = false } = {}) {
      if (this.pollingLocksLoading) return;
      if (this.pollingLocksResetting && !duringReset) return;

      this.pollingLocksLoading = true;
      this.pollingLocksError = '';

      try {
        const data = await this.fetchPollingLocks();
        this.pollingLocksServices = data.services || [];
        this.pollingLocksUpdatedAt = data.updatedAt
          ? this.formatPollingTimestamp(data.updatedAt)
          : '';
      } catch (error) {
        console.error('Error fetching polling locks:', error);
        this.pollingLocksServices = [];
        this.pollingLocksUpdatedAt = '';
        this.pollingLocksError =
          error.response?.data?.message ||
          error.message ||
          'Unable to load polling lock status';
      } finally {
        this.pollingLocksLoading = false;
      }
    },

    isDevPollingLockOwner(service) {
      const host = (service?.lockedBy || '').split('.')[0];
      return host === 'EricMiniPC';
    },

    isPollingLockActionBusy(serviceName) {
      return (
        this.pollingLocksClaimingService === serviceName ||
        this.pollingLocksReleasingService === serviceName
      );
    },

    async claimPollingLock(serviceName) {
      if (
        !this.isDev ||
        this.isPollingLockActionBusy(serviceName) ||
        this.pollingLocksLoading ||
        this.pollingLocksResetting
      ) {
        return;
      }

      this.pollingLocksClaimingService = serviceName;
      this.pollingLocksError = '';

      try {
        const response = await axios.post('/api/polling-locks/claim', {
          serviceName,
        });
        if (!response.data?.success) {
          throw new Error(
            response.data?.message || 'Unable to claim polling lock',
          );
        }

        await this.refreshPollingLocks();
      } catch (error) {
        console.error('Error claiming polling lock:', error);
        this.pollingLocksError =
          error.response?.data?.message || 'Unable to claim polling lock';
      } finally {
        this.pollingLocksClaimingService = null;
      }
    },

    async releasePollingLock(serviceName) {
      if (
        !this.isDev ||
        this.isPollingLockActionBusy(serviceName) ||
        this.pollingLocksLoading ||
        this.pollingLocksResetting
      ) {
        return;
      }

      this.pollingLocksReleasingService = serviceName;
      this.pollingLocksError = '';

      try {
        const response = await axios.post('/api/polling-locks/release', {
          serviceName,
        });
        if (!response.data?.success) {
          throw new Error(
            response.data?.message || 'Unable to release polling lock',
          );
        }

        await this.refreshPollingLocks();
      } catch (error) {
        console.error('Error releasing polling lock:', error);
        this.pollingLocksError =
          error.response?.data?.message || 'Unable to release polling lock';
      } finally {
        this.pollingLocksReleasingService = null;
      }
    },

    openRunServiceDialog(service) {
      if (!service?.serviceName || this.runServiceRunning) {
        return;
      }
      this.stopRunServicePoll();
      this.runServiceName = service.serviceName;
      this.runServiceJobId = null;
      this.runServiceLogs = [];
      this.runServiceLogOffset = 0;
      this.runServiceStatus = '';
      this.runServiceError = '';
      this.runServiceRunning = false;
      this.runServicePollInFlight = false;
      this.showRunServiceDialog = true;
    },

    closeRunServiceDialog() {
      if (this.runServiceRunning) {
        return;
      }
      this.stopRunServicePoll();
      this.showRunServiceDialog = false;
      this.runServiceName = '';
      this.runServiceJobId = null;
      this.runServiceLogs = [];
      this.runServiceLogOffset = 0;
      this.runServiceStatus = '';
      this.runServiceError = '';
      this.runServicePollInFlight = false;
    },

    stopRunServicePoll() {
      if (this.runServicePollTimer) {
        clearInterval(this.runServicePollTimer);
        this.runServicePollTimer = null;
      }
    },

    scrollRunServiceOutputToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.runServiceOutput;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },

    downloadRunServiceLog() {
      const text = this.runServiceLogsText;
      if (!text) {
        return;
      }

      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const stamp = [
        now.getFullYear(),
        pad(now.getMonth() + 1),
        pad(now.getDate()),
        '-',
        pad(now.getHours()),
        pad(now.getMinutes()),
        pad(now.getSeconds()),
      ].join('');
      const safeName = String(this.runServiceName || 'service')
        .replace(/[^a-zA-Z0-9._-]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'service';
      const filename = `${safeName}-run-${stamp}.log`;

      const header = [
        `Service: ${this.runServiceName || '(unknown)'}`,
        `Job: ${this.runServiceJobId || '(none)'}`,
        `Status: ${this.runServiceStatus || '(n/a)'}`,
        `Downloaded: ${now.toISOString()}`,
        '',
        '--- log ---',
        '',
      ].join('\n');

      const blob = new Blob([header + text + '\n'], {
        type: 'text/plain;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    async startRunServiceOnce() {
      if (!this.runServiceName || this.runServiceRunning) {
        return;
      }

      this.runServiceRunning = true;
      this.runServiceStatus = 'running';
      this.runServiceError = '';
      this.runServiceLogs = [];
      this.runServiceLogOffset = 0;
      this.runServiceJobId = null;
      this.runServicePollInFlight = false;
      this.stopRunServicePoll();

      try {
        const response = await axios.post('/api/polling-locks/run-once', {
          serviceName: this.runServiceName,
        });
        if (!response.data?.success || !response.data?.jobId) {
          throw new Error(
            response.data?.message || 'Unable to start service run',
          );
        }

        this.runServiceJobId = response.data.jobId;
        this.runServiceLogs.push(
          `[started] ${this.runServiceName} (job ${this.runServiceJobId})`,
        );
        this.scrollRunServiceOutputToBottom();

        this.runServicePollTimer = setInterval(() => {
          this.pollRunServiceJob();
        }, 500);
        await this.pollRunServiceJob();
      } catch (error) {
        console.error('Error starting service run:', error);
        this.runServiceRunning = false;
        this.runServiceStatus = 'failed';
        this.runServiceError =
          error.response?.data?.message ||
          error.message ||
          'Unable to start service run';
        this.runServiceLogs.push(`[error] ${this.runServiceError}`);
      }
    },

    async pollRunServiceJob() {
      if (!this.runServiceJobId || this.runServicePollInFlight) {
        return;
      }

      this.runServicePollInFlight = true;
      try {
        const response = await axios.get(
          `/api/polling-locks/run-once/${this.runServiceJobId}`,
          {
            params: { logOffset: this.runServiceLogOffset },
          },
        );
        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Unable to poll run status');
        }

        const newLogs = response.data.logs || [];
        if (newLogs.length) {
          this.runServiceLogs = this.runServiceLogs.concat(newLogs);
          this.scrollRunServiceOutputToBottom();
        }
        if (typeof response.data.logTotal === 'number') {
          this.runServiceLogOffset = response.data.logTotal;
        }

        this.runServiceStatus = response.data.status || this.runServiceStatus;

        if (
          response.data.status === 'completed' ||
          response.data.status === 'failed'
        ) {
          this.stopRunServicePoll();
          this.runServiceRunning = false;
          if (response.data.status === 'failed') {
            this.runServiceError =
              response.data.error || 'Service run failed';
          } else if (response.data.result != null) {
            const resultLine =
              typeof response.data.result === 'string'
                ? response.data.result
                : JSON.stringify(response.data.result, null, 2);
            this.runServiceLogs.push(`[result] ${resultLine}`);
            this.scrollRunServiceOutputToBottom();
          }
          await this.refreshPollingLocks();
        }
      } catch (error) {
        console.error('Error polling service run:', error);
        this.stopRunServicePoll();
        this.runServiceRunning = false;
        this.runServiceStatus = 'failed';
        this.runServiceError =
          error.response?.data?.message ||
          error.message ||
          'Lost connection while watching service run';
        this.runServiceLogs.push(`[error] ${this.runServiceError}`);
      } finally {
        this.runServicePollInFlight = false;
      }
    },

    async resetPollingLocksErrors() {
      if (
        this.pollingLocksResetting ||
        this.pollingLocksLoading ||
        !this.pollingLocksHasActiveErrors
      ) {
        return;
      }

      this.pollingLocksResetting = true;
      this.pollingLocksError = '';

      try {
        const response = await axios.post('/api/polling-locks/dismiss-errors');
        if (!response.data?.success) {
          throw new Error(
            response.data?.message || 'Unable to reset polling lock errors',
          );
        }

        this.showPollingLocksAlert = false;
        await this.refreshPollingLocks({ duringReset: true });
        await this.markPollingLocksErrorsSeen();
      } catch (error) {
        console.error('Error resetting polling lock errors:', error);
        this.pollingLocksError = 'Unable to reset polling lock errors';
      } finally {
        this.pollingLocksResetting = false;
      }
    },

    getChatbotTelemetryLastSeenId() {
      const stored = localStorage.getItem('chatbotTelemetryLastSeenId');
      const parsed = Number.parseInt(stored, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    },

    setChatbotTelemetryLastSeenId(id) {
      const parsed = Number.parseInt(id, 10);
      if (!Number.isFinite(parsed) || parsed <= 0) return;
      localStorage.setItem('chatbotTelemetryLastSeenId', String(parsed));
    },

    markChatbotTelemetrySeen(id) {
      const parsed = Number.parseInt(id, 10);
      if (!Number.isFinite(parsed) || parsed <= 0) return;
      const current = this.getChatbotTelemetryLastSeenId();
      if (current == null || parsed > current) {
        this.setChatbotTelemetryLastSeenId(parsed);
      }
    },

    highestTelemetryId(rows) {
      return (rows || []).reduce((max, row) => {
        const id = Number.parseInt(row.id, 10);
        return Number.isFinite(id) && id > max ? id : max;
      }, 0);
    },

    snapshotChatbotTelemetryHighestId() {
      const maxShown = this.highestTelemetryId(this.chatbotTelemetryRows);
      if (maxShown > 0) {
        this.setChatbotTelemetryLastSeenId(maxShown);
      }
    },

    snapshotVisibleChatbotTelemetryHighestId() {
      const maxShown = this.highestTelemetryId(
        this.visibleChatbotTelemetryRows,
      );
      if (maxShown > 0) {
        this.setChatbotTelemetryLastSeenId(maxShown);
      }
    },

    toggleChatbotTelemetryNewOnly() {
      if (!this.chatbotTelemetryShowNewOnly) {
        this.snapshotChatbotTelemetryHighestId();
        this.chatbotTelemetryShowNewOnly = true;
        this.chatbotTelemetryApplyNewFilter = false;
      } else {
        this.chatbotTelemetryShowNewOnly = false;
        this.chatbotTelemetryApplyNewFilter = false;
      }
      localStorage.setItem(
        'chatbotTelemetryShowNewOnly',
        this.chatbotTelemetryShowNewOnly ? '1' : '0',
      );
    },

    startChatbotTelemetryMonitor() {
      this.checkForNewChatbotTelemetry();
      this.chatbotTelemetryMonitorInterval = setInterval(() => {
        this.checkForNewChatbotTelemetry();
      }, 300000);
    },

    async fetchLatestChatbotTelemetryId() {
      const response = await axios.get('/api/chatbot-telemetry', {
        params: { limit: 1, offset: 0 },
      });

      if (!response.data?.success) {
        return null;
      }

      const rows = response.data.data || [];
      const latestId = Number.parseInt(rows[0]?.id, 10);
      return Number.isFinite(latestId) && latestId > 0 ? latestId : null;
    },

    async checkForNewChatbotTelemetry() {
      if (this.myUserRole == null || this.myUserRole > 1) return;
      if (this.showChatbotTelemetryAlert || this.showChatbotTelemetryPopup) {
        return;
      }

      try {
        const latestId = await this.fetchLatestChatbotTelemetryId();
        if (latestId == null) return;

        const lastSeenId = this.getChatbotTelemetryLastSeenId();
        if (lastSeenId == null) {
          this.setChatbotTelemetryLastSeenId(latestId);
          return;
        }

        if (latestId > lastSeenId && shouldShowNavAlert(NAV_ALERT_IDS.telemetry)) {
          this.chatbotTelemetryAlertPendingId = latestId;
          this.showChatbotTelemetryAlert = true;
        }
      } catch (error) {
        console.error('Error checking for new chatbot telemetry:', error);
      }
    },

    ignoreChatbotTelemetryAlert() {
      if (this.chatbotTelemetryAlertPendingId != null) {
        this.markChatbotTelemetrySeen(this.chatbotTelemetryAlertPendingId);
      }
      this.showChatbotTelemetryAlert = false;
      this.chatbotTelemetryAlertPendingId = null;
      this.ignorePrompt = null;
    },

    async openChatbotTelemetryFromAlert() {
      this.showChatbotTelemetryAlert = false;
      this.chatbotTelemetryAlertPendingId = null;
      this.ignorePrompt = null;
      await this.openChatbotTelemetry();
    },

    async openChatbotTelemetry() {
      this.hideMenu('tools');
      this.showChatbotTelemetryAlert = false;
      this.chatbotTelemetryAlertPendingId = null;
      this.chatbotTelemetryApplyNewFilter = this.chatbotTelemetryShowNewOnly;
      this.chatbotTelemetryFilterAfterId = this.chatbotTelemetryShowNewOnly
        ? this.getChatbotTelemetryLastSeenId() || 0
        : 0;
      this.showChatbotTelemetryPopup = true;
      this.$nextTick(() => this.initChatbotTelemetryDrag());
      await this.refreshChatbotTelemetry();
    },

    scheduleChatbotTelemetrySearch() {
      if (this.chatbotTelemetrySearchTimer) {
        clearTimeout(this.chatbotTelemetrySearchTimer);
      }

      this.chatbotTelemetrySearchTimer = setTimeout(() => {
        this.refreshChatbotTelemetry();
      }, 250);
    },

    async refreshChatbotTelemetry() {
      await this.loadChatbotTelemetry(true);
    },

    async loadMoreChatbotTelemetry() {
      await this.loadChatbotTelemetry(false);
    },

    async loadChatbotTelemetry(reset = false) {
      const search = this.chatbotTelemetrySearch.trim();
      const offset = reset ? 0 : this.chatbotTelemetryRows.length;

      if (reset) {
        this.chatbotTelemetryLoading = true;
        this.chatbotTelemetryError = '';
        this.chatbotTelemetryRows = [];
        this.chatbotTelemetryOffset = 0;
        this.chatbotTelemetryTotal = 0;
      } else if (this.chatbotTelemetryLoading) {
        return;
      } else {
        this.chatbotTelemetryLoading = true;
        this.chatbotTelemetryError = '';
      }

      try {
        const response = await axios.get('/api/chatbot-telemetry', {
          params: {
            limit: this.chatbotTelemetryLimit,
            offset,
            search: search || undefined,
          },
        });

        if (response.data.success) {
          const rows = response.data.data || [];
          this.chatbotTelemetryTotal = Number(response.data.total) || 0;
          this.chatbotTelemetryOffset = offset + rows.length;
          this.chatbotTelemetryRows = reset
            ? rows
            : [...this.chatbotTelemetryRows, ...rows];

          if (this.chatbotTelemetryApplyNewFilter) {
            this.$nextTick(() => {
              this.snapshotVisibleChatbotTelemetryHighestId();
            });
          }
        } else {
          if (reset) {
            this.chatbotTelemetryRows = [];
            this.chatbotTelemetryTotal = 0;
            this.chatbotTelemetryOffset = 0;
          }
          this.chatbotTelemetryError =
            response.data.message || 'Unable to load telemetry rows';
        }
      } catch (error) {
        console.error('Error fetching chatbot telemetry:', error);
        if (reset) {
          this.chatbotTelemetryRows = [];
          this.chatbotTelemetryTotal = 0;
          this.chatbotTelemetryOffset = 0;
        }
        this.chatbotTelemetryError = 'Unable to load telemetry rows';
      } finally {
        this.chatbotTelemetryLoading = false;
      }
    },

    getTelemetryStatusClass(status) {
      const normalized = String(status || '').toLowerCase();

      if (
        normalized === 'success' ||
        normalized === 'lock_code_success' ||
        normalized === 'completed'
      ) {
        return 'telemetry-badge-status-success';
      }

      if (normalized === 'clicked') {
        return 'telemetry-badge-status-clicked';
      }

      if (
        normalized === 'fail' ||
        normalized === 'error' ||
        normalized === 'lock_code_failed'
      ) {
        return 'telemetry-badge-status-fail';
      }

      return 'telemetry-badge-status-neutral';
    },

    formatTelemetryDetails(details) {
      if (!details) return '';

      if (typeof details === 'string') {
        try {
          return JSON.stringify(JSON.parse(details), null, 2);
        } catch (error) {
          return details;
        }
      }

      try {
        return JSON.stringify(details, null, 2);
      } catch (error) {
        return String(details);
      }
    },

    startChatbotDailyPopupMonitor() {
      this.checkDailyChatbotNewUsersPopup();
      this.chatbotDailyPopupInterval = setInterval(() => {
        this.checkDailyChatbotNewUsersPopup();
      }, 60000);
    },

    async fetchNewChatbotUsersSinceLastOpen() {
      const response = await axios.get('/api/rent-payment-signups');
      const allSignups = response.data?.success ? response.data.data || [] : [];

      const lastOpenedAt = localStorage.getItem('chatbotNewUsersLastOpenedAt');
      if (!lastOpenedAt) {
        return allSignups;
      }

      const lastOpenedMs = new Date(lastOpenedAt).getTime();
      if (Number.isNaN(lastOpenedMs)) {
        return allSignups;
      }

      return allSignups.filter((item) => {
        const agreedMs = new Date(item.agreedAt).getTime();
        return !Number.isNaN(agreedMs) && agreedMs > lastOpenedMs;
      });
    },

    async checkDailyChatbotNewUsersPopup() {
      if (this.myUserRole == null || this.myUserRole > 3) return;

      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();

      // Fire once per day at/after 9:00am local time.
      if (!(hours >= 9)) return;

      const todayKey = now.toISOString().slice(0, 10);
      const lastShownDate = localStorage.getItem(
        'chatbotNewUsersLastShownDate',
      );
      if (lastShownDate === todayKey) return;

      try {
        const newUsers = await this.fetchNewChatbotUsersSinceLastOpen();
        if (newUsers.length > 0) {
          this.chatbotNewUsersData = newUsers;
          this.showChatbotNewUsersPopup = true;
          localStorage.setItem('chatbotNewUsersLastShownDate', todayKey);
        }
      } catch (error) {
        console.error('Error checking daily chatbot new users:', error);
      }
    },

    closeChatbotNewUsersPopup() {
      this.showChatbotNewUsersPopup = false;
      this.chatbotNewUsersData = [];
      localStorage.setItem(
        'chatbotNewUsersLastOpenedAt',
        new Date().toISOString(),
      );
    },

    formatVisitDay(day) {
      if (!day) return '';
      const d = new Date(day);
      const month = String(d.getUTCMonth() + 1).padStart(2, '0');
      const date = String(d.getUTCDate()).padStart(2, '0');
      const year = String(d.getUTCFullYear()).slice(-2);
      return `${month}/${date}/${year}`;
    },

    formatActivityDateTime(dateStr) {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      // Format date as "mm/dd/yy"
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      const dateFormatted = `${month}/${day}/${year}`;

      // Format time as "hh:mm am/pm"
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12 || 12;
      const timeFormatted = `${hours}:${minutes} ${ampm}`;

      // Calculate relative time
      let relative;
      if (diffMins < 1) {
        relative = 'just now';
      } else if (diffMins < 60) {
        relative = `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
      } else if (diffHours < 24) {
        relative = `${diffHours} hr${diffHours !== 1 ? 's' : ''} ago`;
      } else {
        relative = `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      }

      return `${dateFormatted} ${timeFormatted} (${relative})`;
    },

    openServerManagement() {
      console.log('openServerManagement called');
      try {
        this.hideMenu('tools');
        this.$router.push('/server-management');
        console.log('Router push completed');
      } catch (error) {
        console.error('Router push error:', error);
      }
    },

    // Set up storage event listener to detect changes from other tabs
    setupStorageListener() {
      window.addEventListener('storage', (event) => {
        if (event.key === 'quickReminders') {
          try {
            // Parse the updated reminders from localStorage
            const updatedReminders = JSON.parse(event.newValue) || [];

            // Update the local reminders array with the changes from other tabs
            this.quickReminders = updatedReminders;

            // Check if any of the updated reminders are currently alerting
            this.alertingTasks = this.alertingTasks.filter((task) => {
              // Only keep tasks that still exist in the updated reminders
              return updatedReminders.some(
                (reminder) => reminder.noteId === task.noteId,
              );
            });

            // Close the reminder popup if there are no more alerting tasks
            if (this.alertingTasks.length === 0) {
              this.showQuickReminderPopup = false;
            }
          } catch (error) {
            console.error(
              'Error processing quickReminders storage event:',
              error,
            );
          }
        }
      });
    },
  },

  watch: {
    isGridView(newVal) {
      // clear the search when leaving gridView
      if (!newVal) this.searchQuery = '';
    },
    searchQuery(newVal) {
      // set gridView when searching if not already set
      if (newVal && !this.isGridView) this.isGridView = true;
    },
    showQuickReminderPopup(newVal) {
      if (newVal) {
        // Start polling when popup becomes visible
        this.startReminderPopupPolling();
      } else {
        // Stop polling when popup is hidden
        this.stopReminderPopupPolling();
      }
    },
    showReminders(isOpen) {
      if (isOpen) {
        this.$nextTick(() => this.initRemindersPanelDrag());
      } else {
        this.teardownRemindersPanelDrag();
      }
    },
    '$route.query.scroll'() {
      this.pageScrollEnabled = applyPageScrollFromLocation();
    },
    openNavPanelSnapshot() {
      this.persistOpenNavPanels();
    },
    '$route.path'(to) {
      this.restoreOpenNavPanels(to);
    },
  },
};
</script>

<style scoped>
.nav-bar {
  width: 100vw; /* Make the navbar take up the full width of the viewport */
  margin: 0; /* Remove any margin */
  padding: 0; /* Remove any padding */
  box-sizing: border-box; /* Ensure padding and border are included in the width */
}

.navbar-nav {
  display: flex;
  justify-content: flex-start; /* Keep the other items aligned to the left */
}

.navbar-nav .right-aligned {
  margin-left: auto; /* This will push the notifications <li> to the right */
}

.menu-bar {
  background-color: #333;
  color: white;
  padding: 5px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  margin-bottom: 20px;
  z-index: 1005;
}

.menu-bar ul {
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  z-index: 1005;
}

.menu-bar ul li {
  position: relative;
  padding: 5px;
}

.menu-bar ul li a {
  color: white;
  text-decoration: none;
}

.menu-bar-dev ul li a {
  color: rgb(228, 73, 73) !important;
}

.menu-bar ul li .dropdown {
  display: none;
  position: absolute;
  background-color: #fff;
  color: #000;
  min-width: 150px;
  top: 100%;
  left: 0;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.2);
  z-index: 10000;
}

.menu-bar ul li .dropdown li {
  padding: 8px;
}

.menu-bar ul li .dropdown li a {
  color: #333;
}

.menu-bar li:hover {
  background: #a080a8;
  color: #333 !important;
}

.menu-bar ul li:hover .dropdown {
  display: block;
}

/* Cascading submenu styles */
.menu-bar .has-submenu {
  position: relative;
}

.menu-bar .has-submenu > a {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.menu-bar .has-submenu .submenu-arrow {
  margin-left: auto;
  padding-left: 10px;
}

.menu-bar .has-submenu > .submenu {
  display: none;
  position: absolute;
  left: calc(100% - 5px); /* Overlap slightly to prevent gap */
  top: -1px;
  background-color: #fff;
  min-width: 160px;
  padding-left: 5px; /* Compensate for the overlap */
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.2);
  z-index: 10001;
  border-top: 1px solid #aaa;
}

.menu-bar .has-submenu:hover > .submenu {
  display: block;
}

.menu-bar .submenu li {
  padding: 8px;
}

.menu-bar .submenu li a {
  color: #333;
  white-space: nowrap;
}

/* Compact menu for narrow screens (< 1375px) - 25% smaller text */
.menu-bar-compact {
  font-size: 0.75em;
}

.menu-bar-compact ul li {
  padding: 4px;
}

.menu-bar-compact ul li .dropdown {
  min-width: 120px;
}

.menu-bar-compact ul li .dropdown li {
  padding: 6px;
}

.menu-bar-compact .has-submenu > .submenu {
  min-width: 130px;
}

.menu-bar-compact .submenu li {
  padding: 6px;
}

.notification-bell {
  font-size: 0.85em;
  color: rgb(193, 74, 74);
  border-color: #ddd;
}

.calendar-badge {
  position: absolute;
  top: -10px;
  right: -15px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  padding: 0;
  font-size: 10px;
  font-weight: bold;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dc3545;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  line-height: 1;
}

.reminders-panel {
  position: fixed;
  top: 125px;
  width: 600px;
  left: 97px;
  /* height: 600px; */
  background: #d3d3d3;
  border-radius: 4px;
  border: 1pt solid #0c0c0c;
  padding: 15px;
  z-index: 6000;
  box-shadow: 11px 9px 7px 2px rgb(0 0 0 / 32%);
}

.reminders-panel-drag-handle {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: move;
  user-select: none;
}

.reminders-panel-drag-handle .fa-grip-horizontal {
  opacity: 0.55;
  font-size: 0.75em;
}

.reminders-panel-drag-handle .close-button {
  right: 10px;
  cursor: pointer;
}

.hotkeys-panel {
  position: absolute;
  top: calc(50% - 200px);
  left: calc(50% - 400px);
  width: 600px;
  height: 430px;
  background: #d3d3d3;
  border-radius: 4px;
  border: 1pt solid #bfbcbc;
  padding: 15px;
  z-index: 50000;
  box-shadow: 11px 9px 7px 5px rgb(0 0 0 / 32%);
  font-size: 0.9em;
}

.help-title {
  font-size: 1.3em;
  margin-bottom: 10px;
  background: #333;
  color: #ddd;
  margin-top: -15px;
  margin-left: -15px;
  margin-right: -15px;
  padding: 6px;
  text-align: center;
}

.key-table {
  width: 100%;
  border-collapse: collapse;
  font-family: monospace;
  font-size: 10pt;
}

.key-table th,
.key-table td {
  padding: 2px 4px 3px 3px;
  text-align: left;
  border: 1px solid #ddd;
}

.headers {
  background-color: #bfbcbc;
  font-weight: bold;
  color: #333;
}

.key-table tbody tr:nth-child(even) {
  background-color: #f9f9f9;
}

.key-table tbody tr:nth-child(odd) {
  background-color: #e7e7e7;
}

.key-table tbody tr:hover {
  background-color: #e0e0e0;
  cursor: pointer;
}

.hotkey {
  font-weight: bold;
  margin: 1px;
}

.key-description {
  color: #222;
}

.todo-note {
  position: absolute;
  top: 37px;
  right: 0px;
  width: 150px;
  max-height: 150px;
  overflow-y: auto;
  background-color: rgb(248, 248, 176);
  padding: 5px;
  border: 1pt solid #333;
  font-size: 12px;
  z-index: 9999;
}

.footer-message {
  transition: background-color 0.5s ease; /* Smooth transition for the background fade */
  position: fixed; /* Position it absolutely relative to the parent container */
  border-radius: 8px;
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
  bottom: 0; /* Align it to the bottom of the parent container */
  left: 0;
  width: calc(
    100vw - 5px
  ); /* Make it span the full width of the parent container */
  height: 20px; /* Set the desired height */
  text-align: left; /* Optional: Center text horizontally */
  line-height: 20px; /* Optional: Center text vertically within the 20px height */
  font-size: 0.8em;
  padding: 3px;
  padding-left: 6px;
  z-index: 100000; /* Ensure the footer stays on top of other elements */
}

.footer-message.highlighted {
  background-color: #9b7ba8; /* Your desired background color */
  color: white;
}

.draggable-icon {
  cursor: pointer;
  color: #888;
  transition: color 0.3s;
  position: absolute;
  right: 10px;
}

.draggable-icon.active {
  color: #007bff;
}

.close-button {
  position: absolute;
  right: 35px;
  top: 6px;
}

.help-title i {
  font-size: 0.8em;
  color: #888;
}

/* Overlay for the MOTD modal */
.motd-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}

/* Content styling for the MOTD modal */
.motd-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 11px 9px 7px 2px rgb(0 0 0 / 32%);
  width: 400px;
  max-width: 90%;
  text-align: left;
  font-size: 0.8em;
  z-index: 2000;
  user-select: none;
}

.motd-inner {
  max-height: 500px;
  overflow-y: auto;
}

/* Button styling for the MOTD modal */
.motd-buttons button {
  margin-top: 20px;
  position: relative;
  padding: 10px 20px;
  background-color: #a080a8;
  text-align: center;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  right: -138px;
}

.motd-buttons button:hover {
  background-color: #5a55d6;
}

.motd-title {
  margin: -20px;
  margin-bottom: 10px;
  background-color: #333;
  color: #eee;
  font-size: 1.2em;
  padding: 8px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.motd-inner :deep(li) {
  margin-bottom: 5px;
  margin-left: -15px;
  margin-right: 15px;
}

/* For quickReminder feature */
.reminder-popup {
  position: fixed;
  top: 40px;
  left: 165px;
  background: #dddddd;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 7px 8px 10px 6px rgb(0 0 0 / 31%);
  padding: 20px;
  padding-bottom: 0px;
  text-align: center;
  z-index: 10000;
  width: 330px;
}

.reminder-title {
  margin-left: -21px;
  margin-right: -21px;
  margin-top: -21px;
  margin-bottom: 15px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-top-color: #dfdada;
  padding: 0px 10px;
  color: #ffffff;
  background: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.toggle-reminder-icon {
  cursor: pointer;
  font-size: 1.1em;
  transition: transform 0.3s ease;
  padding: 5px;
}

.toggle-reminder-icon:hover {
  opacity: 0.8;
}

.reminder-content {
  margin-bottom: 15px;
  height: 300px;
  overflow-y: auto;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.reminder-content::-webkit-scrollbar {
  width: 0px;
  background: transparent;
}

/* Hide scrollbar for IE, Edge and Firefox */
.reminder-content {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.reminder-buttons {
  display: flex;
  gap: 6px;
  justify-content: space-between;
  padding: 10px 0;
  border-top: 1px solid #555;
}

.reminder-task {
  font-size: 0.85em;
  text-align: left;
  padding: 6px 10px 6px 25px;
  position: relative;
}

.reminder-task::before {
  content: '•';
  position: absolute;
  left: 10px;
  font-size: 1.2em;
  color: #a080a8;
}

.reminder-task:hover {
  background: #bf99c8;
}

.reminder-buttons button {
  flex: 1;
  padding: 8px 6px;
  font-size: 0.65em;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: #a080a8;
  box-shadow: 3px 4px 6px 1px rgb(0 0 0 / 21%);
}

.reminder-content {
  font-size: 1em;
  text-align: left;
}

#dismiss {
  background-color: #f44336;
  color: white;
}

#snooze-hour {
  background-color: #2196f3;
  color: white;
}

#snooze-day {
  background-color: #4caf50;
  color: white;
}

.reminders-list {
  display: inline-block;
  background-color: #f9f9f9;
  border: 1px solid #ccc;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 5pt 5pt 5pt #7d7a7a;
  position: absolute;
  top: calc(50% - 175px);
  right: calc(50% - 230px);
  z-index: 10001;
  width: 475px;
  height: 320px;
  overflow: hidden;
}

.reminders-list span {
  text-indent: -118px;
  padding-left: 150px;
}

.reminders-list-outer {
  width: 470px;
  height: 260px;
  overflow: hidden;
}

.reminders-list-inner {
  width: 490px;
  height: 260px;
  overflow-y: scroll;
  font-size: 0.8em;
}

.reminders-list ul li:nth-child(even) {
  background: #ddd;
}

.reminders-list ul li {
  padding: 2px 0 2px 4px;
}

.reminders-list-item {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  align-items: center;
  margin-bottom: 10px;
}

.reminders-list-title {
  margin-left: -21px;
  margin-right: -21px;
  margin-top: -21px;
  margin-bottom: 15px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-top-color: #dfdada;
  padding: 10px 10px;
  color: #ffffff;
  background: #333;
}

.reminders-list-item .delete-button {
  color: #888;
  cursor: pointer;
  position: relative;
  right: 10px;
}

.reminders-list button {
  margin-top: 10px;
  background: #a080a8;
  padding: 4px 8px 4px 8px;
  border-radius: 5px;
  color: #fff;
  border: 1pt solid #888;
  box-shadow: 3pt 3pt 3pt #c5c5c5;
}

.reminder-task.selected {
  background-color: #a080a8; /* Example highlight color */
}

/* simple notes */

.red-icon {
  color: red;
}

.state-bars {
  display: flex;
  gap: 1px; /* Space between bars */
  margin-left: auto; /* Push bars to the right */
}

.state-bar {
  width: 4px;
  height: 10px;
  background-color: #ddd;
  border-radius: 2px;
}

.state-bar.active {
  background-color: #999; /* Highlighted color for active states */
}

.note-tile {
  background-color: #e1e1a6;
  border: 1px solid #888;
  border-radius: 5px;
  padding: 3px;
  margin-bottom: 0px;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  font-size: 0.6em;
  height: 60px;
  overflow-y: hidden;
  width: 117px;
  font-family: consolas;
  margin-right: 4px;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  box-shadow: 6px 1px 5px 0px rgb(0 0 0 / 22%);
}

.outer-note {
  width: 137px;
  overflow-x: hidden;
  overflow-y: scroll;
  max-height: 60px;
}

.inner-note {
  width: 117px;
  overflow: hidden;
}

.inner-note :deep(p) {
  margin: 0 !important;
}

.note-tile-wrapper {
  width: 150px;
  overflow-x: hidden;
}

.note-date {
  position: absolute;
  bottom: 0;
  font-size: 0.9em;
  background: #f9f9ba;
  width: 100%;
  margin-left: -3px;
  padding-left: 11px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.note-tile.placeholder {
  background-color: #e1e1a6;
  color: #888;
  font-style: italic;
}

.note-icons {
  background-color: #b3b367;
  padding: 4px;
  position: absolute;
  bottom: 0px;
  right: 0px;
  display: flex;
  gap: 5px;
  border-bottom-right-radius: 4px;
}

.note-icons i {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.3s;
}

.note-icons i:hover {
  opacity: 1;
}
.more-notes {
  margin-top: 3px;
  text-align: center;
  user-select: none;
  cursor: pointer;
  font-size: 0.7em;
  color: #333;
}

.search-input {
  width: 120px;
  margin-bottom: 3px;
  border: 1pt solid #ddd;
  border-radius: 4px;
  font-size: 0.7em;
}

.textarea-container {
  position: fixed;
  top: calc(50% - 150px);
  right: calc(75% - 268px);
  z-index: 999999;
}

.editing-textarea {
  height: 300px;
  width: 368px;
  /* border: 1px solid #aaa; */
  font-size: 11px;
  background-color: #e1e1a6;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  margin-top: 0;
  margin-right: 0;
  box-shadow: 11px 9px 7px 2px rgb(0 0 0 / 32%);
}

.quicknote-edit-title {
  font-size: 10pt;
  border-top-left-radius: 4px;
  border: 1pt solid #333;
  background: #333;
  color: #eee;
  width: 378px;
  border-top-right-radius: 4px;
  padding: 5px;
}

.quicknotes-help {
  background: #333;
  color: #eee;
  padding: 8px;
  margin-top: 10px;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  width: 372px;
  margin-left: 0px;
  font-size: 1.3em;
  height: 171px;
  font-family: monospace;
  border: 2pt solid #bc8ce9;
}

.quick-help-table {
  width: 100%;
  border-collapse: collapse;
}

.quick-help-table .emphasis {
  color: lightgreen;
}

.quick-help-table tr {
  margin-top: 8px;
  border-bottom: 1pt solid #888;
}

.quick-help-table td:first-child {
  width: 50%;
}

.quick-help-table td:nth-child(2) {
  width: 50%;
}

/* for textarea pin behavior */
.pin-icon {
  position: absolute;
  top: 1px;
  right: 0;
  color: #888;
  cursor: pointer;
  padding: 5px;
  z-index: 10;
}

.pin-icon.pinned {
  color: lightgreen !important; /* Differentiate pinned state through styling */
}

.pin-icon i {
  font-size: 16px;
}

.edit-in-progress {
  width: 95%;
  height: 60px;
  text-align: center;
  padding: 10px 5px;
  color: #888;
}

.editing-textarea:focus {
  outline: 0;
}

.notes-container-wrapper.single-column-view {
  display: block; /* Retain the original behavior */
  width: 150px;
  max-height: 700px;
  overflow-y: auto;
}

/* fixed styles */
.notes-container {
  position: fixed;
  right: -16px;
  top: 42px;
  bottom: 10%;
  /* width: 150px; */
  overflow: hidden;
  padding-right: 10px;
  max-height: 352px;
  z-index: 9999;
  overflow-y: scroll;
}

.notes-container-grid-view {
  position: fixed;
  left: 250px;
  top: 10%;
  /* transform: translateX(-50%); */
  width: 920px;
  max-height: 675px;
  z-index: 9999;
  overflow: hidden;
}

.notes-container-grid-view-inner {
  width: 938px;
  height: 675px;
  overflow-x: hidden;
  overflow-y: scroll;
}

.notes-container-wrapper {
  width: 150px;
  overflow-y: auto;
  overflow-x: hidden;
}

.notes-title-bar {
  position: absolute;
  display: flex;
  align-items: center;
  position: relative;
  background-color: #6e5875;
  color: #eee;
  font-size: 0.7em;
  text-align: center;
  padding: 2px;
  user-select: none;
  cursor: pointer;
  margin-bottom: 2px;
  border-radius: 5px;
  width: 119px;
  margin-left: 1px;
  border: 1pt solid #666;
}

.grid-view {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}

.single-column-view {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.grid-view-underlay {
  position: fixed; /* Ensures it covers the entire viewport */
  top: 0;
  left: 0;
  width: 100%; /* Full width */
  height: 100%; /* Full height */
  background-color: rgba(0, 0, 0, 0.5); /* Black color with 50% transparency */
  z-index: 10; /* Adjust z-index as needed to layer correctly */
}

.toggle-view-icon {
  margin-right: 5px;
}

.title-search-container {
  position: sticky; /* Keeps the title and search input fixed at the top */
  top: 0;
  z-index: 9999; /* Ensures it stays above the notes */
}

.notes-container-wrapper {
  flex-grow: 1;
  overflow-y: auto; /* Scrollable area for the notes */
  padding: 10px;
  margin-top: 0px; /* To avoid overlap with the fixed title and search container */
}

.voice-memo-title {
  font-size: 1.4em;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background: #6e5875;
  color: #eee;
  margin: -1px;
  margin-bottom: 10px;
  padding: 8px;
  margin-top: -2px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.voice-memo-title span {
  flex: 1;
}

.voice-memo-title > div {
  display: flex;
  align-items: center;
  gap: 5px;
}

.voice-memo-title button {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 0.6em;
  cursor: pointer;
}

.voice-memo-title button:hover {
  opacity: 0.8;
}

.voice-memo-title button i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.voice-memo-container {
  width: 730px;
  height: 660px;
  font-size: 0.9em;
  position: absolute;
  top: 100px;
  left: 100px;
  z-index: 9999;
  background: #f5f5f5;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  box-shadow: 11px 9px 7px 2px rgb(0 0 0 / 32%);
  border: 2px solid #aaa;
  border-radius: 5px;
}

.close-btn {
  background: #444;
  border-radius: 4px;
  border: 1px solid #555;
  top: 7px;
}

.divider {
  color: #ccc;
  margin: 0 10px;
  pointer-events: none;
}

.alert-item {
  margin-left: 20px;
}

.user-menu-trigger {
  font-size: 9pt;
  margin-right: 5px;
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0;
}

.user-menu-trigger:hover {
  text-decoration: underline;
}

.user-dropdown {
  right: 0;
  left: auto;
}

.quick-email-wrapper {
  box-shadow: 8px 9px 7px 3px rgb(0 0 0 / 22%);
  position: absolute;
  top: 89px;
  left: 1692px;
  width: 414px;
  height: 804px;
  border-radius: 5px;
  overflow: hidden;
  z-index: 10000;
}

.mobile-notes-wrapper {
  box-shadow: 8px 9px 7px 3px rgb(0 0 0 / 22%);
  position: absolute;
  top: 89px;
  left: 1250px;
  width: 414px;
  height: calc(790px + 15px);
  background: #eee;
  border-radius: 5px;
  overflow: hidden;
  z-index: 10000;
}

.cash-position-wrapper {
  z-index: 10000;
  position: absolute;
  top: 77px;
  left: 1234px;
  width: 414px;
  height: 84vh;
  box-shadow: 8px 9px 7px 3px rgb(0 0 0 / 22%);
}

.lock-codes-wrapper {
  position: absolute;
  top: 84px;
  left: 1234px;
  width: 414px;
  height: 78vh;
  box-shadow: 8px 9px 7px 3px rgb(0 0 0 / 22%);
}

.cash-position-inner {
  width: 429px;
}

.work-orders-wrapper {
  position: absolute;
  top: 90px;
  left: 1270px;
  width: 413px;
  height: 772px;
  overflow: hidden;
  border: 1px solid #969696;
  border-radius: 5px;
  background: #ddd;
  box-shadow: rgba(0, 0, 0, 0.1) 5px 5px 7px 2px;
}

.financial-summary-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 20000;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.appliances-wrapper {
  position: absolute;
  top: 90px;
  left: 1270px;
  width: 419px;
  height: 772px;
  overflow: hidden;
  border: 1px solid #969696;
  border-radius: 5px;
  background: #ddd;
  box-shadow: rgba(0, 0, 0, 0.1) 5px 5px 7px 2px;
}

.collector-setup-wrapper {
  position: absolute;
  top: 90px;
  left: 1270px;
  width: 918px;
  height: 772px;
  overflow: hidden;
  border: 1px solid #969696;
  border-radius: 5px;
  background: #ddd;
  box-shadow: rgba(0, 0, 0, 0.1) 5px 5px 7px 2px;
}

.survey-setup-wrapper {
  position: absolute;
  top: 90px;
  left: 1270px;
  width: 918px;
  height: 772px;
  overflow: hidden;
  border: 1px solid #969696;
  border-radius: 5px;
  background: #ddd;
  box-shadow: rgba(0, 0, 0, 0.1) 5px 5px 7px 2px;
}

.chat-wrapper {
  width: 420px;
  z-index: 10001;
  position: absolute;
  top: 89px;
  left: 1250px;
  height: 805px;
  border-radius: 5px;
  overflow: hidden;
}
/* must be below all the other wrappers */
.wrapper-absolute-position {
  position: absolute;
  left: 1324px;
  top: 57px;
  height: 856px;
}
.wrapper-absolute-position-2 {
  position: absolute;
  left: 1404px;
  top: 77px;
}

.wrapper-absolute-position-3 {
  position: absolute;
  left: 1704px;
  top: 60px;
  height: 884px;
}

.wrapper-absolute-position-3.wrapper-main-wo-offset {
  left: 1744px;
  top: 57px;
}

/* Left tool column on /main ends at 1324px + 420px. Keep this to the right of that. */
.wrapper-absolute-position-3.wrapper-main-right-column {
  left: 1804px;
}

/* /properties: same slot as .quick-email-wrapper, beside Chat Bot. */
.work-orders-wrapper.wrapper-properties-quick-email-column {
  top: 89px;
  left: 1692px;
  width: 414px;
  height: 804px;
  z-index: 10001;
}

.wrapper-absolute-position-4 {
  position: absolute;
  left: 1804px;
  top: 77px;
}

.dev-mode {
  background: yellow;
  border-radius: 5px;
  color: #333;
  border: 2px solid red;
  font-size: 0.7em;
  margin-left: -5px;
}

.quick-notes-badge {
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
  padding: 3px 6px;
  font-size: 0.7em;
  font-weight: bold;
  margin-left: 5px;
  display: inline-block;
  text-align: center;
  position: absolute;
  left: 120px;
  top: 0px;
}

/* Screen Size Warning Popup */
.screen-size-warning-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
}

.screen-size-warning-popup {
  background-color: #fff;
  padding: 25px 30px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 450px;
  text-align: center;
}

.screen-size-warning-popup p {
  margin: 0 0 20px 0;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}

.screen-size-warning-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
}

.screen-size-warning-buttons button {
  padding: 8px 25px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.screen-size-warning-buttons button:first-child {
  background-color: #4caf50;
  color: white;
}

.screen-size-warning-buttons button:first-child:hover {
  background-color: #45a049;
}

.screen-size-warning-buttons button:last-child {
  background-color: #f0f0f0;
  color: #333;
}

.screen-size-warning-buttons button:last-child:hover {
  background-color: #e0e0e0;
}

/* Health check status circle */
.health-status-circle {
  display: inline-block;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.health-status-circle.online {
  background-color: #4caf50;
}

.health-status-circle.offline {
  background-color: #f44336;
  animation: pulse-red 1.5s ease-in-out infinite;
}

@keyframes pulse-red {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Offline popup */
.offline-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
}

.offline-popup-dialog {
  background: white;
  border-radius: 10px;
  padding: 24px 32px;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.offline-popup-dialog i {
  font-size: 48px;
  color: #f44336;
  margin-bottom: 16px;
}

.offline-popup-dialog p {
  font-size: 16px;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.5;
}

.offline-popup-dialog button {
  padding: 10px 24px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.offline-popup-dialog button:hover {
  background: #5a6268;
}

/* Recent Activity Popup */
.recent-activity-dialog {
  background: white;
  border-radius: 10px;
  padding: 0;
  min-width: 320px;
  max-width: 400px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.tenant-app-stats-overlay {
  align-items: flex-start;
  /* 40px bottom leaves the fixed .footer-message (20px + padding) clear */
  padding: 64px 16px 40px;
  box-sizing: border-box;
  overflow: hidden;
}

.rent-payment-signups-dialog {
  max-width: 720px;
  width: min(720px, 90vw);
  max-height: calc(100vh - 104px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tenant-app-stats-body {
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.tenant-app-stats-body::-webkit-scrollbar {
  display: none;
}

.rent-payment-signups-dialog .recent-activity-list {
  max-height: none;
}

.chatbot-telemetry-overlay {
  display: block;
  background: rgba(0, 0, 0, 0.35);
}

.chatbot-telemetry-dialog {
  position: fixed;
  top: 40px;
  left: 5vw;
  width: min(90vw, 1500px);
  max-width: 1500px;
  height: calc(100vh - 80px);
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  z-index: 100000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
}

.chatbot-telemetry-drag-handle {
  cursor: move;
  user-select: none;
  flex-shrink: 0;
}

.polling-locks-dialog {
  width: min(96vw, 1180px);
  max-width: 1180px;
  height: calc(100vh - 80px);
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.polling-locks-dialog .recent-activity-header {
  flex-shrink: 0;
}

.polling-locks-dialog,
.polling-locks-dialog * {
  -webkit-user-select: text;
  -moz-user-select: text;
  user-select: text;
}

.polling-locks-dialog button {
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  cursor: pointer;
}

.polling-locks-body {
  padding: 12px 16px 16px;
  font-family: 'Consolas', 'Menlo', 'Monaco', monospace;
  font-size: 12px;
  color: #2e2434;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.polling-locks-body::-webkit-scrollbar {
  display: none;
}

.polling-locks-updated {
  color: #6b5f73;
  margin-bottom: 12px;
}

.polling-locks-empty,
.polling-locks-error {
  padding: 12px 0;
}

.polling-locks-error {
  color: #b42318;
}

.polling-locks-table {
  border-top: 1px solid #e8e0ee;
}

.polling-locks-row {
  display: grid;
  grid-template-columns: 1.15fr 0.7fr 0.6fr 0.75fr 1.15fr 1.35fr;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f0eaf4;
}

.polling-last-success {
  color: #2e5a3c;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  line-height: 1.25;
  white-space: normal;
}

.polling-errors {
  color: #8b8190;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  line-height: 1.25;
  white-space: normal;
}

.polling-when-primary {
  white-space: nowrap;
}

.polling-when-ago {
  color: #6b5f73;
  font-weight: 400;
  white-space: nowrap;
}

.polling-locks-row-header {
  font-weight: 700;
  color: #4a3d52;
  border-bottom: 1px solid #d9cfe1;
}

.polling-service-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.polling-lock-action-btn {
  background: none;
  border: none;
  color: #6b5f73;
  padding: 0;
  font-size: 11px;
  line-height: 1;
  flex-shrink: 0;
}

.polling-lock-action-btn:hover:not(:disabled) {
  color: #2f5fd0;
}

.polling-lock-action-btn-release:hover:not(:disabled) {
  color: #b42318;
}

.polling-lock-action-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.polling-service-name {
  color: #2f5fd0;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
}

.polling-service-name:hover {
  text-decoration: underline;
}

/* Above .offline-popup-overlay (99999) so this sits over Poll Lock Monitor */
.run-service-overlay {
  z-index: 100100;
}

.run-service-dialog {
  background: #fff;
  border-radius: 10px;
  width: min(640px, 94vw);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
  overflow: hidden;
}

.run-service-body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}

.run-service-intro {
  margin: 0;
  color: #333;
  font-size: 0.95em;
  line-height: 1.4;
}

.run-service-output {
  background: #1e1e1e;
  color: #d4d4d4;
  border-radius: 6px;
  border: 1px solid #333;
  min-height: 220px;
  max-height: 45vh;
  overflow: auto;
  padding: 10px 12px;
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.45;
}

.run-service-output pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.run-service-output-placeholder {
  color: #888;
  font-style: italic;
}

.run-service-output-running {
  border-color: #6e5875;
}

.run-service-output-done {
  border-color: #2f6b42;
}

.run-service-output-failed {
  border-color: #b42318;
}

.run-service-error {
  color: #b42318;
  font-size: 0.9em;
}

.run-service-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.run-service-run-btn,
.run-service-close-btn,
.run-service-download-btn {
  border: none;
  border-radius: 5px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 0.95em;
}

.run-service-download-btn {
  background: #eef3fb;
  color: #2f5fd0;
  margin-right: auto;
}

.run-service-download-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.run-service-run-btn {
  background: #6e5875;
  color: #fff;
}

.run-service-run-btn:disabled {
  opacity: 0.65;
  cursor: default;
}

.run-service-close-btn {
  background: #e8e4ea;
  color: #333;
}

.run-service-close-btn:disabled {
  opacity: 0.65;
  cursor: default;
}

.polling-server {
  font-weight: 600;
}

.polling-version {
  color: #0f766e;
}

.polling-heartbeat-healthy {
  color: #2f6b42;
}

.polling-heartbeat-warning {
  color: #9a6700;
}

.polling-heartbeat-expired {
  color: #b42318;
}

.polling-errors-active {
  color: #b42318;
  font-weight: 600;
}

.polling-error-detail {
  color: #6b5f73;
  padding: 0 0 4px 12px;
}

.polling-meta {
  color: #8b8190;
  padding: 0 0 10px 12px;
}

.polling-locks-legend {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #e8e0ee;
  color: #6b5f73;
}

.chatbot-telemetry-dialog,
.chatbot-telemetry-dialog * {
  -webkit-user-select: text;
  -moz-user-select: text;
  user-select: text;
}

.chatbot-telemetry-dialog button {
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  cursor: pointer;
}

.bottom-right-alerts {
  position: fixed;
  bottom: 60px;
  right: 24px;
  z-index: 100000;
  display: flex;
  flex-direction: column-reverse;
  align-items: stretch;
  gap: 8px;
  width: 380px;
  max-width: calc(100vw - 48px);
  box-sizing: border-box;
}

/* Shared bottom-right toast alerts — same width when stacked */
.polling-locks-alert,
.chatbot-telemetry-alert,
.rent-me-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #d9cfe1;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  font-size: 12px;
  color: #333;
}

.polling-locks-alert > span,
.chatbot-telemetry-alert > span,
.rent-me-alert > span {
  flex: 1;
  min-width: 0;
  line-height: 1.3;
}

.polling-locks-alert-actions,
.chatbot-telemetry-alert-actions,
.rent-me-alert-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.polling-locks-alert-actions button,
.chatbot-telemetry-alert-actions button,
.rent-me-alert-actions button {
  padding: 4px 10px;
  border: 1px solid #6e5875;
  border-radius: 6px;
  background: #6e5875;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
}

.polling-locks-alert-actions button:last-child,
.chatbot-telemetry-alert-actions button:last-child,
.rent-me-alert-actions button:last-child {
  background: #fff;
  color: #6e5875;
}

.polling-locks-alert-actions button:hover,
.chatbot-telemetry-alert-actions button:hover,
.rent-me-alert-actions button:hover {
  opacity: 0.9;
}

.chatbot-telemetry-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #faf7fc;
  border-bottom: 1px solid #e8e0ee;
}

.chatbot-telemetry-search {
  flex: 1;
  min-width: 180px;
  border: 1px solid #d9cfe1;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  color: #2e2434;
  background: #fff;
}

.chatbot-telemetry-search:focus {
  outline: none;
  border-color: #8d6a9a;
  box-shadow: 0 0 0 2px rgba(141, 106, 154, 0.12);
}

.chatbot-telemetry-summary {
  font-size: 11px;
  color: #6e5875;
  white-space: nowrap;
}

.chatbot-telemetry-toolbar,
.chatbot-telemetry-footer {
  flex-shrink: 0;
}

.chatbot-telemetry-list {
  flex: 1;
  min-height: 0;
  padding: 12px 16px 14px;
  max-height: none;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px;
  background: #fcfbfd;
}

.telemetry-card {
  border: 1px solid #e5dfee;
  border-radius: 10px;
  background: white;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(80, 54, 92, 0.08);
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 11px;
  color: #33283a;
}

.telemetry-card-top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: flex-start;
}

.telemetry-card-title {
  font-size: 12px;
  font-weight: 700;
  color: #2f2434;
  line-height: 1.3;
}

.telemetry-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
}

.telemetry-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 999px;
  background: #f0ebf4;
  color: #664f72;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.telemetry-badge-status-success {
  background: #e8f2ea;
  color: #2f6b42;
}

.telemetry-badge-status-clicked {
  background: #dceefb;
  color: #2a5f8f;
}

.telemetry-badge-status-fail {
  background: #fde8e8;
  color: #a83232;
}

.telemetry-badge-status-neutral {
  background: #f0ebf4;
  color: #664f72;
}

.telemetry-card-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: #5d5564;
  line-height: 1.35;
}

.telemetry-details {
  margin: 0;
  padding: 8px;
  border-radius: 8px;
  background: #f8f5fb;
  color: #4b3b54;
  font-size: 10px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 150px;
  overflow: auto;
}

.chatbot-telemetry-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 16px 14px;
  border-top: 1px solid #e8e0ee;
  background: #faf7fc;
}

.telemetry-load-more {
  margin-left: auto;
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  background: #6e5875;
  color: white;
  font-size: 12px;
  cursor: pointer;
}

.telemetry-load-more:hover:not(:disabled) {
  background: #5f4a66;
}

.telemetry-load-more:disabled {
  opacity: 0.6;
  cursor: default;
}

.telemetry-error {
  color: #b14646;
  font-size: 11px;
}

.chatbot-new-users-dialog {
  max-width: 620px;
  width: 86%;
}

.chatbot-visits-section {
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
  background: #faf8fb;
}

.chatbot-visits-title {
  font-size: 13px;
  font-weight: 600;
  color: #6e5875;
  margin-bottom: 8px;
}

.chatbot-visits-hint {
  font-size: 11px;
  color: #888;
  margin: -4px 0 8px;
}

.chatbot-visits-table tbody tr.chatbot-visit-row {
  cursor: pointer;
  user-select: none;
}

.chatbot-visits-table tbody tr.chatbot-visit-row:hover {
  background: #f0eaf4;
}

.chatbot-visits-table tbody tr.chatbot-visit-row.is-selected {
  background: #e6dcee;
}

.visit-day-names-overlay {
  z-index: 100000;
}

.visit-day-names-dialog {
  max-width: 560px;
  width: min(560px, 90vw);
}

.chatbot-visits-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.chatbot-visits-table th,
.chatbot-visits-table td {
  padding: 4px 10px;
  text-align: left;
  border-bottom: 1px solid #e8e0ee;
}

.chatbot-visits-table th {
  color: #888;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.chatbot-version-count {
  border: none;
  background: none;
  padding: 0;
  color: #5c4d8a;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
}

.chatbot-visits-table td:last-child,
.chatbot-visits-table th:last-child {
  text-align: right;
}

.recent-activity-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #6e5875;
  color: white;
  font-weight: 600;
  font-size: 15px;
}

.recent-activity-header .close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.recent-activity-header .close-btn:hover {
  color: #ccc;
}

.recent-activity-header .refresh-btn {
  background: none;
  border: none;
  color: white;
  font-size: 14px;
  cursor: pointer;
  padding: 2px 4px;
  line-height: 1;
  opacity: 0.85;
}

.recent-activity-header .refresh-btn:hover:not(:disabled) {
  opacity: 1;
}

.recent-activity-header .refresh-btn:disabled {
  cursor: default;
  opacity: 0.5;
}

.recent-activity-header .telemetry-new-only-btn {
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.recent-activity-header .telemetry-new-only-btn.is-on {
  background: #1a1a1a;
  opacity: 1;
}

.recent-activity-list {
  padding: 12px 16px;
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.activity-item {
  display: flex;
  align-items: baseline;
  padding: 6px 0;
  border-bottom: 1px solid #eee;
  gap: 12px;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-user {
  font-weight: 500;
  color: #333;
  font-size: 14px;
  min-width: 130px;
  flex-shrink: 0;
}

.activity-time {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  flex-grow: 1;
  text-align: left;
}

.no-activity {
  text-align: center;
  color: #999;
  padding: 20px;
  font-style: italic;
}

.late-fees-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 20000;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.late-fees-wrapper > * {
  width: 90vw;
  max-width: 1200px;
  height: 80vh;
  border-radius: 5px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.sms-inbound-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100002;
  box-sizing: border-box;
  width: min(440px, calc(100vw - 32px));
  background: #fff;
  border: 1px solid #d9cfe1;
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.22);
  color: #333;
  pointer-events: auto;
  overflow: hidden;
}

.sms-inbound-popup-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #6e5875;
  color: #fff;
  font-size: 1.05em;
  font-weight: 600;
  padding: 10px 12px;
}

.sms-inbound-popup-main {
  padding: 14px 16px 14px;
}

.sms-inbound-popup-sender {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  color: #2e2434;
}

.sms-inbound-popup-close {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  margin: 0;
  padding: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.sms-inbound-popup-time {
  margin-top: 2px;
  font-size: 12px;
  color: #666;
}

.sms-inbound-popup-body {
  margin-top: 10px;
  max-height: 240px;
  overflow: auto;
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  -webkit-user-select: text;
  user-select: text;
}

.sms-inbound-popup-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.sms-inbound-popup-actions button {
  margin: 0;
  padding: 6px 12px;
  border: 1px solid #6e5875;
  border-radius: 6px;
  background: #6e5875;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}

.sms-inbound-popup-actions button:hover,
.sms-inbound-popup-close:hover {
  opacity: 0.9;
}
</style>
